import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import EngTourPage, { RoomGuideList } from "./EngTourPage";
import UMapsWordmark from './components/UMapsWordmark.jsx';
import { buildZip, readZip } from './utils/zip.js';
import { backupSources, restorableStops, restoreStops, diffContent } from './utils/engtourRestore.js';
import { collectImageRefs, resolveImageUrl, imageFileName, buildExportFiles } from './utils/engtourExport.js';
import { loadEngTourRoomNames, roomNameFor } from './utils/engtourRoomNames.js';
import { isStopRoom, normalizeStopLabels, filterRoomChoices } from './utils/engtourStops.js';
import { bankKey, buildStopBank, stripBankFlags, migrateFreeRoamToBank, FREE_ROAM_MODES, freeRoamMode, freeRoamPatch, nextFreeRoamMode } from './utils/engtourBank.js';
import { introContent, fillStamps, newFieldId, MAX_CUSTOM_FIELDS, RECIPIENT_KINDS, MAX_RECIPIENTS, cleanAddress, isEmailAddress, normalizeRecipients, recipientCount } from './utils/engtourIntro.js';
import {
  ROUTE_MODES, MODE_IDS, legsOf, storedRoute, withRoute, pruneRoutes, validateRoute,
  buildAdjacency, shortestPath, shaftFloors, bestAutoPath,
} from './utils/engtourRoutes.js';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_MAPS_KEY;
const API_BASE = process.env.NODE_ENV === "development" ? "http://localhost:3001" : "";

const BUILDING_KEYS = [
  { key: 'eitc_e1', building: 'e1' },
  { key: 'eitc_e2', building: 'e2' },
  { key: 'eitc_e3', building: 'e3' },
  { key: 'stanley_pauley_centre', building: 'stanley_pauley_centre' },
  { key: 'stanley_pauley_engineering', building: 'stanley_pauley_engineering' },
];
// The same nav files the live tour walks (EngTourPage's EITC_NAV_KEYS). Loaded
// here only to snap a hand-placed spot to the nearest node — the creator does
// no routing of its own.
// ⚠️ `nav_tunnels` is where the INDOOR links between E1, E2, E3 and the Stanley
// Pauley buildings live (the atrium crossings on floors 1–3, the E1 bridges),
// not in the building files. Without it every leg between two buildings went
// out of an exit and back in through another — the "route goes outside between
// 4 and 5" report. The file is identical in data/buildings, the pathfinder
// snapshot and UMapsTools; the tour just never asked for it.
// Does this session belong to the UMaps Admin account? The operator signing in
// here is given an editor token with a signed `adm` claim (api/auth.js); the
// payload is readable base64, so the page can show the operator-only buttons
// (⬇ Export, ⤴ Restore from a backup) without a second flag to keep in step.
// A UI gate only: the server checks the signature, never this function.
function tokenSaysAdmin(token) {
  try {
    const payload = String(token || "").split(".")[0].replace(/-/g, "+").replace(/_/g, "/");
    const claims = JSON.parse(atob(payload));
    return claims.adm === 1 || claims.adm === true;
  } catch { return false; }
}

// The Route Adjuster's legs carry the index of the stop they ARRIVE at (there is
// no leg to stop 0), so a leg is found by that index, never by array position.
const legOf = (legs, idx) => (idx == null ? null : (legs?.find(l => l.idx === idx) ?? null));

const NAV_KEYS = [
  'nav_eitc_e1', 'nav_eitc_e2', 'nav_eitc_e3',
  'nav_stanley_pauley_centre', 'nav_stanley_pauley_engineering', 'nav_outdoors', 'nav_tunnels',
];
const EITC_CENTER = { lat: 49.8085709, lng: -97.1340870 };

// ── University of Manitoba brand palette (mirrors the live tour /engtour) ──────
const UM = {
  brown:   "#4A1E13",
  gold:    "#FFB81C",
  sky:     "#0093D0",
  blue:    "#2C5697",
  navy:    "#1B335E",
  success: "#2E7D32",
  cream:   "#FBF6EE",
  sand:    "#F1E7D6",
  line:    "#E4D8C5",
  ink:     "#2E2117",
};
const SERIF = "'Georgia', 'Times New Roman', serif";

// Map glyphs for the Route Adjuster: a staircase and an elevator car, on the
// colours the floor plan already uses for those polygons.
const svgIcon = (bg, path) => "data:image/svg+xml;utf8," + encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><circle cx="12" cy="12" r="11" fill="${bg}" stroke="#fff" stroke-width="2"/>${path}</svg>`);
const STAIR_ICON = svgIcon("#a0724a", '<path d="M6 17h3v-3h3v-3h3V8h3" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>');
const ELEVATOR_ICON = svgIcon("#607d8b", '<rect x="7" y="6" width="10" height="12" rx="1.5" fill="none" stroke="#fff" stroke-width="1.8"/><path d="M10 10.5l1.5-2 1.5 2M10 13.5l1.5 2 1.5-2" fill="none" stroke="#fff" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>');
const SANS = "'Helvetica Neue', Arial, sans-serif";

// Engineering departments a stop / free-roam place is relevant to. Drives the
// Free Roam filter chips on the live tour (a student picks a department to see
// only the places relevant to it). Stored as `departments: string[]`.
const DEPARTMENTS = ["Mechanical", "Civil", "Electrical", "Computer", "Biosystems"];

// Multi-select department chips, shared by the stop and free-roam editors.
// `value` is an array of department names; toggling a chip adds/removes it and
// re-emits the array in the canonical DEPARTMENTS order.
function DepartmentPicker({ value, onChange }) {
  const set = new Set(value || []);
  const toggle = (d) => {
    const next = new Set(set);
    if (next.has(d)) next.delete(d); else next.add(d);
    onChange(DEPARTMENTS.filter(x => next.has(x)));
  };
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
      {DEPARTMENTS.map(d => {
        const active = set.has(d);
        return (
          <button key={d} type="button" onClick={() => toggle(d)} style={{
            padding: "5px 12px", borderRadius: 999,
            border: `1.5px solid ${active ? UM.gold : UM.line}`, background: active ? UM.gold : "#fff",
            color: active ? UM.brown : "#6b5d4b", fontSize: 12, fontWeight: 800, cursor: "pointer",
          }}>{d}</button>
        );
      })}
    </div>
  );
}

// Subtle, app-wide button interactivity (hover lift + brightness, press shrink).
// Scoped to the creator root so it never leaks elsewhere. Buttons that set their
// own inline `transform` (the [+] inserters, the floating "Edit link" pill) keep
// it — inline styles win — so only their brightness/press feedback is added.
const BTN_CSS = `
.etc-resizer{flex:0 0 6px;width:6px;margin:0 -3px;position:relative;z-index:8;cursor:col-resize;touch-action:none;background:transparent;transition:background .12s;}
.etc-resizer:hover,.etc-resizer:focus-visible{background:rgba(0,147,208,.55);outline:none;}
.etc-resizer:active{background:#0093D0;}
.etc-root button:not(:disabled){transition:transform .12s ease,filter .12s ease,box-shadow .12s ease;}
.etc-root button:not(:disabled):hover{transform:translateY(-1px);filter:brightness(1.07);}
.etc-root button:not(:disabled):hover:not([style*="transparent"]){box-shadow:0 3px 10px rgba(0,0,0,0.18);}
.etc-root button:not(:disabled):active{transform:translateY(0) scale(.94);}
`;

// The sidebar's width (px): dragged by its edge, remembered per browser.
const SIDEBAR_KEY = 'engtourcreator_sidebar_w';
const SIDEBAR_DEFAULT = 320, SIDEBAR_MIN = 260, SIDEBAR_MAX = 640;
const clampSidebar = (w) => Math.round(Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, Number(w) || SIDEBAR_DEFAULT)));

// Normalize any building tag (eitc_e2, EITC E2, E2, e2) → 'e2'
function normalizeBuilding(b) {
  if (b == null) return null;
  return String(b).toLowerCase().replace(/^eitc[\s_-]*/, '').trim();
}
function buildingShortCode(b) {
  const n = normalizeBuilding(b);
  if (!n) return "";
  if (n === "e1") return "E1";
  if (n === "e2") return "E2";
  if (n === "e3") return "E3";
  if (n === "stanley_pauley_engineering") return "SP";
  if (n === "stanley_pauley_centre") return "SP-C";
  return String(b).toUpperCase();
}
function formatRoomCode(building, room) {
  const bc = buildingShortCode(building);
  if (!bc) return String(room == null ? "" : room);
  // A hand-placed spot has no room number. Interpolating it printed
  // "E2-null"; the building on its own is the honest answer.
  if (room == null || String(room).trim() === "") return bc;
  return `${bc}-${room}`;
}
// A hand-placed spot's label, formatted like a room code: "E2-Atrium Stage".
// The building prefix is what makes it sort and read alongside E2-213 in the
// stop list and the search box. Typing the prefix yourself doesn't double it.
function spotLabelFor(building, name) {
  const clean = String(name || "").trim();
  const bc = buildingShortCode(building);
  if (!bc || !clean) return clean;
  return clean.toLowerCase().startsWith(`${bc.toLowerCase()}-`) ? clean : `${bc}-${clean}`;
}

// The caption for a stop or bank entry: its room code, or — for a hand-placed
// spot, which has no room — the name it was given.
function stopCode(s) {
  if (!s) return "";
  if (s.room != null && String(s.room).trim() !== "") return formatRoomCode(s.building, s.room);
  return s.label || formatRoomCode(s.building, s.room);
}


// Parse a free-text room query ("E2-213", "E2 213", "SP-C 120", "sp347") into
// { building, room } using the same building tags the polygons carry. Returns
// null if it doesn't look like a room code.
function parseRoomQuery(q) {
  const s = String(q || "").trim().toUpperCase();
  const m = s.match(/^(SP-?C|SPC|E1|E2|E3|SP)\s*[-\s]?\s*([0-9]+[A-Z]?)$/);
  if (!m) return null;
  const pfx = m[1].replace(/[-\s]/g, "");
  let building = null;
  if (pfx === "E1") building = "e1";
  else if (pfx === "E2") building = "e2";
  else if (pfx === "E3") building = "e3";
  else if (pfx === "SPC") building = "stanley_pauley_centre";
  else if (pfx === "SP") building = "stanley_pauley_engineering";
  if (!building) return null;
  return { building, room: m[2] };
}

// Pull the 11-char YouTube id out of any common URL form.
function youtubeId(url) {
  if (!url) return null;
  const m = String(url).match(
    /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([\w-]{11})/
  );
  return m ? m[1] : null;
}

// `autoplay` starts the video as soon as it renders. Browsers only allow
// autoplay when muted, so an autoplaying embed is muted (viewers can unmute).
function VideoEmbed({ url, autoplay = false }) {
  const yt = youtubeId(url);
  if (yt) {
    const params = autoplay ? "?autoplay=1&mute=1&playsinline=1" : "";
    return (
      <div style={{ position: "relative", width: "100%", paddingTop: "56.25%" }}>
        <iframe
          src={`https://www.youtube.com/embed/${yt}${params}`}
          title="Video"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
    );
  }
  return <video src={url} controls autoPlay={autoplay} muted={autoplay} playsInline={autoplay} style={{ width: "100%", display: "block" }} />;
}

// ── Rich-body block model ─────────────────────────────────────────────────────
// The stop body is stored as a single `text` string where bracketed lines are
// media blocks (rendered identically on the live tour by RichText):
//   [[Label|url]]   → link box      [[img|src]] → image      [[video|url]] → video
// The editor works on an ordered list of blocks (text / link / image / video)
// and serializes back to that same string. On first edit, a stop's legacy
// images[] (top strip) and videoUrl migrate into inline blocks.
let _blockId = 0;
const newId = () => ++_blockId;

// Image token payload is "src" or "src|HEIGHT" (HEIGHT = px crop height; width
// is always locked to 100%). Only treat a trailing all-digits segment as height.
function parseImgPayload(payload) {
  const parts = String(payload || "").split("|");
  if (parts.length > 1 && /^\d+$/.test(parts[parts.length - 1].trim())) {
    const height = Number(parts.pop().trim());
    return { src: parts.join("|").trim(), height };
  }
  return { src: String(payload || "").trim(), height: undefined };
}

// Video token payload is "url" or "url|autoplay". Only a trailing "autoplay"
// segment flips the flag; URLs never contain "|", so this is unambiguous.
function parseVideoPayload(payload) {
  const parts = String(payload || "").split("|");
  let autoplay = false;
  if (parts.length > 1 && parts[parts.length - 1].trim().toLowerCase() === "autoplay") {
    autoplay = true;
    parts.pop();
  }
  return { url: parts.join("|").trim(), autoplay };
}

function parseTextTokens(text) {
  const blocks = [];
  let buf = [];
  const flush = () => { if (buf.length) { blocks.push({ _id: newId(), type: "text", value: buf.join("\n") }); buf = []; } };
  String(text || "").split("\n").forEach(line => {
    const m = line.match(/^\s*\[\[\s*([^\]|]+?)\s*(?:\|\s*([^\]]*?)\s*)?\]\]\s*$/);
    if (m) {
      flush();
      const kind = m[1].toLowerCase();
      if (kind === "img") { const { src, height } = parseImgPayload(m[2]); blocks.push({ _id: newId(), type: "image", src, height }); }
      else if (kind === "video") { const { url, autoplay } = parseVideoPayload(m[2]); blocks.push({ _id: newId(), type: "video", url, autoplay }); }
      else blocks.push({ _id: newId(), type: "link", label: m[1], url: m[2] || "" });
    } else buf.push(line);
  });
  flush();
  return blocks;
}

// Build the editor block list for a stop, migrating legacy top images[] (first)
// and videoUrl (last) into inline blocks alongside whatever the body already has.
function parseStopBlocks(stop) {
  const blocks = [];
  (stop.images || []).forEach(src => { if (src) blocks.push({ _id: newId(), type: "image", src }); });
  parseTextTokens(stop.text).forEach(b => blocks.push(b));
  if (stop.videoUrl) blocks.push({ _id: newId(), type: "video", url: stop.videoUrl });
  return blocks;
}

function serializeBlocks(blocks) {
  return blocks.map(b => {
    if (b.type === "link") return `[[${b.label}${b.url ? `|${b.url}` : ""}]]`;
    if (b.type === "image") return `[[img|${b.src || ""}${b.height ? `|${b.height}` : ""}]]`;
    if (b.type === "video") return `[[video|${b.url || ""}${b.autoplay ? "|autoplay" : ""}]]`;
    return b.value;
  }).join("\n");
}

// ── Stop bank ────────────────────────────────────────────────────────────────
// A reusable library of every stop that's been created, so an editor can drop a
// previously-built stop into another tour. Entries hold all stop info EXCEPT
// `guidance` ("What's Next"), which is tour-specific (it points at the next stop
// in a particular tour). Deduped by room code. `bankKey` / `buildStopBank` live
// in src/utils/engtourBank.js — including why the Free Roam star and the
// primary-landmark flag are the BANK's, never a tour stop's.


const BLOCK_TYPES = [
  { type: "text",  label: "Text",  emoji: "📝" },
  { type: "image", label: "Image", emoji: "🖼" },
  { type: "video", label: "Video", emoji: "🎬" },
  { type: "link",  label: "Link",  emoji: "🔗" },
];
function makeBlock(type) {
  if (type === "link") return { _id: newId(), type: "link", label: "Learn more", url: "https://" };
  if (type === "image") return { _id: newId(), type: "image", src: "" };
  if (type === "video") return { _id: newId(), type: "video", url: "", autoplay: false };
  return { _id: newId(), type: "text", value: "" };
}

// Upload a File to the dev-only endpoint; resolves to the stored web path.
const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

// Downscale large images in the browser before upload. Serverless hosts (Vercel)
// cap the request body around 4.5 MB, and base64 inflates size ~33%, so a big
// phone photo would be rejected. We cap the longest side at 1600px and re-encode;
// small images pass through untouched (PNGs keep transparency).
async function downscaleImageDataUrl(file) {
  const dataUrl = await readFileAsDataUrl(file);
  const MAX = 1600;
  if (file.size <= 1_000_000) return dataUrl; // already small enough
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = dataUrl;
    });
    const scale = Math.min(1, MAX / Math.max(img.width, img.height));
    if (scale === 1 && file.size <= 3_000_000) return dataUrl;
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    canvas.getContext("2d").drawImage(img, 0, 0, w, h);
    // Keep PNG (alpha) as PNG; everything else becomes JPEG for a small payload.
    const type = file.type === "image/png" ? "image/png" : "image/jpeg";
    return canvas.toDataURL(type, 0.85);
  } catch {
    return dataUrl; // fall back to the original if canvas processing fails
  }
}

/** "Send test email": a sample completion email to the recipients as typed (api/send-tour-email.js). */
async function sendTestTourEmail(body) {
  const token = sessionStorage.getItem("engtourcreator_token");
  const res = await fetch(`${API_BASE}/api/tour-admin`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ op: "test-email", ...body }),
  });
  const j = await res.json().catch(() => ({}));
  if (res.status === 401) throw new Error("Your session expired. Log in again.");
  if (!res.ok) throw new Error(j.error || `Could not send (HTTP ${res.status}).`);
  return j;
}

async function uploadImageFile(file) {
  const dataUrl = await downscaleImageDataUrl(file);
  const token = sessionStorage.getItem("engtourcreator_token");
  const res = await fetch(`${API_BASE}/api/tour-admin`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify({ op: "upload", filename: file.name, dataUrl }),
  });
  if (!res.ok) {
    // Surface the server's reason (e.g. storage not configured, too large, auth).
    let msg = `Upload failed (HTTP ${res.status}).`;
    if (res.status === 401) msg = "Upload failed — your session expired. Log in again.";
    else if (res.status === 413) msg = "Image too large — try a smaller file.";
    else { try { const j = await res.json(); if (j?.error) msg = j.error; } catch { /* non-JSON */ } }
    throw new Error(msg);
  }
  const data = await res.json();
  return data.path;
}

function loadGoogleMapsScript(apiKey) {
  return new Promise((resolve) => {
    if (window.google && window.google.maps) { resolve(); return; }
    const existing = document.getElementById("gmap-script");
    if (existing) { existing.addEventListener("load", resolve); return; }
    const script = document.createElement("script");
    script.id = "gmap-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
    script.async = true;
    script.onload = resolve;
    document.head.appendChild(script);
  });
}

// zIndex matters: outline (the white building shell) must sit BELOW rooms,
// otherwise it paints over every room polygon and nothing is selectable.
const TYPE_STYLES = {
  outline:        { fillColor: "#ffffff", fillOpacity: 1,    strokeColor: "#aaaaaa", strokeOpacity: 1,   strokeWeight: 2,   zIndex: 0 },
  room:           { fillColor: "#FCF8EF", fillOpacity: 1,    strokeColor: "#dbdbdb", strokeOpacity: 0.9, strokeWeight: 1.5, zIndex: 1 },
  elevatorstairs: { fillColor: "#9e9e9e", fillOpacity: 1,    strokeColor: "#616161", strokeOpacity: 1,   strokeWeight: 1.5, zIndex: 1 },
  stair:          { fillColor: "#c4956a", fillOpacity: 1,    strokeColor: "#a0724a", strokeOpacity: 1,   strokeWeight: 1.5, zIndex: 1 },
  elevator:       { fillColor: "#b0bec5", fillOpacity: 1,    strokeColor: "#78909c", strokeOpacity: 1,   strokeWeight: 1.5, zIndex: 1 },
  corridor:       { fillColor: "#f5f5f5", fillOpacity: 1,    strokeColor: "#cccccc", strokeOpacity: 0.8, strokeWeight: 1,   zIndex: 1 },
  washroom:       { fillColor: "#e3f2fd", fillOpacity: 1,    strokeColor: "#90caf9", strokeOpacity: 1,   strokeWeight: 1.5, zIndex: 1 },
  other:          { fillColor: "#eeeeee", fillOpacity: 0.85, strokeColor: "#bdbdbd", strokeOpacity: 0.8, strokeWeight: 1,   zIndex: 1 },
};
const getStyle = (t) => TYPE_STYLES[t] || TYPE_STYLES.other;

// Textarea that grows to fit its content — used for the inline body paragraphs
// so the editor reads like flowing text rather than a fixed input box.
function AutoTextarea({ value, onChange, style, placeholder }) {
  const ref = useRef(null);
  const resize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = el.scrollHeight + "px";
  }, []);
  useEffect(() => { resize(); }, [value, resize]);
  return (
    <textarea
      ref={ref}
      value={value}
      placeholder={placeholder}
      onChange={e => { onChange(e.target.value); resize(); }}
      rows={1}
      style={{ resize: "none", overflow: "hidden", ...style }}
    />
  );
}

// A single-line editable field (title / subtitle) with a faint pencil at the end
// hinting that the text is editable.
function EditableLine({ value, onChange, placeholder, style, wrapStyle }) {
  const [focus, setFocus] = useState(false);
  return (
    <div style={{ position: "relative", ...wrapStyle }}>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
        style={{
          width: "100%", boxSizing: "border-box", border: "none", outline: "none", background: "transparent",
          borderBottom: `1px dashed ${focus ? UM.line : "transparent"}`, paddingRight: 20, ...style,
        }} />
      <span aria-hidden style={{ position: "absolute", right: 2, top: "50%", transform: "translateY(-50%)", fontSize: 13, opacity: 0.32, pointerEvents: "none" }}>✎</span>
    </div>
  );
}

// ── Geometry helpers ─────────────────────────────────────────────────────────

const COS_LAT = Math.cos(49.808 * Math.PI / 180); // ≈ 0.6441

function scaledDist(lat1, lng1, lat2, lng2) {
  return Math.hypot(lat2 - lat1, (lng2 - lng1) * COS_LAT);
}

// Find the nearest point on any edge of a Google Maps path to (lat, lng).
// Returns { edgeIndex, lat, lng } — used for Add Vertex insertion.
function findNearestEdge(lat, lng, path) {
  const n = path.getLength();
  let best = null, bestDist = Infinity;
  for (let i = 0; i < n; i++) {
    const p1 = path.getAt(i), p2 = path.getAt((i + 1) % n);
    const dx = (p2.lng() - p1.lng()) * COS_LAT, dy = p2.lat() - p1.lat();
    const len2 = dx * dx + dy * dy;
    if (len2 < 1e-20) continue;
    const t = Math.max(0, Math.min(1, ((lng - p1.lng()) * COS_LAT * dx + (lat - p1.lat()) * dy) / len2));
    const plat = p1.lat() + t * (p2.lat() - p1.lat());
    const plng = p1.lng() + t * (p2.lng() - p1.lng());
    const d = scaledDist(lat, lng, plat, plng);
    if (d < bestDist) { bestDist = d; best = { edgeIndex: i, lat: plat, lng: plng }; }
  }
  return best;
}


// Is (lat, lng) inside a Google Maps polygon path? Ray casting, so the Maps
// `geometry` library (not loaded here) is not needed.
function pointInPath(lat, lng, path) {
  const n = path.getLength();
  let inside = false;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const a = path.getAt(i), b = path.getAt(j);
    const yi = a.lat(), xi = a.lng(), yj = b.lat(), xj = b.lng();
    if ((yi > lat) !== (yj > lat) && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

export default function EngTourCreator() {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const polyMetaRef = useRef([]); // { poly, floor, building, room_number, type, origFill, origStroke }
  const stopMarkersRef = useRef([]);
  const roomLabelsRef = useRef([]); // { overlay, floor } — always-on room-number labels
  const [mapReady, setMapReady] = useState(false);
  const [selectedFloor, setSelectedFloor] = useState(2);
  useEffect(() => { selectedFloorRef.current = selectedFloor; }, [selectedFloor]);
  const [mapZoom, setMapZoom] = useState(19);
  // Room-number labels clutter the map, so they're hidden by default; this
  // toggle reveals every room number on the current floor.
  const [showRoomNumbers, setShowRoomNumbers] = useState(false);
  const [bankOpen, setBankOpen] = useState(false); // "Add stop from bank" picker
  const [editingBankKey, setEditingBankKey] = useState(null); // bank entry being edited
  const [deleteTourId, setDeleteTourId] = useState(null); // tour id pending delete-confirm
  const [testTour, setTestTour] = useState(null); // tour id being previewed 1:1 in a modal

  const [tourData, setTourData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTourId, setActiveTourId] = useState(null);
  const [activeStopIdx, setActiveStopIdx] = useState(null);

  // ── Editing mode: tour stops vs. free-roam (starred bank places) ──
  const [mode, setMode] = useState("tours"); // "tours" | "freeroam"

  const [resourcesOpen, setResourcesOpen] = useState(false); // collapsed by default
  const [search, setSearch] = useState("");
  const [searchMsg, setSearchMsg] = useState(null);
  const [roomOptions, setRoomOptions] = useState([]); // { code, building, room } for the search dropdown
  // The campus's preset room names (the pathfinder's sheets plus the operator's
  // published overlay — one name on every screen, never a list of our own).
  // An editor searches "library" and gets a room; a new stop's title starts as
  // the room's name. Null until loaded; everything works without it.
  const [roomNames, setRoomNames] = useState(null);
  const roomNamesRef = useRef(null);
  useEffect(() => { roomNamesRef.current = roomNames; }, [roomNames]);
  // Every room the dropdowns may offer, with its preset name beside the code.
  const roomChoices = useMemo(
    () => roomOptions.map(o => ({ ...o, name: roomNames ? roomNameFor(roomNames, o.building, o.room) : "" })),
    [roomOptions, roomNames]);
  const [tourInfoOpen, setTourInfoOpen] = useState(false); // the tour's title/subtitle/description editor
  const [introOpen, setIntroOpen] = useState(false);
  // Sidebar width, dragged by its right edge and remembered in this browser.
  const [sidebarW, setSidebarW] = useState(() => {
    try { const v = Number(localStorage.getItem(SIDEBAR_KEY)); return v ? clampSidebar(v) : SIDEBAR_DEFAULT; } catch { return SIDEBAR_DEFAULT; }
  });
  const sidebarDrag = useRef(null);
  const saveSidebarW = useCallback((w) => { try { localStorage.setItem(SIDEBAR_KEY, String(w)); } catch { /* private mode: not remembered */ } }, []);
  const resizeSidebar = useCallback((w) => { const c = clampSidebar(w); setSidebarW(c); saveSidebarW(c); }, [saveSidebarW]); // "Intro & closing": the welcome, room-numbers and completion screens

  // ── Stale shape overrides ──
  // Buildings whose polygons the live site serves from a KV override (saved
  // long ago by this editor's shape tool) instead of the current campus map.
  const [shapeOverrides, setShapeOverrides] = useState([]);
  const [resettingShapes, setResettingShapes] = useState(false);

  // ── Restore from a backup (an Export zip, or a tours.json) ──
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreNote, setRestoreNote] = useState(null); // "Restored 1 stop — press Save…"

  // ── Route Adjuster ────────────────────────────────────────────────────────
  // The nav graph WITH edges (the viewer and the rest of the creator get nodes
  // only), so a path can be drawn click by click along real corridors.
  const [navGraph, setNavGraph] = useState(null);     // { nodes, nodeById, edges, adj, roomDoors }
  const [routeMode, setRouteMode] = useState("std");  // which drawing is on screen: std | a11y
  const [routeLeg, setRouteLeg] = useState(null);     // index of the leg on screen, or null
  // While drawing: the committed node ids so far, `marks` = ids.length after
  // each click (for Undo), and `shaft` = a stair/elevator awaiting a floor.
  const [routeDraw, setRouteDraw] = useState(null);
  const routeDrawRef = useRef(null);
  useEffect(() => { routeDrawRef.current = routeDraw; }, [routeDraw]);
  const [hoverPath, setHoverPath] = useState(null);   // { targetId, ids } under the cursor while drawing
  const hoverPathRef = useRef(null);
  useEffect(() => { hoverPathRef.current = hoverPath; }, [hoverPath]);
  const routeHoverRef = useRef(null);                 // (latLng) => void, set while drawing
  const routeClickRef = useRef(null);                 // () => void, set while drawing
  const routeEdgeOverlaysRef = useRef([]);            // the edge network + shaft dots
  const routePathOverlaysRef = useRef([]);            // the leg's path, hover preview, markers
  const worldPtsRef = useRef({ graph: null, pts: null }); // node → world point, for pixel hit-testing

  // ── Create Spot ──────────────────────────────────────────────────────────
  // Somewhere worth stopping that isn't a room: the middle of the atrium, a
  // display case in a corridor, the bottom of a staircase. The map has no
  // polygon to click for any of those, so a spot is placed by hand and pinned
  // to the nearest nav node — the node is what routing actually walks to, and
  // borrowing an existing one means no nav data has to be invented or edited.
  const navNodesRef = useRef([]);                       // nav graph nodes, for the snap
  const [navNodesReady, setNavNodesReady] = useState(false);
  const [placingSpot, setPlacingSpot] = useState(false);
  const placingSpotRef = useRef(false);
  useEffect(() => { placingSpotRef.current = placingSpot; }, [placingSpot]);
  const ghostPinRef = useRef(null);                     // the pin that follows the cursor
  const spotMarkersRef = useRef([]);                    // { marker, floor } per saved spot
  const [spotDraft, setSpotDraft] = useState(null);     // { lat, lng, node } awaiting a label
  const [spotLabel, setSpotLabel] = useState("");

  const [saving, setSaving] = useState(false);
  const [saveState, setSaveState] = useState("idle"); // idle | saved | error
  const [saveError, setSaveError] = useState(null);   // server message when a save fails

  // ── Edit history ──
  const [historyOpen, setHistoryOpen] = useState(false);
  const [versions, setVersions] = useState([]);        // [{ ts }] newest first
  const [openDays, setOpenDays] = useState(() => new Set()); // days unfolded in the History list
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [previewTs, setPreviewTs] = useState(null);    // non-null while viewing an old save
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideInput, setOverrideInput] = useState("");

  // ── Export everything (one zip: live data, every history snapshot, every photo) ──
  // null while closed; { phase, lines, done, error } while the panel is up.
  const [exportState, setExportState] = useState(null);
  const exportCancelRef = useRef(false);

  // ── Auth: username + password, matched against TOUR_EDITORS server-side. ──
  // The issued token is editor-scoped (only editor tokens may write tours), and
  // carries the editor's name so saves show up under their name in Edit History.
  const tokenRef = useRef(sessionStorage.getItem("engtourcreator_token") || null);
  const [authed, setAuthed] = useState(() => !!sessionStorage.getItem("engtourcreator_token"));
  const [isAdmin, setIsAdmin] = useState(() => tokenSaysAdmin(sessionStorage.getItem("engtourcreator_token")));
  const [editorName, setEditorName] = useState(() => sessionStorage.getItem("engtourcreator_user") || "");
  const [userInput, setUserInput] = useState("");
  const [pwInput, setPwInput] = useState("");
  const [pwError, setPwError] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [pwRateLimited, setPwRateLimited] = useState(false);

  // ── Polygon tools shared ──
  const selectedFloorRef = useRef(2);
  const buildingDataRef = useRef({});
  const changedBuildingKeysRef = useRef(new Set());
  const [polyEditorSaving, setPolyEditorSaving] = useState(false);
  const [polyEditorSaveState, setPolyEditorSaveState] = useState("idle");

  // ── Drag vertices mode ──
  const dragPolyModeRef = useRef(false);
  const dragSelectedPolyRef = useRef(null);
  const dragVertexMarkersRef = useRef([]);
  const clearDragSelectionFnRef = useRef(null);
  const dragAddVertexModeRef = useRef(false);
  const [dragPolyMode, setDragPolyMode] = useState(false);
  const [dragHasSelection, setDragHasSelection] = useState(false);
  const [dragAddVertexMode, setDragAddVertexMode] = useState(false);

  const [titleClickCount, setTitleClickCount] = useState(0);
  const [guideOpen, setGuideOpen] = useState(false);
  const [guideTab, setGuideTab] = useState(0);

  const handlePasswordSubmit = useCallback(async () => {
    if (pwLoading || pwRateLimited || !userInput.trim() || !pwInput) return;
    setPwLoading(true); setPwError(false);
    try {
      const res = await fetch(`${API_BASE}/api/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: userInput.trim(), password: pwInput }),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data.token) { tokenRef.current = data.token; sessionStorage.setItem("engtourcreator_token", data.token); }
        setIsAdmin(!!data.admin || tokenSaysAdmin(data.token));
        if (data.user) { setEditorName(data.user); sessionStorage.setItem("engtourcreator_user", data.user); }
        else { setEditorName(""); sessionStorage.removeItem("engtourcreator_user"); }
        setAuthed(true); setPwInput('');
      } else if (res.status === 429) setPwRateLimited(true);
      else setPwError(true);
    } catch { setPwError(true); }
    finally { setPwLoading(false); }
  }, [userInput, pwInput, pwLoading, pwRateLimited]);

  const tourDataRef = useRef(null);
  const activeTourIdRef = useRef(null);
  const modeRef = useRef("tours");
  useEffect(() => { tourDataRef.current = tourData; }, [tourData]);
  useEffect(() => { activeTourIdRef.current = activeTourId; }, [activeTourId]);
  useEffect(() => { modeRef.current = mode; }, [mode]);

  // Apply a freshly-fetched tour-data document into editor state.
  const applyLoaded = useCallback((data, { resetActive = false } = {}) => {
    if (!Array.isArray(data.freeRoamLocations)) data.freeRoamLocations = [];
    // Seed the reusable stop bank from existing stops if it's absent, then fold
    // any legacy free-roam places into it as starred entries.
    if (!Array.isArray(data.stopBank)) data.stopBank = buildStopBank(data);
    migrateFreeRoamToBank(data);
    normalizeStopLabels(data);
    setTourData(data);
    setActiveStopIdx(null);
    setActiveTourId(prev => (!resetActive && prev && data.tours?.some(t => t.id === prev)) ? prev : (data.tours?.[0]?.id ?? null));
  }, []);

  // Load the current (live) tour data and leave preview mode.
  const loadCurrent = useCallback(async () => {
    const res = await fetch(`${API_BASE}/api/tour-admin`, {
      headers: tokenRef.current ? { Authorization: `Bearer ${tokenRef.current}` } : {},
    });
    if (res.status === 401) { // token expired/invalid — drop back to the gate
      sessionStorage.removeItem("engtourcreator_token"); tokenRef.current = null; setAuthed(false); setIsAdmin(false);
      throw new Error("unauthorized");
    }
    const data = await res.json();
    applyLoaded(data, { resetActive: true });
    setPreviewTs(null);
    setLoading(false);
  }, [applyLoaded]);

  // ── Initial load (after auth) ──
  useEffect(() => {
    if (!authed) return;
    loadCurrent().catch(e => {
      if (e.message !== "unauthorized") { setLoading(false); setTourData({ questions: [], tours: [], freeRoamLocations: [], tagTypes: [] }); }
    });
  }, [authed, loadCurrent]);

  // ── Insert a stop for a room polygon. index == null → append; otherwise the
  //    new stop is spliced in at that position (used by the between-stops [+]). ──
  const insertStop = useCallback((meta, index) => {
    const td = tourDataRef.current;
    const tid = activeTourIdRef.current;
    if (!td || !tid || !meta) return;
    const tour = td.tours.find(t => t.id === tid);
    if (!tour) return;
    const newStop = {
      building: meta.building, room: meta.room_number,
      label: meta.label || formatRoomCode(meta.building, meta.room_number),
      // Only a hand-placed spot carries these; a room stop is shaped exactly as
      // it always was, so nothing already saved changes meaning.
      ...(meta.nodeId ? { nodeId: meta.nodeId, floor: meta.floor, lat: meta.lat, lng: meta.lng } : {}),
      // The title starts as the room's preset name (or what the picker passed
      // in), so "search library, pick, done" needs no typing.
      title: meta.title || (meta.nodeId ? "" : roomNameFor(roomNamesRef.current, meta.building, meta.room_number)) || "",
      departments: [],
      subtitle: "", images: [],
      text: "", videoUrl: "", quote: "",
    };
    const stops = [...tour.stops];
    const at = (index == null || index < 0 || index > stops.length) ? stops.length : index;
    stops.splice(at, 0, newStop);
    setTourData({ ...td, tours: td.tours.map(t => t.id !== tid ? t : { ...t, stops }) });
    setActiveStopIdx(at);
  }, []);
  const addStopForMeta = useCallback((meta) => insertStop(meta, null), [insertStop]);

  // Append a copy of a bank stop (a reusable, saved stop) to the active tour.
  // What's Next is left blank — it's specific to this tour's ordering.
  const insertBankStop = useCallback((entry) => {
    const td = tourDataRef.current;
    const tid = activeTourIdRef.current;
    if (!td || !tid || !entry) return;
    const tour = td.tours.find(t => t.id === tid);
    if (!tour) return;
    const { guidance, ...rest } = entry; // eslint-disable-line no-unused-vars
    const stops = [...tour.stops, { ...stripBankFlags(rest), guidance: "" }];
    setTourData({ ...td, tours: td.tours.map(t => t.id !== tid ? t : { ...t, stops }) });
    setActiveStopIdx(stops.length - 1);
  }, []);

  // Open the bank picker, first refreshing it so it lists EVERY current stop
  // (across all tours) plus any previously-banked ones.
  const openBank = useCallback(() => {
    setTourData(td => td ? { ...td, stopBank: buildStopBank(td) } : td);
    setBankOpen(true);
  }, []);

  // Remove a stop from the bank. Orphaned entries (not used by any tour) stay
  // gone; an entry that's still a live tour stop reappears next time the picker
  // is opened, since the bank is rebuilt from current stops.
  const deleteBankStop = useCallback((key) => {
    setTourData(td => ({ ...td, stopBank: (td.stopBank || []).filter(s => bankKey(s) !== key) }));
  }, []);

  // Live-edit a bank entry (used by the bank editor modal).
  const updateBankStop = useCallback((key, patch) => {
    setTourData(td => ({ ...td, stopBank: (td.stopBank || []).map(s => bankKey(s) === key ? { ...s, ...patch } : s) }));
  }, []);

  // Step a bank entry's Free Roam visibility: Don't show → Show always → Show zoomed.
  const cycleBankFreeRoam = useCallback((key) => {
    setTourData(td => ({ ...td, stopBank: (td.stopBank || []).map(s => bankKey(s) === key ? { ...s, ...freeRoamPatch(nextFreeRoamMode(freeRoamMode(s))) } : s) }));
  }, []);

  // Clicking a room in free-roam mode: star its bank entry (creating one if the
  // room isn't in the bank yet) and open it for editing.
  const starRoomForMeta = useCallback((meta) => {
    if (!meta) return;
    const key = bankKey({ building: meta.building, room: meta.room_number, nodeId: meta.nodeId });
    setTourData(td => {
      const list = td.stopBank || [];
      if (list.some(s => bankKey(s) === key)) {
        return { ...td, stopBank: list.map(s => bankKey(s) === key ? { ...s, freeRoam: true } : s) };
      }
      const entry = {
        building: meta.building, room: meta.room_number,
        label: meta.label || formatRoomCode(meta.building, meta.room_number),
        ...(meta.nodeId ? { nodeId: meta.nodeId, floor: meta.floor, lat: meta.lat, lng: meta.lng } : {}),
        title: meta.title || (meta.nodeId ? "" : roomNameFor(roomNamesRef.current, meta.building, meta.room_number)) || "",
        subtitle: "", text: "", images: [], videoUrl: "",
        // Starts as "Show always": Free Roam opens zoomed out, where a "Show zoomed" star is hidden.
        departments: [], icon: "📍", ...freeRoamPatch('always'),
      };
      return { ...td, stopBank: [...list, entry] };
    });
    setEditingBankKey(key);
  }, []);

  // Delete an entire tour (its stops go with it; the stop bank is untouched, so
  // the stops remain reusable). Caller confirms first.
  const deleteTour = useCallback((id) => {
    setTourData(td => {
      if (!td) return td;
      const tours = (td.tours || []).filter(t => t.id !== id);
      return { ...td, tours };
    });
    setActiveTourId(prev => {
      if (prev !== id) return prev;
      const remaining = (tourDataRef.current?.tours || []).filter(t => t.id !== id);
      return remaining[0]?.id ?? null;
    });
    setActiveStopIdx(null);
  }, []);

  // Route a polygon/search click to the right action based on the active mode:
  // add a tour stop, or (in free-roam mode) star the room's bank entry.
  const addRoomForMeta = useCallback((meta) => {
    if (modeRef.current === "routes") return; // the map is for drawing paths, not adding stops
    if (modeRef.current === "freeroam") starRoomForMeta(meta);
    else addStopForMeta(meta);
  }, [starRoomForMeta, addStopForMeta]);
  const addStopRef = useRef(addRoomForMeta);
  useEffect(() => { addStopRef.current = addRoomForMeta; }, [addRoomForMeta]);

  // Which buildings are being served from an override rather than the bundled
  // file. Asked once the map is up; a failure simply shows no banner.
  useEffect(() => {
    if (!authed || !mapReady) return;
    let cancelled = false;
    fetch(`${API_BASE}/api/building`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(tokenRef.current ? { Authorization: `Bearer ${tokenRef.current}` } : {}) },
      body: JSON.stringify({ op: "status", keys: BUILDING_KEYS.map(b => b.key) }),
    })
      .then(r => (r.ok ? r.json() : null))
      .then(j => { if (!cancelled && j?.overrides) setShapeOverrides(Object.keys(j.overrides).filter(k => j.overrides[k])); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [authed, mapReady]);

  // Drop the overrides so the current campus map is served, then reload: the
  // polygons are built once when the map is created.
  const resetShapeOverrides = useCallback(async () => {
    const names = shapeOverrides.map(k => buildingShortCode(BUILDING_KEYS.find(b => b.key === k)?.building) || k).join(", ");
    if (!window.confirm(`Use the current campus map for ${names}?\n\nThe room shapes saved from this editor's shape tool will be discarded and the buildings will be drawn from the campus map the pathfinder and the phone app use. Stops stay as they are; one on a room that only existed in the old shapes will show “not on map” until it is reassigned.`)) return;
    setResettingShapes(true);
    try {
      for (const key of shapeOverrides) {
        const r = await fetch(`${API_BASE}/api/building`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(tokenRef.current ? { Authorization: `Bearer ${tokenRef.current}` } : {}) },
          body: JSON.stringify({ op: "reset", key }),
        });
        if (!r.ok) throw new Error(`Could not reset ${key} (HTTP ${r.status}).`);
      }
      window.location.reload();
    } catch (e) {
      setResettingShapes(false);
      window.alert(e?.message || "Reset failed.");
    }
  }, [shapeOverrides]);

  // Preset room names, fetched once after sign-in. Failing is not fatal: the
  // dropdowns show codes alone and titles start blank, as they always did.
  useEffect(() => {
    if (!authed) return;
    let cancelled = false;
    loadEngTourRoomNames({ apiBase: API_BASE })
      .then(n => { if (!cancelled) setRoomNames(n); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [authed]);

  // ── Create Spot ──────────────────────────────────────────────────────────
  // Nav nodes, fetched once, purely so a placed pin can find the node nearest
  // it. Failing is not fatal: the button simply stays disabled and everything
  // else in the creator works exactly as before.
  useEffect(() => {
    if (!authed) return;
    let cancelled = false;
    fetch(`${API_BASE}/api/navgraph?keys=${NAV_KEYS.join(',')}&edges=1`)
      .then(r => r.json())
      .then(g => {
        if (cancelled) return;
        const nodes = (g?.nodes || []).filter(n => Number.isFinite(n.lat) && Number.isFinite(n.lng));
        navNodesRef.current = nodes;
        setNavNodesReady(nodes.length > 0);
        // For the Route Adjuster: edges, and each room's door nodes keyed the
        // way the tour data names a room ("e2::229"), which is how the viewer's
        // findRoomEntry resolves a stop to the doors a leg may end at.
        const nodeById = new Map(nodes.map(n => [n.id, n]));
        const roomDoors = new Map();
        for (const r of g?.rooms || []) {
          const k = `${normalizeBuilding(r.building)}::${String(r.room_number ?? "").toLowerCase()}`;
          const ids = (r.node_ids || []).filter(id => nodeById.has(id));
          if (ids.length) roomDoors.set(k, [...new Set([...(roomDoors.get(k) || []), ...ids])]);
        }
        const edges = Array.isArray(g?.edges) ? g.edges : [];
        setNavGraph({ nodes, nodeById, edges, adj: buildAdjacency(edges), roomDoors });
      })
      .catch(() => { if (!cancelled) setNavNodesReady(false); });
    return () => { cancelled = true; };
  }, [authed]);

  // Nearest nav node to a dropped pin. Nodes on the floor being viewed win
  // outright — a pin dropped on the 2nd floor plan means the 2nd floor, and the
  // node directly below it in plan view is usually on another storey.
  const nearestNavNode = useCallback((lat, lng) => {
    const nodes = navNodesRef.current;
    if (!nodes.length) return null;
    const floor = selectedFloorRef.current;
    const pick = (list) => {
      let best = null, bestD = Infinity;
      for (const n of list) {
        const d = (n.lat - lat) ** 2 + (n.lng - lng) ** 2;
        if (d < bestD) { bestD = d; best = n; }
      }
      return best;
    };
    const sameFloor = nodes.filter(n => Number(n.floor) === Number(floor));
    return pick(sameFloor.length ? sameFloor : nodes);
  }, []);

  // Click on the map (or on a building polygon, which swallows map clicks)
  // while placing: snap, then ask for the label. A spot is for the places that
  // are not rooms — an atrium, a study area, a corridor — so three things are
  // decided for the editor rather than asked: the FLOOR is the one being
  // looked at, the BUILDING is whichever outline the pin landed inside on that
  // floor, and the NODE is the nearest nav point on that floor, which is what
  // the tour actually walks to. The node's own building is only the fallback
  // for a pin dropped outside every outline.
  const placeSpotAt = useCallback((lat, lng) => {
    const node = nearestNavNode(lat, lng);
    if (!node) { setSearchMsg("Nav data hasn't loaded yet — try again in a moment."); return; }
    const floor = selectedFloorRef.current;
    const outline = polyMetaRef.current.find(m => m.type === "outline" && m.floor === floor && pointInPath(lat, lng, m.poly.getPath()))
      || polyMetaRef.current.find(m => m.type === "outline" && pointInPath(lat, lng, m.poly.getPath()));
    const building = normalizeBuilding(outline?.building) || normalizeBuilding(node.building) || null;
    setSpotDraft({ lat, lng, node, building, floor });
    setSpotLabel("");
    setPlacingSpot(false);
  }, [nearestNavNode]);
  const placeSpotRef = useRef(placeSpotAt);
  useEffect(() => { placeSpotRef.current = placeSpotAt; }, [placeSpotAt]);

  // Commit the labelled spot. Purely additive: a new `customSpots` entry, and
  // nothing existing in the tour data is read, moved or rewritten.
  const confirmSpot = useCallback(() => {
    const name = spotLabel.trim();
    if (!name || !spotDraft) return;
    const { lat, lng, node, building, floor } = spotDraft;
    const label = spotLabelFor(building, name);
    const spot = {
      id: `spot_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`,
      // `label` reads like a room code — "E2-Atrium Stage" — so a spot sits in
      // the stop list beside E2-213 rather than looking like a different kind
      // of thing. `name` keeps what was actually typed, for the map pin, where
      // the building prefix is redundant and the space is tight.
      label,
      name,
      nodeId: node.id,
      // The outline the pin landed in and the floor being viewed (decided in
      // placeSpotAt), in the "e1" form the rest of the tour data uses.
      building,
      room: node.room_number != null ? String(node.room_number) : null,
      floor,
      lat, lng,
      createdAt: Date.now(),
    };
    setTourData(td => ({ ...(td || {}), customSpots: [...((td && td.customSpots) || []), spot] }));
    setSpotDraft(null);
    setSpotLabel("");
    setSearchMsg(`Created “${label}” — it's now in the search box, ready to add.`);
  }, [spotLabel, spotDraft]);

  const cancelSpot = useCallback(() => { setSpotDraft(null); setSpotLabel(""); setPlacingSpot(false); }, []);

  // Add a custom spot to the tour (or star it, in free-roam mode). The stop
  // carries `nodeId`, which is what the viewer walks to; `room` stays null
  // unless the borrowed node happens to be a real room.
  const addCustomSpotRef = useRef(null);
  const addCustomSpot = useCallback((spot) => {
    if (!spot) return;
    addRoomForMeta({
      building: spot.building, room_number: spot.room,
      label: spot.label, nodeId: spot.nodeId, floor: spot.floor,
      lat: spot.lat, lng: spot.lng,
    });
  }, [addRoomForMeta]);
  useEffect(() => { addCustomSpotRef.current = addCustomSpot; }, [addCustomSpot]);

  // Delete a hand-placed spot. Stops already made from it keep their nodeId,
  // so the tour still walks to the same place; only the shortcut goes.
  const deleteCustomSpot = useCallback((spot) => {
    const td = tourDataRef.current;
    const used = (td?.tours || []).reduce((n, t) => n + (t.stops || []).filter(s => s.nodeId === spot.nodeId).length, 0);
    const msg = used
      ? `Delete the spot “${spot.label}”? ${used} stop${used === 1 ? "" : "s"} made from it will keep working; the spot just stops being offered.`
      : `Delete the spot “${spot.label}”?`;
    if (!window.confirm(msg)) return;
    setTourData(t => ({ ...t, customSpots: (t.customSpots || []).filter(x => x.id !== spot.id) }));
    setSearchMsg(`Deleted “${spot.label}”.`);
  }, []);

  // Insert a stop at `index` from a typed/picked room code (between-stops [+]).
  // Returns { ok, msg } so the inline picker can show an error.
  const insertStopByCode = useCallback((index, codeStr) => {
    const parsed = parseRoomQuery(codeStr);
    if (!parsed) return { ok: false, msg: 'Use a code like "E2-213".' };
    const match = polyMetaRef.current.find(p => p.building === parsed.building && p.room_number === String(parsed.room));
    if (!match) return { ok: false, msg: `Couldn't find ${formatRoomCode(parsed.building, parsed.room)}.` };
    insertStop(match, index);
    return { ok: true };
  }, [insertStop]);

  // ── Init map ─────────────────────────────────────────────────
  useEffect(() => {
    if (!authed) return;
    let cancelled = false;
    async function init() {
      await loadGoogleMapsScript(GOOGLE_MAPS_API_KEY);
      if (cancelled) return;
      const map = new window.google.maps.Map(mapRef.current, {
        center: EITC_CENTER, zoom: 19, mapTypeId: "satellite", tilt: 0,
        disableDefaultUI: true, zoomControl: true, gestureHandling: "greedy",
      });
      mapInstanceRef.current = map;

      class RoomLabel extends window.google.maps.OverlayView {
        constructor(position, text) {
          super();
          this.position = position; this.text = text; this._div = null;
        }
        onAdd() {
          const d = document.createElement("div");
          d.textContent = this.text;
          d.style.cssText = [
            "position:absolute", "transform:translate(-50%,-50%)",
            "background:rgba(255,255,255,0.9)", "border-radius:4px",
            "padding:2px 6px", "font-family:Arial,sans-serif", "font-weight:700",
            "color:#111", "white-space:nowrap", "pointer-events:none",
            "user-select:none", "box-shadow:0 1px 5px rgba(0,0,0,0.25)",
          ].join(";");
          this._div = d;
          this.getPanes().floatPane.appendChild(d);
          this.applyZoom(mapInstanceRef.current?.getZoom());
        }
        applyZoom(z) {
          if (!this._div || z == null) return;
          this._div.style.fontSize = Math.max(9, Math.min(20, Math.round((z - 16) * 4))) + "px";
        }
        draw() {
          const pos = this.getProjection()?.fromLatLngToDivPixel(this.position);
          if (pos && this._div) { this._div.style.left = pos.x + "px"; this._div.style.top = pos.y + "px"; }
        }
        onRemove() { if (this._div) { this._div.parentNode?.removeChild(this._div); this._div = null; } }
      }

      map.addListener("zoom_changed", () => setMapZoom(map.getZoom()));

      // ── Drag mode helpers ──────────────────────────────────────────────────────
      const clearDragVertexMarkersFn = () => {
        dragVertexMarkersRef.current.forEach(m => m.setMap(null));
        dragVertexMarkersRef.current = [];
      };

      const clearDragSelectionFn = () => {
        clearDragVertexMarkersFn();
        if (dragSelectedPolyRef.current) {
          const { meta } = dragSelectedPolyRef.current;
          meta.poly.setOptions({ strokeColor: meta.origStroke, strokeWeight: getStyle(meta.type).strokeWeight });
          dragSelectedPolyRef.current = null;
          setDragHasSelection(false);
        }
      };
      clearDragSelectionFnRef.current = clearDragSelectionFn;

      const showDragVerticesFn = (meta) => {
        clearDragSelectionFn();
        meta.poly.setOptions({ strokeColor: '#00bcd4', strokeWeight: 3 });
        dragSelectedPolyRef.current = { meta };
        setDragHasSelection(true);
        const path = meta.poly.getPath();
        const circleIcon = {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 8, fillColor: '#00bcd4', fillOpacity: 1,
          strokeColor: '#ffffff', strokeWeight: 2,
        };
        // Translucent "+" shown while dragging so the exact drop point is visible.
        const crosshairIcon = {
          path: 'M -1,-9 1,-9 1,-1 9,-1 9,1 1,1 1,9 -1,9 -1,1 -9,1 -9,-1 -1,-1 Z',
          scale: 1, fillColor: '#00bcd4', fillOpacity: 0.35,
          strokeColor: '#00bcd4', strokeWeight: 1.5,
          anchor: new window.google.maps.Point(0, 0),
        };
        for (let i = 0; i < path.getLength(); i++) {
          const pos = path.getAt(i);
          const marker = new window.google.maps.Marker({
            position: { lat: pos.lat(), lng: pos.lng() },
            map, zIndex: 800, draggable: true, icon: circleIcon,
          });
          const idx = i;
          marker.addListener('drag', (e) => {
            path.setAt(idx, e.latLng);
            marker.setIcon(crosshairIcon);
          });
          marker.addListener('dragend', (e) => {
            path.setAt(idx, e.latLng);
            marker.setIcon(circleIcon);
            changedBuildingKeysRef.current.add(meta.buildingKey);
          });
          marker.addListener('click', (ev) => { window.google.maps.event.stop(ev); });
          dragVertexMarkersRef.current.push(marker);
        }
      };

      // ── Add Vertex (drag sub-mode) ──────────────────────────────────────────
      const addVertexFn = (meta, lat, lng) => {
        const path = meta.poly.getPath();
        const nearest = findNearestEdge(lat, lng, path);
        if (!nearest) return;
        path.insertAt(nearest.edgeIndex + 1, new window.google.maps.LatLng(nearest.lat, nearest.lng));
        changedBuildingKeysRef.current.add(meta.buildingKey);
        showDragVerticesFn(meta);
      };


      const results = await Promise.all(
        BUILDING_KEYS.map(({ key, building }) =>
          fetch(`${API_BASE}/api/building?key=${encodeURIComponent(key)}`).then(r => r.json()).then(data => ({ data, building, key }))
        )
      );
      results.forEach(({ data, building, key }) => {
        buildingDataRef.current[key] = data;
        (data.polygons || []).forEach((item, polyIdx) => {
          if (!item.coordinates?.length) return;
          const type = item.type?.toLowerCase() || "other";
          const style = getStyle(type);
          const floor = item.floor ?? 1;
          const paths = item.coordinates.map(c => ({ lat: c.latitude, lng: c.longitude }));
          // Only a real room can become a stop: a stairwell or an elevator is
          // numbered in the file too, but is nowhere to send a visitor.
          const clickable = isStopRoom(type, item.room_number);
          const zIndex = type === "outline" ? 0 : (clickable ? 20 : 5);
          const poly = new window.google.maps.Polygon({
            paths, ...style, zIndex, clickable: true,
            map: floor === 2 ? map : null,
          });
          const meta = {
            poly, floor, building, buildingKey: key, polyIdx, type,
            room_number: item.room_number ? String(item.room_number) : null,
            origFill: style.fillColor, origStroke: style.strokeColor,
          };
          // Room-number tags only on rooms — a "253st" on every staircase and
          // an "outline" tag on the shell were clutter, not information.
          if (clickable) {
            let clat = 0, clng = 0;
            paths.forEach(p => { clat += p.lat; clng += p.lng; });
            const center = new window.google.maps.LatLng(clat / paths.length, clng / paths.length);
            const label = new RoomLabel(center, String(item.room_number));
            label.setMap(null);
            roomLabelsRef.current.push({ overlay: label, floor, building, room: String(item.room_number) });
          }
          poly.addListener("click", (e) => {
            // Placing a spot beats every other click meaning. Building polygons
            // cover most of the map and swallow the map's own click, so without
            // this the pin could only ever be dropped outdoors.
            if (placingSpotRef.current) {
              window.google.maps.event.stop(e);
              placeSpotRef.current(e.latLng.lat(), e.latLng.lng());
              return;
            }
            if (dragPolyModeRef.current) {
              if (dragAddVertexModeRef.current && dragSelectedPolyRef.current?.meta.poly === poly) {
                addVertexFn(meta, e.latLng.lat(), e.latLng.lng());
              } else {
                showDragVerticesFn(meta);
              }
              return;
            }
            if (clickable) addStopRef.current(meta);
          });
          const trackChange = () => changedBuildingKeysRef.current.add(key);
          poly.getPath().addListener("set_at", trackChange);
          poly.getPath().addListener("insert_at", trackChange);
          poly.getPath().addListener("remove_at", trackChange);
          polyMetaRef.current.push(meta);
        });
      });

      map.addListener('click', (e) => {
        if (placingSpotRef.current && e?.latLng) {
          placeSpotRef.current(e.latLng.lat(), e.latLng.lng());
          return;
        }
        if (routeDrawRef.current) { routeClickRef.current?.(); return; }
        if (dragPolyModeRef.current && !dragAddVertexModeRef.current) clearDragSelectionFn();
      });

      // The pin that rides the cursor while placing. One marker, moved — a
      // marker per mousemove would thrash the map. While a route is being
      // drawn the same event feeds the hover path instead.
      map.addListener('mousemove', (e) => {
        if (!e?.latLng) return;
        if (routeDrawRef.current) { routeHoverRef.current?.(e.latLng); return; }
        const ghost = ghostPinRef.current;
        if (!ghost || !placingSpotRef.current) return;
        ghost.setPosition(e.latLng);
      });

      // Distinct, sorted room codes for the search dropdown.
      const seen = new Set();
      const opts = [];
      polyMetaRef.current.forEach(m => {
        if (!isStopRoom(m.type, m.room_number)) return;
        const code = formatRoomCode(m.building, m.room_number);
        if (seen.has(code)) return;
        seen.add(code);
        opts.push({ code, building: m.building, room: m.room_number });
      });
      opts.sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
      setRoomOptions(opts);

      setMapReady(true);
    }
    init();
    return () => { cancelled = true; };
  }, [authed]);

  // In routes mode the map is about ONE leg: only its start and end stops are
  // marked and labelled, so the path reads against two points, not ten.
  const routeFocus = useMemo(() => {
    const idx = routeDraw ? routeDraw.idx : routeLeg;
    if (mode !== "routes" || idx == null) return null;
    return new Set([idx - 1, idx].filter(i => i >= 0));
  }, [mode, routeDraw, routeLeg]);
  const routeFocusRooms = useMemo(() => {
    const tour = tourData?.tours?.find(t => t.id === activeTourId);
    if (!routeFocus || !tour) return null;
    const set = new Set();
    for (const i of routeFocus) { const st = tour.stops[i]; if (st) set.add(`${normalizeBuilding(st.building)}::${String(st.room ?? "").toLowerCase()}`); }
    return set;
  }, [routeFocus, tourData, activeTourId]);

  // ── Floor change ─────────────────────────────────────────────
  useEffect(() => {
    if (!mapReady) return;
    const map = mapInstanceRef.current;
    clearDragSelectionFnRef.current?.();
    polyMetaRef.current.forEach(({ poly, floor }) => poly.setMap(floor === selectedFloor ? map : null));
  }, [selectedFloor, mapReady]);

  // ── Room-number labels: hidden by default (map declutter); the "Show room
  // numbers" toggle reveals every label on the current floor. ──
  useEffect(() => {
    if (!mapReady) return;
    const map = mapInstanceRef.current;
    roomLabelsRef.current.forEach(({ overlay, floor, building, room }) => {
      const onFloor = floor === selectedFloor;
      // Routes mode: the leg's two rooms are labelled whatever the toggle says, and nothing else is.
      const show = mode === "routes"
        ? onFloor && !!routeFocusRooms?.has(`${normalizeBuilding(building)}::${String(room).toLowerCase()}`)
        : showRoomNumbers && onFloor;
      overlay.setMap(show ? map : null);
      if (show) overlay.applyZoom?.(mapZoom);
    });
  }, [showRoomNumbers, mapZoom, selectedFloor, mapReady, mode, routeFocusRooms]);

  // ── Create Spot: the pin that follows the cursor ──────────────────────────
  // Placement is a mode, so it needs to look like one from the moment it starts:
  // a crosshair over the map and a pin under the cursor, both gone the instant
  // it ends (placed, cancelled, or Escape).
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!mapReady || !map) return undefined;

    // Buildings are the whole point of placing a spot — a stop in the middle of
    // the atrium is drawn on top of E2's outline — so the polygons have to get
    // out of the way while placing. A clickable Maps overlay swallows the map's
    // own mousemove and click, which froze the pin the moment the cursor
    // crossed a building and made "on top of a room" the one place it looked
    // like you couldn't drop it. They all go non-clickable for the duration and
    // come back after; every polygon here is created clickable, so restoring
    // them all is faithful.
    const setPolysClickable = (on) => {
      polyMetaRef.current.forEach(m => m.poly.setOptions({ clickable: on }));
    };

    if (!placingSpot) {
      ghostPinRef.current?.setMap(null);
      ghostPinRef.current = null;
      map.setOptions({ draggableCursor: null });
      setPolysClickable(true);
      return undefined;
    }

    setPolysClickable(false);
    map.setOptions({ draggableCursor: 'crosshair' });
    ghostPinRef.current = new window.google.maps.Marker({
      map,
      position: map.getCenter(),
      clickable: false,
      zIndex: 9999,
      icon: {
        path: 'M12 23s7.5-8.2 7.5-13.1a7.5 7.5 0 1 0-15 0C4.5 14.8 12 23 12 23z',
        fillColor: UM.gold, fillOpacity: 1,
        strokeColor: '#4A1E13', strokeWeight: 1.6,
        scale: 1.5, anchor: new window.google.maps.Point(12, 23),
      },
    });

    const onKey = (e) => { if (e.key === 'Escape') setPlacingSpot(false); };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      ghostPinRef.current?.setMap(null);
      ghostPinRef.current = null;
      map.setOptions({ draggableCursor: null });
      setPolysClickable(true);
    };
  }, [placingSpot, mapReady]);

  // ── Create Spot: pins for spots already made ──────────────────────────────
  // Shown on their own floor, so the editor can see what exists before adding
  // another. Clicking one adds it, same as picking it out of the search.
  useEffect(() => {
    if (!mapReady) return;
    const map = mapInstanceRef.current;
    spotMarkersRef.current.forEach(({ marker }) => marker.setMap(null));
    spotMarkersRef.current = [];
    for (const spot of tourData?.customSpots || []) {
      if (!Number.isFinite(spot?.lat) || !Number.isFinite(spot?.lng)) continue;
      const marker = new window.google.maps.Marker({
        map: Number(spot.floor) === Number(selectedFloor) ? map : null,
        position: { lat: spot.lat, lng: spot.lng },
        title: `${spot.label} — click to add`,
        zIndex: 900,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          fillColor: UM.gold, fillOpacity: 1, strokeColor: '#4A1E13', strokeWeight: 2, scale: 7,
        },
        label: { text: spot.name || spot.label, fontSize: '11px', fontWeight: '800', color: '#fff', className: 'etc-spot-label' },
      });
      marker.addListener('click', () => {
        if (placingSpotRef.current) return; // a pin is being placed on top of it
        addCustomSpotRef.current(spot);
      });
      spotMarkersRef.current.push({ marker, floor: spot.floor });
    }
  }, [mapReady, tourData?.customSpots, selectedFloor]);

  // ── Render markers: tour stops (numbered) or free-roam places (icon) ──────
  useEffect(() => {
    if (!mapReady) return;
    stopMarkersRef.current.forEach(m => m.setMap(null));
    stopMarkersRef.current = [];
    if (!tourData) return;
    const map = mapInstanceRef.current;

    if (mode === "freeroam") {
      (tourData.stopBank || []).filter(s => s.freeRoam).forEach((loc) => {
        const match = polyMetaRef.current.find(p => normalizeBuilding(p.building) === normalizeBuilding(loc.building) && p.room_number === String(loc.room));
        if (!match) return;
        const path = match.poly.getPath();
        let lat = 0, lng = 0, n = 0;
        for (let j = 0; j < path.getLength(); j++) { lat += path.getAt(j).lat(); lng += path.getAt(j).lng(); n++; }
        const pos = { lat: lat / n, lng: lng / n };
        const isActive = editingBankKey === bankKey(loc);
        const marker = new window.google.maps.Marker({
          position: pos, map, zIndex: isActive ? 700 : 600,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: isActive ? 16 : 13,
            fillColor: isActive ? "#00897b" : "#0277bd",
            fillOpacity: 1, strokeColor: "#fff", strokeWeight: 3,
          },
          label: { text: loc.icon || "📍", fontSize: "13px" },
        });
        marker.addListener("click", () => { if (!placingSpotRef.current) setEditingBankKey(bankKey(loc)); });
        stopMarkersRef.current.push(marker);
      });
      return;
    }

    if (!activeTourId) return;
    const tour = tourData.tours.find(t => t.id === activeTourId);
    if (!tour) return;
    tour.stops.forEach((stop, i) => {
      if (routeFocus && !routeFocus.has(i)) return;
      let pos = null;
      if (stop.nodeId && Number.isFinite(stop.lat) && Number.isFinite(stop.lng)) {
        pos = { lat: stop.lat, lng: stop.lng }; // a pinned spot: where the pin was dropped
      } else {
        const match = polyMetaRef.current.find(p => p.building === stop.building && p.room_number === String(stop.room));
        if (!match) return;
        const path = match.poly.getPath();
        let lat = 0, lng = 0, n = 0;
        for (let j = 0; j < path.getLength(); j++) { lat += path.getAt(j).lat(); lng += path.getAt(j).lng(); n++; }
        pos = { lat: lat / n, lng: lng / n };
      }
      const isActive = i === activeStopIdx;
      const marker = new window.google.maps.Marker({
        position: pos, map, zIndex: isActive ? 700 : 600,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: isActive ? 16 : 13,
          fillColor: isActive ? "#e65100" : "#6a1b9a",
          fillOpacity: 1, strokeColor: "#fff", strokeWeight: 3,
        },
        label: { text: String(i + 1), color: "#fff", fontSize: "12px", fontWeight: "700" },
      });
      marker.addListener("click", () => { if (!placingSpotRef.current && modeRef.current !== "routes") setActiveStopIdx(i); });
      stopMarkersRef.current.push(marker);
    });
  }, [tourData, activeTourId, activeStopIdx, mode, editingBankKey, mapReady, selectedFloor, routeFocus]);

  // Centre the map on a room polygon and switch to its floor.
  const panToMeta = useCallback((match) => {
    const path = match.poly.getPath();
    let lat = 0, lng = 0, n = 0;
    for (let j = 0; j < path.getLength(); j++) { lat += path.getAt(j).lat(); lng += path.getAt(j).lng(); n++; }
    const map = mapInstanceRef.current;
    if (n && map) { map.panTo({ lat: lat / n, lng: lng / n }); if (map.getZoom() < 20) map.setZoom(20); }
    setSelectedFloor(match.floor);
  }, []);

  // What the search box can offer: hand-placed spots first (the ones you just
  // made and can't guess the code of), then every room with its preset name.
  const searchChoices = useMemo(() => [
    ...(tourData?.customSpots || []).map(sp => ({ code: sp.label, name: sp.name || '', building: sp.building, room: sp.room, spot: sp })),
    ...roomChoices,
  ], [tourData?.customSpots, roomChoices]);

  // A suggestion picked from the search box: add it as if its polygon (or its
  // pin) had been clicked, and bring the map to it.
  const pickSearchChoice = useCallback((c) => {
    setSearchMsg(null);
    if (modeRef.current !== "freeroam" && !activeTourIdRef.current) { setSearchMsg("Pick a tour first."); return; }
    if (c.spot) {
      addCustomSpot(c.spot);
      const map = mapInstanceRef.current;
      if (map && Number.isFinite(c.spot.lat)) { map.panTo({ lat: c.spot.lat, lng: c.spot.lng }); if (map.getZoom() < 20) map.setZoom(20); }
      if (Number.isFinite(c.spot.floor)) setSelectedFloor(c.spot.floor);
    } else {
      const match = polyMetaRef.current.find(p => p.building === c.building && p.room_number === String(c.room));
      if (!match) { setSearchMsg(`Couldn't find ${c.code} on the map.`); return; }
      addRoomForMeta({ ...match, title: c.name || "" });
      panToMeta(match);
    }
    setSearch("");
  }, [addCustomSpot, addRoomForMeta, panToMeta]);

  // ── Search: "E2-213" / "E2 213" → add that room as if its polygon was clicked ──
  const handleSearch = useCallback(() => {
    setSearchMsg(null);
    if (modeRef.current !== "freeroam" && !activeTourIdRef.current) { setSearchMsg("Pick a tour first."); return; }
    // A hand-placed spot is matched by its label, before the room-code parser
    // gets a look — a label like "Atrium Stage" isn't a room code and would
    // otherwise be rejected as a malformed one.
    const typed = search.trim().toLowerCase();
    const spot = (tourDataRef.current?.customSpots || []).find(s =>
      String(s.label || '').trim().toLowerCase() === typed ||
      String(s.name || '').trim().toLowerCase() === typed);
    if (spot) {
      addCustomSpot(spot);
      const map = mapInstanceRef.current;
      if (map && Number.isFinite(spot.lat)) {
        map.panTo({ lat: spot.lat, lng: spot.lng });
        if (map.getZoom() < 20) map.setZoom(20);
      }
      if (Number.isFinite(spot.floor)) setSelectedFloor(spot.floor);
      setSearch("");
      return;
    }
    const parsed = parseRoomQuery(search);
    if (!parsed) { setSearchMsg('Use a room code like "E2-213" or "E2 213".'); return; }
    const match = polyMetaRef.current.find(
      p => p.building === parsed.building && p.room_number === String(parsed.room)
    );
    if (!match) { setSearchMsg(`Couldn't find ${formatRoomCode(parsed.building, parsed.room)} on the map.`); return; }
    addRoomForMeta(match);
    // Centre the map on it and switch to its floor so it's visible.
    const path = match.poly.getPath();
    let lat = 0, lng = 0, n = 0;
    for (let j = 0; j < path.getLength(); j++) { lat += path.getAt(j).lat(); lng += path.getAt(j).lng(); n++; }
    if (n) {
      const map = mapInstanceRef.current;
      if (map) { map.panTo({ lat: lat / n, lng: lng / n }); if (map.getZoom() < 20) map.setZoom(20); }
    }
    setSelectedFloor(match.floor);
    setSearch("");
  }, [search, addRoomForMeta, addCustomSpot]);

  // ── Mutators ─────────────────────────────────────────────────
  const updateStop = useCallback((idx, patch) => {
    setTourData(td => ({
      ...td,
      tours: td.tours.map(t => t.id !== activeTourId ? t : {
        ...t,
        stops: t.stops.map((s, i) => i !== idx ? s : { ...s, ...patch }),
      }),
    }));
  }, [activeTourId]);

  const removeStop = useCallback((idx) => {
    setTourData(td => ({
      ...td,
      tours: td.tours.map(t => t.id !== activeTourId ? t : { ...t, stops: t.stops.filter((_, i) => i !== idx) }),
    }));
    setActiveStopIdx(null);
  }, [activeTourId]);

  const moveStop = useCallback((idx, delta) => {
    setTourData(td => ({
      ...td,
      tours: td.tours.map(t => {
        if (t.id !== activeTourId) return t;
        const next = [...t.stops];
        const j = idx + delta;
        if (j < 0 || j >= next.length) return t;
        [next[idx], next[j]] = [next[j], next[idx]];
        return { ...t, stops: next };
      }),
    }));
    // Only follow the moved stop if it was the one open in the editor — never
    // open the editor just because the user clicked a reorder arrow.
    setActiveStopIdx(cur => {
      if (cur == null) return cur;
      if (cur === idx) return idx + delta;
      if (cur === idx + delta) return idx;
      return cur;
    });
  }, [activeTourId]);

  const addTour = useCallback(() => {
    const id = window.prompt("New tour ID (e.g., D):", "");
    if (!id) return;
    if (tourDataRef.current.tours.some(t => t.id === id)) { window.alert("ID already exists"); return; }
    setTourData(td => ({
      ...td,
      tours: [...td.tours, { id, title: `Discipline ${id} Tour (placeholder)`, description: "", stops: [] }],
    }));
    setActiveTourId(id);
    setActiveStopIdx(null);
  }, []);


  const updateActiveTour = useCallback((patch) => {
    setTourData(td => ({
      ...td,
      tours: td.tours.map(t => t.id !== activeTourId ? t : { ...t, ...patch }),
    }));
  }, [activeTourId]);

  const toggleDragPolyMode = useCallback(() => {
    setDragPolyMode(prev => {
      const next = !prev;
      dragPolyModeRef.current = next;
      if (!next) {
        clearDragSelectionFnRef.current?.();
        setDragAddVertexMode(false);
        dragAddVertexModeRef.current = false;
      }
      return next;
    });
  }, []);


  const downloadShapes = useCallback(() => {
    BUILDING_KEYS.forEach(({ key }) => {
      const raw = buildingDataRef.current[key];
      if (!raw) return;
      // Deep-copy so we don't mutate the cached reference
      const exportData = JSON.parse(JSON.stringify(raw));
      // Sync current polygon positions into the export copy
      polyMetaRef.current.forEach(({ poly, buildingKey, polyIdx }) => {
        if (buildingKey !== key) return;
        const path = poly.getPath();
        const coords = [];
        for (let i = 0; i < path.getLength(); i++) {
          const ll = path.getAt(i);
          coords.push({ latitude: ll.lat(), longitude: ll.lng() });
        }
        exportData.polygons[polyIdx].coordinates = coords;
      });
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${key}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
  }, []);

  const savePolyChanges = useCallback(async () => {
    if (polyEditorSaving) return;
    // Save all loaded buildings — not just the "changed" set — so that file edits
    // made before loading the page also get pushed to KV.
    const keys = BUILDING_KEYS.map(b => b.key).filter(k => buildingDataRef.current[k]);
    if (keys.length === 0) { setPolyEditorSaveState("saved"); return; }
    setPolyEditorSaving(true); setPolyEditorSaveState("idle");
    try {
      for (const bKey of keys) {
        const data = buildingDataRef.current[bKey];
        if (!data) continue;
        polyMetaRef.current.forEach(({ poly, buildingKey, polyIdx }) => {
          if (buildingKey !== bKey) return;
          const path = poly.getPath();
          const coords = [];
          for (let i = 0; i < path.getLength(); i++) {
            const ll = path.getAt(i);
            coords.push({ latitude: ll.lat(), longitude: ll.lng() });
          }
          data.polygons[polyIdx].coordinates = coords;
        });
        const res = await fetch(`${API_BASE}/api/building`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(tokenRef.current ? { Authorization: `Bearer ${tokenRef.current}` } : {}) },
          body: JSON.stringify({ key: bKey, data }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${await res.text().catch(() => '')}`);
      }
      changedBuildingKeysRef.current = new Set();
      setPolyEditorSaveState("saved");
    } catch (err) {
      console.error("Save Shapes failed:", err);
      setPolyEditorSaveState("error");
    } finally {
      setPolyEditorSaving(false);
    }
  }, [polyEditorSaving]);

  // ── Resources ──
  const addResource = useCallback(() => {
    setTourData(td => ({ ...td, resources: [...(td.resources || []), { title: 'New resource', url: 'https://' }] }));
  }, []);
  const updateResource = useCallback((idx, patch) => {
    setTourData(td => ({
      ...td,
      resources: (td.resources || []).map((r, i) => i !== idx ? r : { ...r, ...patch }),
    }));
  }, []);
  const removeResource = useCallback((idx) => {
    setTourData(td => ({ ...td, resources: (td.resources || []).filter((_, i) => i !== idx) }));
  }, []);
  const moveResource = useCallback((idx, delta) => {
    setTourData(td => {
      const list = [...(td.resources || [])];
      const j = idx + delta;
      if (j < 0 || j >= list.length) return td;
      [list[idx], list[j]] = [list[j], list[idx]];
      return { ...td, resources: list };
    });
  }, []);

  // ── Edit history ──
  const loadHistory = useCallback(async () => {
    setVersionsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/tour-admin`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(tokenRef.current ? { Authorization: `Bearer ${tokenRef.current}` } : {}) },
        body: JSON.stringify({ op: "history" }),
      });
      const data = await res.json().catch(() => ({}));
      setVersions(Array.isArray(data.versions) ? data.versions : []);
    } catch {
      setVersions([]);
    } finally {
      setVersionsLoading(false);
    }
  }, []);

  const openHistory = useCallback(() => { setHistoryOpen(true); loadHistory(); }, [loadHistory]);

  const previewVersion = useCallback(async (ts) => {
    try {
      const res = await fetch(`${API_BASE}/api/tour-admin`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(tokenRef.current ? { Authorization: `Bearer ${tokenRef.current}` } : {}) },
        body: JSON.stringify({ op: "version", ts }),
      });
      if (!res.ok) return;
      const data = await res.json();
      applyLoaded(data, { resetActive: true });
      setPreviewTs(ts);
      setHistoryOpen(false);
      setSaveState("idle"); setSaveError(null);
    } catch { /* ignore */ }
  }, [applyLoaded]);

  const exitPreview = useCallback(() => { loadCurrent().catch(() => {}); }, [loadCurrent]);

  // ── Export everything ──
  // Reads from the SERVER, not from the editor state, so the file is what is
  // live rather than what is half-edited on screen. Each history snapshot is a
  // separate request (fifty of them in one response would outgrow a serverless
  // payload), so a 429 from the per-minute limit is waited out, not treated as
  // a failure. Photos are best-effort: one that cannot be fetched is listed in
  // the manifest rather than aborting the download.
  const runExport = useCallback(async () => {
    exportCancelRef.current = false;
    const lines = [];
    const show = (patch) => setExportState((s) => ({ ...(s || {}), lines: [...lines], ...patch }));
    const say = (text) => { lines.push(text); show({}); };
    const replaceLast = (text) => { lines[lines.length - 1] = text; show({}); };
    const cancelled = () => exportCancelRef.current;
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    const authHeaders = () => (tokenRef.current ? { Authorization: `Bearer ${tokenRef.current}` } : {});
    // Admin fetch that waits out the rate limit (Retry-After, else 3 s) a few times.
    const admin = async (init) => {
      for (let attempt = 0; attempt < 6; attempt++) {
        const res = await fetch(`${API_BASE}/api/tour-admin`, { ...init, headers: { "Content-Type": "application/json", ...authHeaders(), ...(init.headers || {}) } });
        if (res.status !== 429) return res;
        const wait = Math.min(30, Math.max(1, Number(res.headers.get("Retry-After")) || 3));
        replaceLast(`${lines[lines.length - 1]} (waiting ${wait}s for the rate limit…)`);
        await sleep(wait * 1000);
        if (cancelled()) throw new Error("cancelled");
      }
      throw new Error("The server kept rate-limiting the export — try again in a minute.");
    };
    const post = (body) => admin({ method: "POST", body: JSON.stringify(body) });

    setExportState({ phase: "running", lines: [], done: false, error: null });
    try {
      say("Reading the live tour data…");
      const liveRes = await admin({ method: "GET" });
      if (liveRes.status === 401) throw new Error("Your session has expired — log in again and retry.");
      if (!liveRes.ok) throw new Error(`Could not read the live tour (HTTP ${liveRes.status}).`);
      const live = await liveRes.json();
      const tourCount = Array.isArray(live.tours) ? live.tours.length : 0;
      const stopCount = (live.tours || []).reduce((n, t) => n + (t.stops || []).length, 0);
      replaceLast(`Live tour data: ${tourCount} tour${tourCount === 1 ? "" : "s"}, ${stopCount} stop${stopCount === 1 ? "" : "s"}, ${(live.stopBank || []).length} stop-bank entries.`);
      if (cancelled()) throw new Error("cancelled");

      say("Reading the edit history…");
      const histRes = await post({ op: "history" });
      const hist = histRes.ok ? await histRes.json().catch(() => ({})) : {};
      const versions = Array.isArray(hist.versions) ? hist.versions : [];
      replaceLast(`Edit history: ${versions.length} save${versions.length === 1 ? "" : "s"} listed.`);

      const snapshots = {};
      let expired = 0;
      for (let i = 0; i < versions.length; i++) {
        if (cancelled()) throw new Error("cancelled");
        if (i === 0) say(`Fetching saved version 1 of ${versions.length}…`);
        else replaceLast(`Fetching saved version ${i + 1} of ${versions.length}…`);
        const res = await post({ op: "version", ts: versions[i].ts });
        if (res.ok) snapshots[versions[i].ts] = await res.json();
        else expired++; // past its 180-day TTL — expected on the oldest entries
      }
      if (versions.length) replaceLast(`Saved versions: ${Object.keys(snapshots).length} of ${versions.length} fetched${expired ? ` (${expired} already expired from the store)` : ""}.`);

      // Photos from the live document AND every snapshot, so a picture that was
      // swapped out last month is still in the export.
      const refs = collectImageRefs(live, ...Object.values(snapshots));
      const origin = window.location.origin;
      const images = [], failedImages = [], taken = new Set();
      if (refs.length) say(`Fetching photo 1 of ${refs.length}…`);
      for (let i = 0; i < refs.length; i++) {
        if (cancelled()) throw new Error("cancelled");
        replaceLast(`Fetching photo ${i + 1} of ${refs.length}…`);
        const ref = refs[i];
        try {
          const res = await fetch(resolveImageUrl(ref, origin), { mode: "cors" });
          if (!res.ok) { failedImages.push({ ref, reason: `HTTP ${res.status}` }); continue; }
          const bytes = new Uint8Array(await res.arrayBuffer());
          images.push({ ref, name: imageFileName(ref, taken), bytes });
        } catch (e) {
          failedImages.push({ ref, reason: e?.message || "could not fetch" });
        }
      }
      if (refs.length) replaceLast(`Photos: ${images.length} of ${refs.length} saved${failedImages.length ? ` (${failedImages.length} could not be fetched — listed in images/manifest.json)` : ""}.`);

      say("Building the zip…");
      const takenAt = Date.now();
      const files = buildExportFiles({ live, versions, snapshots, images, failedImages, takenAt, origin });
      const zip = buildZip(files, { date: new Date(takenAt) });
      const name = `engtour-export-${new Date(takenAt).toISOString().slice(0, 10)}.zip`;
      const url = URL.createObjectURL(new Blob([zip], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      replaceLast(`Downloaded ${name} (${(zip.length / (1024 * 1024)).toFixed(1)} MB).`);
      show({ phase: "done", done: true });
    } catch (e) {
      if (e?.message === "cancelled") { setExportState(null); return; }
      show({ phase: "error", error: e?.message || "Export failed." });
    }
  }, []);

  const cancelExport = useCallback(() => { exportCancelRef.current = true; setExportState(null); }, []);

  // Put chosen stops' CONTENT back from a backup. Where the stop sits (room,
  // label, pin), its What's Next and its Free Roam flag are today's; only what
  // the backup had written on the card comes back. Nothing is published until Save.
  const applyRestore = useCallback((picks) => {
    const td = tourDataRef.current;
    if (!td || !picks.length) return null;
    const { data, report } = restoreStops(td, picks);
    setTourData(data);
    const n = report.length;
    const touched = report.reduce((a, r) => a + r.tourStops + r.bankEntries, 0);
    const added = report.filter(r => r.added).length;
    setRestoreNote(`Restored ${n} stop${n === 1 ? "" : "s"} (${touched} place${touched === 1 ? "" : "s"} updated${added ? `, ${added} added to the bank` : ""}). Press Save to publish.`);
    setSaveState("idle");
    return report;
  }, []);

  // ── Persist ──
  const persist = useCallback(async (base) => {
    if (saving || !(base || tourDataRef.current)) return;
    setSaving(true); setSaveState("idle"); setSaveError(null);
    try {
      // Refresh the reusable stop bank from the current stops before persisting,
      // and keep it in local state so the "Add from bank" picker stays current.
      const src = base || tourDataRef.current;
      // Hand-drawn routes are keyed by their two ends; one whose pair is no
      // longer consecutive in its tour is dropped rather than kept as a ghost.
      const data = { ...src, stopBank: buildStopBank(src), tours: (src.tours || []).map(pruneRoutes) };
      setTourData(data);
      const res = await fetch(`${API_BASE}/api/tour-admin`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(tokenRef.current ? { Authorization: `Bearer ${tokenRef.current}` } : {}) },
        body: JSON.stringify({ op: "save", data }),
      });
      if (res.ok) { setSaveState("saved"); setPreviewTs(null); loadHistory(); return; }
      if (res.status === 401) { // token expired — re-authenticate
        sessionStorage.removeItem("engtourcreator_token"); tokenRef.current = null; setIsAdmin(false);
        setSaveError("Session expired — log in again."); setSaveState("error"); setAuthed(false); return;
      }
      // Surface the server's reason (e.g. "cloud store not configured") or status.
      let msg = `HTTP ${res.status}`;
      try { const j = await res.json(); if (j?.error) msg = j.error; } catch { /* non-JSON (e.g. 404) */ }
      setSaveError(msg); setSaveState("error");
    } catch (e) {
      setSaveError(e?.message || "Network error — is the dev server running?");
      setSaveState("error");
    } finally {
      setSaving(false);
    }
  }, [saving, loadHistory]);

  // Persist `base` (or current state), gated by the preview-override prompt when
  // viewing an old version.
  const pendingSaveRef = useRef(null);
  const requestSave = useCallback((base) => {
    if (previewTs != null) { pendingSaveRef.current = base || null; setOverrideInput(""); setOverrideOpen(true); return; }
    persist(base);
  }, [previewTs, persist]);

  const confirmOverride = useCallback(() => {
    if (overrideInput.trim().toUpperCase() !== "CONTINUE") return;
    setOverrideOpen(false); setOverrideInput("");
    persist(pendingSaveRef.current);
    pendingSaveRef.current = null;
  }, [overrideInput, persist]);

  // Persist the whole editor state as-is (used by the top-bar Save button).
  const handleSave = useCallback(() => requestSave(null), [requestSave]);

  // Single "Save" for a stop/location: locations are shared centrally, so the
  // edited content (everything but the tour-specific What's Next) is copied onto
  // every stop with the same room code across all tours, then persisted.
  const saveStopForAllTours = useCallback((stop) => {
    const td = tourDataRef.current;
    if (!td || !stop) return;
    const key = bankKey(stop);
    const { guidance, ...shared } = stop; // eslint-disable-line no-unused-vars
    const tours = (td.tours || []).map(t => ({
      ...t,
      stops: (t.stops || []).map(s => bankKey(s) === key ? { ...stripBankFlags(shared), guidance: s.guidance ?? "" } : s),
    }));
    // The star and primary flag stay on the bank entry (the bank editor sets
    // them there live); the stops get the content only — see engtourBank.js.
    const next = { ...td, tours };
    setTourData(next);
    requestSave(next);
  }, [requestSave]);

  const activeTour = tourData?.tours.find(t => t.id === activeTourId) || null;
  const activeStop = activeTour && activeStopIdx != null ? activeTour.stops[activeStopIdx] : null;

  // Does the tour have somewhere to walk to for this stop? A pinned spot
  // resolves through its nav node; a room stop needs a polygon with that
  // number in that building — "Engineering Library" in the room field has
  // none, which is why such a stop drew no marker and routed nowhere.
  const stopResolves = (s) => {
    if (!s) return true;
    if (s.nodeId) return !navNodesReady || navNodesRef.current.some(n => n.id === s.nodeId);
    if (!mapReady) return true; // can't tell yet; don't flag everything while loading
    return polyMetaRef.current.some(p => normalizeBuilding(p.building) === normalizeBuilding(s.building) && p.room_number === String(s.room));
  };

  // ── Route Adjuster: what each leg of the active tour walks today ───────────
  // Mirrors the viewer's leg builder exactly, client-side. The tour starts AT
  // its first stop, so the first leg leaves from any of that stop's doors; every
  // later leg leaves from the door the previous leg arrived at. A leg with a
  // valid drawing follows it, auto-connected from the arrival door if the
  // drawing starts elsewhere; anything else is the server-model automatic
  // path. Recomputed when the tour, the mode or the graph changes. One entry
  // per leg (none for stop 0), each carrying the stop index it arrives at as
  // `idx` — look legs up with `legOf(legInfo, idx)`, not by array position.
  const legInfo = useMemo(() => {
    if (!navGraph || !activeTour) return null;
    const { nodeById, adj, roomDoors } = navGraph;
    const doorsFor = (stop) => {
      if (!stop) return [];
      if (stop.nodeId) return nodeById.has(stop.nodeId) ? [stop.nodeId] : [];
      return roomDoors.get(`${normalizeBuilding(stop.building)}::${String(stop.room ?? "").toLowerCase()}`) || [];
    };
    let anchor = null;
    return legsOf(activeTour).map((leg) => {
      const i = leg.idx;
      const fromIds = anchor || doorsFor(leg.fromStop);
      const destIds = doorsFor(leg.toStop);
      const drawn = storedRoute(activeTour, i, routeMode);
      const check = drawn ? validateRoute(drawn, nodeById, destIds) : null;
      let info;
      if (drawn && check.ok) {
        const ids = drawn.nodeIds;
        let connector = null, startMoved = false;
        if (!fromIds.includes(ids[0])) {
          startMoved = true;
          const c = bestAutoPath(nodeById, adj, fromIds, [ids[0]], routeMode);
          connector = c ? c.ids.slice(0, -1) : null;
        }
        info = { kind: "manual", ids, connector, startMoved, entry: drawn };
        anchor = [ids[ids.length - 1]];
      } else {
        const auto = destIds.length ? bestAutoPath(nodeById, adj, fromIds, destIds, routeMode) : null;
        info = { kind: "auto", ids: auto ? auto.ids : [], failed: !auto, invalid: drawn ? check.reason : null };
        anchor = auto ? [auto.ids[auto.ids.length - 1]] : (destIds.length ? destIds : fromIds);
      }
      const floors = [...new Set(info.ids.map(id => nodeById.get(id)?.floor).filter(f => Number.isFinite(f) && f > 0))];
      return { idx: i, leg, fromIds, destIds, floors, ...info };
    });
  }, [navGraph, activeTour, routeMode]);

  const routeNodeFloor = useCallback((id) => navGraph?.nodeById.get(id)?.floor, [navGraph]);

  // Show a leg: switch to the floor it starts on and bring the map there.
  const selectRouteLeg = useCallback((idx) => {
    setRouteLeg(idx);
    const info = legOf(legInfo, idx);
    const first = info?.connector?.[0] ?? info?.ids?.[0] ?? info?.fromIds?.[0];
    const n = first ? navGraph?.nodeById.get(first) : null;
    if (n) {
      if (Number.isFinite(n.floor) && n.floor > 0) setSelectedFloor(n.floor);
      const map = mapInstanceRef.current;
      if (map) { map.panTo({ lat: n.lat, lng: n.lng }); if (map.getZoom() < 19) map.setZoom(19); }
    }
  }, [legInfo, navGraph]);

  // Start drawing a leg by hand, from where the visitor actually arrives.
  const beginRouteDraw = useCallback((idx) => {
    const info = legOf(legInfo, idx);
    if (!info || !navGraph) return;
    const startId = info.fromIds[0];
    selectRouteLeg(idx);
    setRouteDraw({ idx, mode: routeMode, ids: [startId], marks: [1], shaft: null });
    setHoverPath(null);
  }, [legInfo, navGraph, routeMode, selectRouteLeg]);

  const cancelRouteDraw = useCallback(() => { setRouteDraw(null); setHoverPath(null); }, []);

  const undoRouteDraw = useCallback(() => {
    setRouteDraw(d => {
      if (!d || d.marks.length < 2) return d;
      const marks = d.marks.slice(0, -1);
      const ids = d.ids.slice(0, marks[marks.length - 1]);
      const f = routeNodeFloor(ids[ids.length - 1]);
      if (Number.isFinite(f) && f > 0) setSelectedFloor(f);
      return { ...d, ids, marks, shaft: null };
    });
    setHoverPath(null);
  }, [routeNodeFloor]);

  // A stair/elevator was clicked: ride it to the chosen floor (every shaft node
  // on the way is appended, since shafts link one floor per edge) — or stay.
  const chooseShaftFloor = useCallback((opt) => {
    setRouteDraw(d => {
      if (!d || !d.shaft) return d;
      if (!opt) return { ...d, shaft: null };
      const ids = [...d.ids, ...opt.path];
      return { ...d, ids, marks: [...d.marks, ids.length], shaft: null };
    });
    if (opt && Number.isFinite(opt.floor) && opt.floor > 0) setSelectedFloor(opt.floor);
    setHoverPath(null);
  }, []);

  const routeDrawDone = !!(routeDraw && legOf(legInfo, routeDraw.idx)?.destIds.includes(routeDraw.ids[routeDraw.ids.length - 1]));

  const finishRouteDraw = useCallback(() => {
    const d = routeDrawRef.current;
    if (!d || !routeDrawDone) return;
    const tid = activeTourIdRef.current;
    setTourData(td => ({
      ...td,
      tours: td.tours.map(t => t.id !== tid ? t : withRoute(t, d.idx, d.mode, { nodeIds: d.ids, ...(editorName ? { by: editorName } : {}) })),
    }));
    setRouteDraw(null); setHoverPath(null);
  }, [routeDrawDone, editorName]);

  const resetRouteToAuto = useCallback((idx, modeId) => {
    const tid = activeTourIdRef.current;
    setTourData(td => ({ ...td, tours: td.tours.map(t => t.id !== tid ? t : withRoute(t, idx, modeId, null)) }));
  }, []);

  // Hover: the nearest node on the floor being looked at (or outdoors) —
  // wherever the cursor is, not only when it sits on the network, so there is
  // always a preview to read — and the shortest path to it from the last
  // click, kept on this floor, so a click never silently rides a staircase.
  // Nodes are hit-tested in world pixels, precomputed once per graph.
  useEffect(() => {
    routeHoverRef.current = (latLng) => {
      const d = routeDrawRef.current;
      const map = mapInstanceRef.current;
      if (!d || d.shaft || !navGraph || !map) return;
      const proj = map.getProjection();
      if (!proj) return;
      if (worldPtsRef.current.graph !== navGraph) {
        const pts = new Map();
        for (const n of navGraph.nodes) { const q = proj.fromLatLngToPoint(new window.google.maps.LatLng(n.lat, n.lng)); pts.set(n.id, { x: q.x, y: q.y }); }
        worldPtsRef.current = { graph: navGraph, pts };
      }
      const pts = worldPtsRef.current.pts;
      const scale = 2 ** map.getZoom();
      const p = proj.fromLatLngToPoint(latLng);
      const floor = selectedFloorRef.current;
      const onView = (n) => n.floor === floor || n.floor === 0 || n.floor == null;
      let best = null, bestD = Infinity;
      const doorsOkHit = routeDoorsAllowedRef.current;
      for (const n of navGraph.nodes) {
        if (!onView(n) || (n.type === "door" && doorsOkHit && !doorsOkHit.has(n.id))) continue;
        const q = pts.get(n.id);
        const dist = Math.hypot((q.x - p.x) * scale, (q.y - p.y) * scale);
        if (dist < bestD) { bestD = dist; best = n; }
      }
      const prev = hoverPathRef.current;
      if (!best) { if (prev) setHoverPath(null); return; }
      if (prev && prev.targetId === best.id) return;
      const from = d.ids[d.ids.length - 1];
      const doorsOk = routeDoorsAllowedRef.current;
      const allowNode = (n) => onView(n) && (n.type !== "door" || !doorsOk || doorsOk.has(n.id));
      const ids = shortestPath(navGraph.nodeById, navGraph.adj, from, best.id, { mode: d.mode, allowNode });
      setHoverPath(ids ? { targetId: best.id, ids } : { targetId: best.id, ids: null });
    };
    routeClickRef.current = () => {
      const d = routeDrawRef.current, hp = hoverPathRef.current;
      if (!d || d.shaft || !hp?.ids || hp.ids.length < 2 || !navGraph) return;
      const target = navGraph.nodeById.get(hp.targetId);
      const ids = [...d.ids, ...hp.ids.slice(1)];
      const next = { ...d, ids, marks: [...d.marks, ids.length], shaft: null };
      if (target && (target.type === "stair" || target.type === "elevator")) {
        // In accessible mode the hover path can never reach a stair (edgeAllowed), so this is an elevator or a normal-mode stair.
        const options = shaftFloors(target.id, navGraph.nodeById, navGraph.adj).filter(o => Number.isFinite(o.floor) && o.floor > 0);
        if (options.length) next.shaft = { nodeId: target.id, type: target.type, options };
      }
      setRouteDraw(next);
      setHoverPath(null);
    };
  }, [navGraph]);

  // Leaving routes mode, or switching tour, ends any drawing.
  useEffect(() => { if (mode !== "routes") { setRouteDraw(null); setHoverPath(null); setRouteLeg(null); } }, [mode]);
  useEffect(() => { setRouteDraw(null); setHoverPath(null); setRouteLeg(null); }, [activeTourId]);

  // Polygons swallow the map's own mousemove/click, so they go inert while a
  // path is being drawn (the same trick Create Spot uses) and come back after.
  useEffect(() => {
    if (!mapReady) return undefined;
    const on = mode === "routes";
    const polys = polyMetaRef.current;
    polys.forEach(m => m.poly.setOptions({ clickable: !on }));
    const map = mapInstanceRef.current;
    map?.setOptions({ draggableCursor: routeDraw ? "crosshair" : null });
    return () => { polys.forEach(m => m.poly.setOptions({ clickable: true })); map?.setOptions({ draggableCursor: null }); };
  }, [mode, mapReady, routeDraw]);

  // Escape cancels a drawing (or a pending floor choice).
  useEffect(() => {
    if (!routeDraw) return undefined;
    const onKey = (e) => { if (e.key === "Escape") { if (routeDrawRef.current?.shaft) chooseShaftFloor(null); else cancelRouteDraw(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [routeDraw, cancelRouteDraw, chooseShaftFloor]);

  // ── Map layer 1: the edge network for the floor on screen ──────────────────
  // Only edges, never nodes — except the stairs and elevators, which get a dot
  // because they are the thing you click to change floors. Outdoor edges are
  // drawn dashed on every floor, since a leg between buildings goes outside.
  // Doors the current leg may touch: the start door(s), every door of the
  // destination, and the start stop's own doors. Every other door's stub edge
  // is hidden — a hundred short spurs into rooms you are not going to only
  // hide the corridor — and the hover path may not pass through them either.
  const routeDoorsAllowed = useMemo(() => {
    const info = routeDraw ? legOf(legInfo, routeDraw.idx) : (routeLeg != null ? legOf(legInfo, routeLeg) : null);
    if (!info || !navGraph) return null;
    const set = new Set([...info.fromIds, ...info.destIds]);
    const from = info.leg.fromStop;
    if (from && !from.nodeId) for (const id of navGraph.roomDoors.get(`${normalizeBuilding(from.building)}::${String(from.room ?? "").toLowerCase()}`) || []) set.add(id);
    return set;
  }, [legInfo, navGraph, routeDraw, routeLeg]);
  const routeDoorsAllowedRef = useRef(null);
  useEffect(() => { routeDoorsAllowedRef.current = routeDoorsAllowed; }, [routeDoorsAllowed]);

  useEffect(() => {
    routeEdgeOverlaysRef.current.forEach(o => o.setMap(null));
    routeEdgeOverlaysRef.current = [];
    if (!mapReady || mode !== "routes" || !navGraph) return;
    const map = mapInstanceRef.current;
    const { nodeById, edges, nodes } = navGraph;
    const G = window.google.maps;
    const isOut = (n) => n.floor === 0 || n.floor == null;
    // The corridor network is the pen's paper: shown only while drawing. When
    // merely looking at a leg, the path alone is the point.
    if (routeDraw) {
      const doorOk = (n) => n.type !== "door" || !!routeDoorsAllowed?.has(n.id);
      for (const e of edges) {
        const a = nodeById.get(e.from), b = nodeById.get(e.to);
        if (!a || !b || !doorOk(a) || !doorOk(b)) continue;
        const aHere = a.floor === selectedFloor, bHere = b.floor === selectedFloor;
        let opts = null;
        if (aHere && bHere) opts = { strokeColor: "#5b6470", strokeOpacity: 0.7, strokeWeight: 2 };
        else if ((aHere && isOut(b)) || (bHere && isOut(a)) || (isOut(a) && isOut(b))) opts = { strokeColor: "#4fa3d8", strokeOpacity: 0, strokeWeight: 2, icons: [{ icon: { path: "M 0,-1 0,1", strokeOpacity: 0.8, strokeColor: "#4fa3d8", scale: 2 }, offset: "0", repeat: "8px" }] };
        if (!opts) continue;
        routeEdgeOverlaysRef.current.push(new G.Polyline({ map, path: [{ lat: a.lat, lng: a.lng }, { lat: b.lat, lng: b.lng }], clickable: false, zIndex: 30, ...opts }));
      }
    }
    // Stairs and elevators are drawn as what they are, always: they are the
    // thing you click to change floors, and a path's floor change reads
    // against them.
    for (const n of nodes) {
      if (n.floor !== selectedFloor || (n.type !== "stair" && n.type !== "elevator")) continue;
      routeEdgeOverlaysRef.current.push(new G.Marker({
        map, position: { lat: n.lat, lng: n.lng }, clickable: false, zIndex: 35,
        icon: { url: n.type === "stair" ? STAIR_ICON : ELEVATOR_ICON, scaledSize: new G.Size(22, 22), anchor: new G.Point(11, 11) },
        title: n.type === "stair" ? "Stairs" : "Elevator",
      }));
    }
  }, [mapReady, mode, navGraph, selectedFloor, routeDraw, routeDoorsAllowed]);

  // ── Map layer 2: the leg on screen — its path, the hover preview, markers ──
  useEffect(() => {
    routePathOverlaysRef.current.forEach(o => o.setMap(null));
    routePathOverlaysRef.current = [];
    if (!mapReady || mode !== "routes" || !navGraph || !legInfo) return;
    const map = mapInstanceRef.current;
    const G = window.google.maps;
    const { nodeById } = navGraph;
    const info = routeDraw ? legOf(legInfo, routeDraw.idx) : (routeLeg != null ? legOf(legInfo, routeLeg) : null);
    if (!info) return;
    const color = ROUTE_MODES[routeDraw ? routeDraw.mode : routeMode].color;
    const isOut = (n) => n.floor === 0 || n.floor == null;
    const push = (o) => routePathOverlaysRef.current.push(o);
    // Draw an id list as polylines, one per run of nodes on this floor (or outdoors, dimmer).
    const arrowLines = [];
    const drawIds = (ids, { dashed = false, weight = 6, opacity = 0.95 } = {}) => {
      let run = [], runOut = null;
      const flush = () => {
        if (run.length >= 2) {
          const line = new G.Polyline({
            map, path: run, clickable: false, zIndex: dashed ? 41 : 40,
            strokeColor: color, strokeWeight: dashed ? 0 : weight, strokeOpacity: dashed ? 0 : (runOut ? opacity * 0.5 : opacity),
            icons: dashed
              ? [{ icon: { path: "M 0,-1 0,1", strokeOpacity: runOut ? 0.5 : 0.9, strokeColor: color, scale: 3 }, offset: "0", repeat: "12px" }]
              // Direction arrows that travel along the path, as on the live tour.
              : [{ icon: { path: G.SymbolPath.FORWARD_CLOSED_ARROW, scale: 2.6, strokeColor: "#fff", strokeWeight: 1.2, fillColor: "#fff", fillOpacity: 1 }, offset: "0%", repeat: "48px" }],
          });
          if (!dashed) arrowLines.push(line);
          push(line);
        }
        run = []; runOut = null;
      };
      for (let i = 0; i < ids.length; i++) {
        const n = nodeById.get(ids[i]);
        if (!n) { flush(); continue; }
        const visible = n.floor === selectedFloor || isOut(n);
        if (!visible) { flush(); continue; }
        const out = isOut(n);
        if (run.length && runOut !== out) { const last = run[run.length - 1]; flush(); run.push(last); }
        runOut = out;
        run.push({ lat: n.lat, lng: n.lng });
        // A floor change: mark the node on this floor with where the path goes.
        const nx = nodeById.get(ids[i + 1]), pv = nodeById.get(ids[i - 1]);
        const other = nx && !isOut(nx) && nx.floor !== n.floor ? nx : (pv && !isOut(pv) && pv.floor !== n.floor ? pv : null);
        if (other && !out) {
          const up = other.floor > n.floor;
          push(new G.Marker({ map, position: { lat: n.lat, lng: n.lng }, clickable: false, zIndex: 45,
            icon: { path: G.SymbolPath.CIRCLE, scale: 11, fillColor: "#fff", fillOpacity: 1, strokeColor: color, strokeWeight: 3 },
            label: { text: `${up ? "↑" : "↓"}${other.floor}`, color, fontSize: "11px", fontWeight: "800" } }));
        }
      }
      flush();
    };
    if (routeDraw) {
      drawIds(routeDraw.ids);
      if (hoverPath?.ids) drawIds(hoverPath.ids, { dashed: true });
    } else {
      if (info.connector) drawIds(info.connector, { dashed: true });
      drawIds(info.ids);
    }
    // Where it starts, where it must end, and (while drawing) where you are.
    const dot = (id, fill, text, z) => {
      const n = nodeById.get(id);
      if (!n || !(n.floor === selectedFloor || isOut(n))) return;
      push(new G.Marker({ map, position: { lat: n.lat, lng: n.lng }, clickable: false, zIndex: z,
        icon: { path: G.SymbolPath.CIRCLE, scale: 9, fillColor: fill, fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2.5 },
        label: text ? { text, color: "#fff", fontSize: "10px", fontWeight: "800" } : undefined }));
    };
    const startId = routeDraw ? routeDraw.ids[0] : (info.connector?.[0] ?? info.ids[0] ?? info.fromIds[0]);
    dot(startId, "#2e7d32", "S", 50);
    for (const id of info.destIds) dot(id, UM.gold, "", 49);
    if (routeDraw) dot(routeDraw.ids[routeDraw.ids.length - 1], color, "", 51);
    if (hoverPath && !hoverPath.ids) dot(hoverPath.targetId, "#c62828", "✕", 52); // no way there on this floor
    // A stair or elevator under the cursor lights up: it is the thing you
    // click to change floors, so it must be plain that it has been picked up.
    const hovered = hoverPath ? nodeById.get(hoverPath.targetId) : null;
    if (hovered && (hovered.type === "stair" || hovered.type === "elevator") && hovered.floor === selectedFloor) {
      const position = { lat: hovered.lat, lng: hovered.lng };
      push(new G.Marker({ map, position, clickable: false, zIndex: 53,
        icon: { path: G.SymbolPath.CIRCLE, scale: 19, fillColor: color, fillOpacity: 0.28, strokeColor: color, strokeWeight: 3 } }));
      push(new G.Marker({ map, position, clickable: false, zIndex: 54,
        icon: { url: hovered.type === "stair" ? STAIR_ICON : ELEVATOR_ICON, scaledSize: new G.Size(30, 30), anchor: new G.Point(15, 15) } }));
    }
    // Move the arrows: one timer for every polyline on screen, cleared with them.
    if (arrowLines.length) {
      let step = 0;
      const timer = setInterval(() => {
        step = (step + 1) % 48;
        for (const line of arrowLines) {
          const icons = line.get("icons");
          if (!icons) continue;
          icons[0].offset = `${step}px`;
          line.set("icons", icons);
        }
      }, 60);
      routePathOverlaysRef.current.push({ setMap: () => clearInterval(timer) });
    }
  }, [mapReady, mode, navGraph, legInfo, routeLeg, routeMode, routeDraw, hoverPath, selectedFloor]);

  // The patch that moves a stop to another room: building + room (what the
  // viewer routes by), the label derived from them, any pin dropped (a room is
  // routed by its door, not a borrowed node), and a blank title filled with the
  // room's preset name. Everything else the stop carries is left alone.
  const roomAssignment = (s, c) => (c.spot
    ? {
      // A pinned spot: the tour walks to its nav node; building/room/floor/lat/lng
      // ride along exactly as insertStop stores them for a spot.
      building: c.spot.building, room: c.spot.room ?? null, label: c.spot.label,
      nodeId: c.spot.nodeId, lat: c.spot.lat, lng: c.spot.lng, floor: c.spot.floor,
      fallbackBuilding: undefined, fallbackRoom: undefined,
      ...(String(s?.title || "").trim() ? {} : { title: c.spot.name || "" }),
    }
    : {
      building: c.building, room: String(c.room), label: c.code,
      nodeId: undefined, lat: undefined, lng: undefined, floor: undefined,
      fallbackBuilding: undefined, fallbackRoom: undefined,
      ...(String(s?.title || "").trim() ? {} : { title: c.name || "" }),
    });

  // What the editor's Room picker offers: every room, and every pinned spot.
  const assignChoices = useMemo(() => [
    ...roomChoices,
    ...(tourData?.customSpots || []).map(sp => ({ code: sp.label, name: `${sp.name || ""}${sp.name ? " · " : ""}pinned spot`, building: sp.building, room: sp.room, spot: sp })),
  ], [roomChoices, tourData?.customSpots]);

  // ── Password gate ──
  if (!authed) {
    return (
      <div className="etc-root" style={{ position: "fixed", inset: 0, background: UM.navy, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: SANS, padding: 20 }}>
        <style>{BTN_CSS}</style>
        <div style={{ background: UM.cream, borderRadius: 22, overflow: "hidden", maxWidth: 400, width: "100%", boxShadow: "0 18px 60px rgba(0,0,0,0.45)" }}>
          <div style={{ background: "#0d2137", padding: "20px 24px", display: "flex", alignItems: "center", gap: 10 }}>
            <UMapsWordmark tool="Eng Tour Creator" size={24} href={null} style={{ fontSize: 17, color: "#fff" }} />
          </div>
          <div style={{ padding: "26px 28px 30px" }}>
            <div style={{ fontFamily: SERIF, fontSize: 24, fontWeight: 800, color: UM.brown, lineHeight: 1.15 }}>Admin access</div>
            <div style={{ fontSize: 13, color: "#7a6a58", marginTop: 6, marginBottom: 22 }}>Sign in with your editor account to edit tours.</div>
            <input autoFocus type="text" autoComplete="username" value={userInput} disabled={pwLoading || pwRateLimited}
              onChange={e => { setUserInput(e.target.value); setPwError(false); setPwRateLimited(false); }}
              onKeyDown={e => { if (e.key === 'Enter') handlePasswordSubmit(); }}
              placeholder="Username"
              style={{ width: "100%", fontSize: 15, padding: "12px 14px", borderRadius: 11, border: `2px solid ${pwError ? "#c0392b" : UM.line}`, outline: "none", boxSizing: "border-box", marginBottom: 10, background: "#fff", opacity: (pwLoading || pwRateLimited) ? 0.6 : 1 }} />
            <input type="password" autoComplete="current-password" value={pwInput} disabled={pwLoading || pwRateLimited}
              onChange={e => { setPwInput(e.target.value); setPwError(false); setPwRateLimited(false); }}
              onKeyDown={e => { if (e.key === 'Enter') handlePasswordSubmit(); }}
              placeholder="Password"
              style={{ width: "100%", fontSize: 15, padding: "12px 14px", borderRadius: 11, border: `2px solid ${pwError ? "#c0392b" : UM.line}`, outline: "none", boxSizing: "border-box", marginBottom: (pwError || pwRateLimited) ? 8 : 16, background: "#fff", opacity: (pwLoading || pwRateLimited) ? 0.6 : 1 }} />
            {pwError && <div style={{ fontSize: 12, color: "#c0392b", marginBottom: 12 }}>Invalid username or password.</div>}
            {pwRateLimited && <div style={{ fontSize: 12, color: "#b45309", marginBottom: 12 }}>Too many tries — give it a minute.</div>}
            <button onClick={handlePasswordSubmit} disabled={pwLoading || pwRateLimited}
              style={{ width: "100%", fontSize: 15, fontWeight: 800, color: UM.brown, background: UM.gold, border: "none", borderRadius: 11, padding: "13px 0", cursor: (pwLoading || pwRateLimited) ? "not-allowed" : "pointer", opacity: (pwLoading || pwRateLimited) ? 0.7 : 1 }}>
              {pwLoading ? "Checking…" : "Unlock →"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="etc-root" style={{ display: 'flex', flexDirection: 'column', height: '100vh', fontFamily: SANS, overflow: 'hidden' }}>
      <style>{BTN_CSS}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 18px', height: 52, background: '#0d2137', color: '#fff', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span onClick={() => setTitleClickCount(c => c + 1)}
                style={{ cursor: 'default', userSelect: 'none', display: 'flex' }}>
            <UMapsWordmark tool="Eng Tour Creator" size={24} href={null} style={{ fontSize: 16 }} />
          </span>
          <span style={{ fontSize: 11, color: '#8aa' }}>myumaps.ca/engtourcreator</span>
          {editorName && (
            <span style={{ fontSize: 11, fontWeight: 700, color: '#7BD389', background: 'rgba(255,255,255,0.08)', padding: '3px 10px', borderRadius: 999 }}>
              ✎ {editorName}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {saveState === "saved" && !dragPolyMode && <span style={{ fontSize: 11, color: '#7BD389', fontWeight: 700 }}>✓ Saved</span>}
          {saveState === "error" && !dragPolyMode && <span style={{ fontSize: 11, color: '#ffab91', fontWeight: 700, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={saveError || 'Save failed'}>⚠ {saveError || 'Save failed'}</span>}
          {dragPolyMode && polyEditorSaveState === "saved" && <span style={{ fontSize: 11, color: '#7BD389', fontWeight: 700 }}>✓ Shapes saved</span>}
          {dragPolyMode && polyEditorSaveState === "error" && <span style={{ fontSize: 11, color: '#ffab91', fontWeight: 700 }}>⚠ Shape save failed</span>}
          {titleClickCount >= 10 && (
            <button onClick={toggleDragPolyMode} style={{ fontSize: 12, fontWeight: 700, padding: '6px 14px', borderRadius: 6, border: `1px solid ${dragPolyMode ? '#00bcd4' : 'rgba(255,255,255,0.25)'}`, background: dragPolyMode ? '#00bcd4' : 'transparent', color: '#fff', cursor: 'pointer' }}>
              ✥ Drag Vertices
            </button>
          )}
          {dragPolyMode && (
            <>
              <button onClick={savePolyChanges} disabled={polyEditorSaving} style={{ fontSize: 12, fontWeight: 800, padding: '6px 16px', borderRadius: 6, border: 'none', background: polyEditorSaveState === 'saved' ? '#2E7D32' : polyEditorSaveState === 'error' ? '#c62828' : '#e65100', color: '#fff', cursor: polyEditorSaving ? 'default' : 'pointer', opacity: polyEditorSaving ? 0.6 : 1 }}>
                {polyEditorSaving ? 'Saving…' : polyEditorSaveState === 'saved' ? '✓ Saved' : polyEditorSaveState === 'error' ? '✗ Error' : '💾 Save Shapes'}
              </button>
              <button onClick={downloadShapes} style={{ fontSize: 12, fontWeight: 700, padding: '6px 14px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.25)', background: 'transparent', color: '#fff', cursor: 'pointer' }}>
                ⬇ Download JSONs
              </button>
            </>
          )}
          {tourData && (
            <button onClick={() => setIntroOpen(true)} title="Edit the welcome, room-numbers and completion screens every visitor sees" style={{ fontSize: 12, fontWeight: 700, padding: '6px 14px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.25)', background: introOpen ? 'rgba(255,255,255,0.15)' : 'transparent', color: '#fff', cursor: 'pointer' }}>
              ✎ Edit Intro &amp; Closing
            </button>
          )}
          <button onClick={openHistory} style={{ fontSize: 12, fontWeight: 700, padding: '6px 14px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.25)', background: historyOpen ? 'rgba(255,255,255,0.15)' : 'transparent', color: '#fff', cursor: 'pointer' }}>
            History
          </button>
          {isAdmin && (
          <button onClick={runExport} disabled={exportState?.phase === "running"} title="Download everything — live data, every saved version and every photo — as one zip" style={{ fontSize: 12, fontWeight: 700, padding: '6px 14px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.25)', background: exportState ? 'rgba(255,255,255,0.15)' : 'transparent', color: '#fff', cursor: exportState?.phase === "running" ? 'default' : 'pointer' }}>
            ⬇ Export
          </button>
          )}
          <button onClick={() => setGuideOpen(true)} style={{ fontSize: 12, fontWeight: 700, padding: '6px 14px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.25)', background: 'transparent', color: '#fff', cursor: 'pointer' }}>
            ? Guide
          </button>
          {!dragPolyMode && (
            <button onClick={handleSave} disabled={saving} style={{ fontSize: 12, fontWeight: 800, padding: '6px 16px', borderRadius: 6, border: 'none', background: UM.gold, color: UM.brown, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1 }}>
              {saving ? "Saving…" : "💾 Save"}
            </button>
          )}
        </div>
      </div>

      {/* Drag vertices mode banner */}
      {dragPolyMode && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 18px', background: '#006064', color: '#fff', flexShrink: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 800 }}>✥ Drag Vertices</span>
          {dragHasSelection && (
            <button
              onClick={() => { const n = !dragAddVertexMode; setDragAddVertexMode(n); dragAddVertexModeRef.current = n; }}
              style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 5, border: `1px solid ${dragAddVertexMode ? '#fff' : 'rgba(255,255,255,0.45)'}`, background: dragAddVertexMode ? 'rgba(255,255,255,0.18)' : 'transparent', color: '#fff', cursor: 'pointer' }}>
              ➕ {dragAddVertexMode ? 'Adding Vertices' : 'Add Vertex'}
            </button>
          )}
          <span style={{ fontSize: 12, opacity: 0.85 }}>
            {dragAddVertexMode
              ? 'Click the polygon to insert a vertex at the nearest edge point.'
              : 'Click polygon to select · drag cyan dots to reposition · click empty map to deselect.'}
          </span>
          <button onClick={toggleDragPolyMode} style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 800, padding: '5px 12px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.5)', background: 'transparent', color: '#fff', cursor: 'pointer' }}>
            Exit
          </button>
        </div>
      )}


      {/* Stale shape overrides — the map on screen is not the campus map */}
      {shapeOverrides.length > 0 && !dragPolyMode && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 18px', background: '#fff3cd', color: '#5c4400', borderBottom: '1px solid #f1dc9a', flexShrink: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 800 }}>⚠ Older room shapes</span>
          <span style={{ fontSize: 12, lineHeight: 1.35 }}>
            {shapeOverrides.map(k => buildingShortCode(BUILDING_KEYS.find(b => b.key === k)?.building) || k).join(', ')} are drawn from shapes saved by this editor on an older map, not the current campus map — room numbers can be wrong or missing here (E2's big room reads 262 instead of 292).
          </span>
          <button onClick={resetShapeOverrides} disabled={resettingShapes} style={{ marginLeft: 'auto', flexShrink: 0, fontSize: 11, fontWeight: 800, padding: '6px 12px', borderRadius: 6, border: 'none', background: '#5c4400', color: '#fff', cursor: resettingShapes ? 'default' : 'pointer', opacity: resettingShapes ? 0.6 : 1 }}>
            {resettingShapes ? 'Switching…' : '↺ Use the current campus map'}
          </button>
        </div>
      )}

      {/* Preview banner — shown while viewing an older save */}
      {previewTs != null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 18px', background: '#7b1fa2', color: '#fff', flexShrink: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 800 }}>👁 Previewing an old save</span>
          <span style={{ fontSize: 12, opacity: 0.9 }}>{new Date(previewTs).toLocaleString()}</span>
          <span style={{ fontSize: 11, opacity: 0.8 }}>— Saving will override the latest version.</span>
          <button onClick={exitPreview} style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 800, padding: '5px 12px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.5)', background: 'transparent', color: '#fff', cursor: 'pointer' }}>
            Exit preview → back to latest
          </button>
        </div>
      )}

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Sidebar */}
        <div style={{ width: sidebarW, flexShrink: 0, display: 'flex', flexDirection: 'column', background: '#fff', borderRight: '1px solid #e0e0e0', overflowY: 'auto' }}>

          {/* Mode tabs: tour stops vs. free-roam locations */}
          <div style={{ display: 'flex', borderBottom: '1px solid #e0e0e0', flexShrink: 0 }}>
            {[{ id: 'tours', label: 'TOURS' }, { id: 'routes', label: 'ROUTES' }, { id: 'freeroam', label: 'FREE ROAM' }].map(tab => {
              const on = mode === tab.id;
              return (
                <button key={tab.id} onClick={() => { setMode(tab.id); setActiveStopIdx(null); setEditingBankKey(null); }}
                  style={{
                    flex: 1, padding: '11px 0', border: 'none', cursor: 'pointer',
                    fontSize: 11, fontWeight: 800, letterSpacing: '0.08em',
                    background: on ? '#fff' : '#f3f3f3',
                    color: on ? '#0d2137' : '#999',
                    borderBottom: on ? '3px solid #0093D0' : '3px solid transparent',
                  }}>{tab.label}</button>
              );
            })}
          </div>

          {loading ? (
            <div style={{ padding: 20, fontSize: 12, color: '#aaa', textAlign: 'center' }}>Loading…</div>
          ) : mode === 'tours' ? (
            <>
              {/* The tour itself (which one, its title, subtitle, description) is
                  edited in the panel above the map; this column is the path. */}
              {!activeTour && (
                <div style={{ padding: '22px 16px', fontSize: 12, color: '#999', textAlign: 'center', lineHeight: 1.5 }}>
                  Pick a tour above the map, or make a new one, and its stops will list here.
                </div>
              )}
              {activeTour && (
                <>
                  <div style={{ padding: '8px 14px 4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: '#888' }}>
                      STOPS ({activeTour.stops.length}) — click a room or search to add
                    </span>
                    <button onClick={openBank} title="Add, edit, or remove stops in the reusable bank"
                      style={{ flexShrink: 0, fontSize: 10, fontWeight: 800, padding: '4px 9px', borderRadius: 999, border: `1.5px solid ${UM.sky}`, background: '#fff', color: UM.sky, cursor: 'pointer' }}>
                      + From bank
                    </button>
                  </div>

                  <div>
                    {activeTour.stops.length === 0 && (
                      <div style={{ padding: '14px 14px 4px', fontSize: 12, color: '#bbb', textAlign: 'center' }}>No stops yet. Click a room, search, or use + below.</div>
                    )}
                    <StopInsertDivider index={0} onSubmit={insertStopByCode} roomChoices={roomChoices} />
                    {activeTour.stops.map((stop, i) => (
                      <div key={i}>
                        <div onClick={() => setActiveStopIdx(i)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            padding: '8px 14px',
                            background: activeStopIdx === i ? '#fff8e6' : 'none',
                            borderLeft: activeStopIdx === i ? '3px solid #f4a900' : '3px solid transparent',
                            cursor: 'pointer',
                          }}>
                          <div style={{ width: 24, height: 24, borderRadius: 12, background: '#6a1b9a', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#222' }}>
                              {stopCode(stop)}
                              {i === 0 && <span title="Visitors begin the tour here — there is no walk to the first stop." style={{ marginLeft: 6, fontSize: 9, fontWeight: 800, letterSpacing: '0.06em', padding: '1px 6px', borderRadius: 999, background: '#fff3cd', color: '#8a6d00' }}>START HERE</span>}
                              {!stopResolves(stop) && <span title="Not a room on the map — the tour cannot route here. Open the stop and pick a room." style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, color: '#c62828' }}>⚠ not on map</span>}
                            </div>
                            <div style={{ fontSize: 10, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{stop.title}</div>
                          </div>
                          <button onClick={e => { e.stopPropagation(); setActiveStopIdx(i); }} title="Edit this stop"
                            style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', border: '1px solid #6a1b9a', background: '#f3e9fb', color: '#6a1b9a', cursor: 'pointer', borderRadius: 4 }}>✎ Edit</button>
                          <button onClick={e => { e.stopPropagation(); moveStop(i, -1); }} disabled={i === 0}
                            style={{ fontSize: 11, padding: '2px 5px', border: '1px solid #ddd', background: '#fff', cursor: i === 0 ? 'default' : 'pointer', opacity: i === 0 ? 0.3 : 1, borderRadius: 4 }}>↑</button>
                          <button onClick={e => { e.stopPropagation(); moveStop(i, 1); }} disabled={i === activeTour.stops.length - 1}
                            style={{ fontSize: 11, padding: '2px 5px', border: '1px solid #ddd', background: '#fff', cursor: i === activeTour.stops.length - 1 ? 'default' : 'pointer', opacity: i === activeTour.stops.length - 1 ? 0.3 : 1, borderRadius: 4 }}>↓</button>
                          <button onClick={e => { e.stopPropagation(); removeStop(i); }}
                            style={{ fontSize: 11, padding: '2px 5px', border: '1px solid #e53935', background: '#fff', color: '#e53935', cursor: 'pointer', borderRadius: 4 }}>✕</button>
                        </div>
                        <StopInsertDivider index={i + 1} onSubmit={insertStopByCode} roomChoices={roomChoices} />
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          ) : mode === 'routes' ? (
            <RoutesPanel
              tour={activeTour}
              legInfo={legInfo}
              graphReady={!!navGraph}
              routeMode={routeMode}
              onMode={(m) => { setRouteMode(m); setRouteDraw(null); setHoverPath(null); }}
              routeLeg={routeLeg}
              onSelectLeg={selectRouteLeg}
              routeDraw={routeDraw}
              onBeginDraw={beginRouteDraw}
              onReset={resetRouteToAuto}
              onUndo={undoRouteDraw}
              onCancel={cancelRouteDraw}
              onFinish={finishRouteDraw}
              canFinish={routeDrawDone}
              onChooseFloor={chooseShaftFloor}
              nodeFloor={routeNodeFloor}
            />
          ) : (
            <>
              <div style={{ padding: '10px 14px 4px', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: '#888' }}>
                FREE ROAM ({(tourData?.stopBank || []).filter(s => s.freeRoam).length} shown of {(tourData?.stopBank || []).length})
              </div>
              <div style={{ fontSize: 11, color: '#999', padding: '0 14px 8px', lineHeight: 1.45 }}>
                Every location in the central bank. Click a place's button to cycle <b>Don't show</b> → <b>Show always</b> (on the visitor's map from the start) → <b>Show zoomed</b> (appears once they zoom in; Free Roam opens zoomed out). Tap a room on the map to add + edit it. <b>Delete</b> removes a duplicate or unwanted place (not one that is still a tour stop). Press Save to publish.
              </div>
              <div>
                {(tourData?.stopBank || []).length === 0 && (
                  <div style={{ padding: '14px 14px 4px', fontSize: 12, color: '#bbb', textAlign: 'center' }}>No stops in the bank yet.</div>
                )}
                {(tourData?.stopBank || []).map((loc) => {
                  const key = bankKey(loc);
                  const starred = !!loc.freeRoam;
                  // A place that is still a tour stop can't be deleted here: Save rebuilds
                  // the bank from the tours (buildStopBank), so it would come straight back.
                  const usedIn = (tourData?.tours || []).filter(t => (t.stops || []).some(st => bankKey(st) === key));
                  return (
                    <div key={key} onClick={() => setEditingBankKey(key)}
                      style={{
                        // Two lines: the name gets the full width, the three controls sit under it.
                        display: 'flex', flexDirection: 'column', gap: 6, padding: '9px 14px',
                        background: editingBankKey === key ? '#e7f6fb' : (starred ? '#fffdf3' : 'none'),
                        borderLeft: editingBankKey === key ? '3px solid #0093D0' : (starred ? '3px solid #FFB81C' : '3px solid transparent'),
                        cursor: 'pointer',
                      }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{loc.title || loc.label || stopCode(loc)}</div>
                        <div style={{ fontSize: 10, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{stopCode(loc)}</div>
                        {(loc.departments || []).length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 3 }}>
                            {loc.departments.map(d => (
                              <span key={d} style={{ fontSize: 9, fontWeight: 700, color: '#0277bd', background: '#e3f2fb', borderRadius: 4, padding: '1px 6px' }}>{d}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <FreeRoamModeButton mode={freeRoamMode(loc)} small onClick={e => { e.stopPropagation(); cycleBankFreeRoam(key); }} />
                      <span style={{ flex: 1 }} />
                      <button onClick={e => { e.stopPropagation(); setEditingBankKey(key); }} title="Edit this place"
                        style={{ flexShrink: 0, fontSize: 10, fontWeight: 700, padding: '3px 8px', border: '1px solid #0277bd', background: '#e3f2fb', color: '#0277bd', cursor: 'pointer', borderRadius: 4 }}>✎ Edit</button>
                      <button disabled={usedIn.length > 0}
                        onClick={e => {
                          e.stopPropagation();
                          const name = loc.title || loc.label || stopCode(loc);
                          if (!window.confirm(`Delete "${name}" (${stopCode(loc)}) from the stop bank?\n\nIts text and photos go with it. It disappears from Free Roam and from "+ From bank" once you press Save. (History and ⬇ Export keep a copy.)`)) return;
                          if (editingBankKey === key) setEditingBankKey(null);
                          deleteBankStop(key);
                        }}
                        title={usedIn.length ? `Still a stop in ${usedIn.map(t => t.title || `Tour ${t.id}`).join(', ')}. Remove it from the tour first, or set it to Don't show.` : 'Delete this place from the stop bank'}
                        style={{ flexShrink: 0, fontSize: 11, padding: '2px 7px', border: '1px solid #e0b4ad', background: '#fff', color: '#c0392b', fontWeight: 700, cursor: usedIn.length ? 'not-allowed' : 'pointer', borderRadius: 4, opacity: usedIn.length ? 0.35 : 1 }}>Delete</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* ── Resources editor (collapsed by default) ── */}
          {tourData && (
            <div style={{ borderTop: '4px solid #f0f0f0', marginTop: 'auto' }}>
              <div onClick={() => setResourcesOpen(o => !o)}
                style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}>
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: '#888' }}>
                  <span style={{ display: 'inline-block', width: 12, color: '#aaa' }}>{resourcesOpen ? '▾' : '▸'}</span>
                  RESOURCES ({(tourData.resources || []).length})
                </span>
                {resourcesOpen && (
                  <button onClick={e => { e.stopPropagation(); addResource(); }} style={{ fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 4, border: '1px solid #1565c0', background: '#e3f0fb', color: '#1565c0', cursor: 'pointer' }}>+ Add</button>
                )}
              </div>
              {resourcesOpen && (
                <div style={{ padding: '0 10px 12px' }}>
                  {(tourData.resources || []).length === 0 ? (
                    <div style={{ padding: '8px 4px', fontSize: 11, color: '#bbb' }}>No resources yet. Click + Add.</div>
                  ) : (tourData.resources || []).map((r, i) => (
                    <div key={i} style={{ border: '1px solid #e6e6e6', borderRadius: 8, padding: 8, marginBottom: 8, background: '#fafafa' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                        <span style={{ fontSize: 10, fontWeight: 800, color: '#999', flex: 1 }}>#{i + 1}</span>
                        <button onClick={() => moveResource(i, -1)} disabled={i === 0}
                          style={{ fontSize: 11, padding: '2px 6px', border: '1px solid #ddd', background: '#fff', cursor: i === 0 ? 'default' : 'pointer', opacity: i === 0 ? 0.3 : 1, borderRadius: 4 }}>↑</button>
                        <button onClick={() => moveResource(i, 1)} disabled={i === (tourData.resources.length - 1)}
                          style={{ fontSize: 11, padding: '2px 6px', border: '1px solid #ddd', background: '#fff', cursor: i === (tourData.resources.length - 1) ? 'default' : 'pointer', opacity: i === (tourData.resources.length - 1) ? 0.3 : 1, borderRadius: 4 }}>↓</button>
                        <button onClick={() => removeResource(i)}
                          style={{ fontSize: 11, padding: '2px 6px', border: '1px solid #e53935', background: '#fff', color: '#e53935', cursor: 'pointer', borderRadius: 4 }}>✕</button>
                      </div>
                      <input value={r.title || ''} onChange={e => updateResource(i, { title: e.target.value })} placeholder="Title"
                        style={{ width: '100%', boxSizing: 'border-box', fontSize: 12, padding: '5px 8px', border: '1px solid #ccc', borderRadius: 4, outline: 'none', marginBottom: 5 }} />
                      <input value={r.url || ''} onChange={e => updateResource(i, { url: e.target.value })} placeholder="https://…"
                        style={{ width: '100%', boxSizing: 'border-box', fontSize: 12, padding: '5px 8px', border: '1px solid #ccc', borderRadius: 4, outline: 'none', marginBottom: 5 }} />
                      <textarea value={r.description || ''} onChange={e => updateResource(i, { description: e.target.value })} placeholder="Description (optional)" rows={2}
                        style={{ width: '100%', boxSizing: 'border-box', fontSize: 12, padding: '5px 8px', border: '1px solid #ccc', borderRadius: 4, outline: 'none', resize: 'vertical' }} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Drag the sidebar's edge to resize it; double-click to reset; arrow keys when focused. */}
        <div role="separator" aria-orientation="vertical" aria-label="Resize the sidebar" tabIndex={0}
          aria-valuemin={SIDEBAR_MIN} aria-valuemax={SIDEBAR_MAX} aria-valuenow={sidebarW}
          title="Drag to resize the sidebar · double-click to reset"
          className="etc-resizer"
          onPointerDown={e => {
            if (e.button !== 0) return;
            e.preventDefault();
            e.currentTarget.setPointerCapture(e.pointerId);
            sidebarDrag.current = { x: e.clientX, w: sidebarW };
            document.body.style.userSelect = 'none';
          }}
          onPointerMove={e => { const d = sidebarDrag.current; if (d) setSidebarW(clampSidebar(d.w + e.clientX - d.x)); }}
          onPointerUp={e => {
            const d = sidebarDrag.current; if (!d) return;
            sidebarDrag.current = null; document.body.style.userSelect = '';
            resizeSidebar(d.w + e.clientX - d.x);
          }}
          onPointerCancel={() => { sidebarDrag.current = null; document.body.style.userSelect = ''; saveSidebarW(sidebarW); }}
          onDoubleClick={() => resizeSidebar(SIDEBAR_DEFAULT)}
          onKeyDown={e => {
            if (e.key === 'ArrowLeft') { e.preventDefault(); resizeSidebar(sidebarW - 20); }
            if (e.key === 'ArrowRight') { e.preventDefault(); resizeSidebar(sidebarW + 20); }
          }} />

        {/* Map + stop editor */}
        <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          <div ref={mapRef} style={{ width: '100%', height: '100%' }} />

          {/* ── The tour: which one, and what it is called — top centre, between
              the search box and the floor buttons. The sidebar is the path. ── */}
          {(mode === 'tours' || mode === 'routes') && !loading && tourData && !dragPolyMode && (
            <div style={{ position: 'absolute', top: 14, left: 326, right: 250, display: 'flex', justifyContent: 'center', pointerEvents: 'none', zIndex: 6 }}>
              <div style={{ pointerEvents: 'auto', background: '#fff', borderRadius: 12, boxShadow: '0 3px 14px rgba(0,0,0,0.25)', padding: '8px 12px', width: '100%', maxWidth: 560, boxSizing: 'border-box' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center' }}>
                  <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.1em', color: '#888', marginRight: 2 }}>TOUR</span>
                  {tourData.tours.map(t => (
                    <button key={t.id} onClick={() => { setActiveTourId(t.id); setActiveStopIdx(null); }}
                      title={t.title || `Tour ${t.id}`}
                      style={{
                        fontSize: 11.5, fontWeight: 700, padding: '4px 10px', borderRadius: 6,
                        border: activeTourId === t.id ? '2px solid #0d2137' : '1.5px solid #ddd',
                        background: activeTourId === t.id ? '#0d2137' : '#fff',
                        color: activeTourId === t.id ? '#fff' : '#333', cursor: 'pointer',
                      }}>{t.id} · {t.stops.length}</button>
                  ))}
                  <button onClick={addTour} title="Create a new tour" style={{ fontSize: 10, fontWeight: 700, padding: '4px 9px', borderRadius: 6, border: '1px solid #34a853', background: '#e8f5e9', color: '#2e7d32', cursor: 'pointer' }}>+ New</button>
                </div>
                {activeTour && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 7, paddingTop: 7, borderTop: '1px solid #f0f0f0' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: SERIF, fontSize: 15, fontWeight: 800, color: UM.brown, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {activeTour.title || <span style={{ color: '#bbb', fontStyle: 'italic' }}>Untitled tour</span>}
                      </div>
                      <div style={{ fontSize: 11, color: '#8a7a66', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {activeTour.subtitle || activeTour.description || <span style={{ color: '#bbb', fontStyle: 'italic' }}>No subtitle yet</span>}
                      </div>
                    </div>
                    {/* Off: no Skip button beside the stamp box, so every stamp
                        takes the door quote. Absent reads as on (as before). */}
                    <button onClick={() => updateActiveTour({ skippableStamps: activeTour.skippableStamps === false })}
                      role="switch" aria-checked={activeTour.skippableStamps !== false}
                      title={activeTour.skippableStamps !== false
                        ? "Visitors can press Skip to take a stamp without the door quote. Click to require the quote at every stop."
                        : "Every stamp needs its door quote — there is no Skip button. Click to allow skipping."}
                      style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 800, padding: '5px 9px', borderRadius: 7, border: `1px solid ${UM.line}`, background: '#fff', color: '#6b5d4b', cursor: 'pointer' }}>
                      <span style={{ width: 26, height: 15, borderRadius: 8, position: 'relative', background: activeTour.skippableStamps !== false ? '#00c853' : '#cfc6b8', transition: 'background 0.15s' }}>
                        <span style={{ position: 'absolute', top: 2, left: activeTour.skippableStamps !== false ? 13 : 2, width: 11, height: 11, borderRadius: 6, background: '#fff', transition: 'left 0.15s' }} />
                      </span>
                      Skippable stamps
                    </button>
                    <button onClick={() => setTourInfoOpen(true)} title="Edit this tour's title, subtitle and description"
                      style={{ flexShrink: 0, fontSize: 11, fontWeight: 800, padding: '6px 11px', borderRadius: 7, border: `1px solid ${UM.sky}`, background: '#fff', color: UM.sky, cursor: 'pointer' }}>✎ Edit</button>
                    <button onClick={() => setTestTour(activeTour.id)} title="Walk through the live tour exactly as a visitor does, from the welcome screen"
                      style={{ flexShrink: 0, fontSize: 11, fontWeight: 800, padding: '6px 12px', borderRadius: 7, border: 'none', background: '#00c853', color: '#fff', cursor: 'pointer' }}>▶ Test tour</button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Search bar — type a room code OR pick from the dropdown (top-left) */}
          {mode !== 'routes' && (mode === 'freeroam' || activeTour) && (
            <div style={{ position: 'absolute', top: 14, left: 14, width: 300, zIndex: 6 }}>
              <div style={{ display: 'flex', gap: 6, background: '#fff', borderRadius: 10, padding: 6, boxShadow: '0 3px 14px rgba(0,0,0,0.25)' }}>
                <span style={{ display: 'flex', alignItems: 'center', paddingLeft: 6, fontSize: 14, opacity: 0.7 }}>🔍</span>
                <input
                  value={search}
                  onChange={e => { setSearch(e.target.value); setSearchMsg(null); }}
                  onKeyDown={e => {
                    if (e.key === 'Escape') setSearch('');
                    if (e.key !== 'Enter') return;
                    // Enter takes the best suggestion — the typed text may be a
                    // name, which the code parser alone would reject.
                    const best = filterRoomChoices(searchChoices, search, 1)[0];
                    if (best) pickSearchChoice(best); else handleSearch();
                  }}
                  placeholder='Room number or name — e.g. "E2-442", "ENGAP"'
                  style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', fontSize: 13, fontWeight: 600, color: '#222' }} />
                <button
                  onClick={() => { setSearchMsg(null); setPlacingSpot(p => !p); }}
                  disabled={!navNodesReady}
                  title={navNodesReady
                    ? 'Place a pin anywhere on the map and give it a name'
                    : 'Loading nav data…'}
                  style={{ fontSize: 12, fontWeight: 800, padding: '6px 12px', borderRadius: 7, border: 'none', whiteSpace: 'nowrap', background: placingSpot ? UM.gold : UM.navy, color: placingSpot ? UM.brown : '#fff', cursor: navNodesReady ? 'pointer' : 'default', opacity: navNodesReady ? 1 : 0.5 }}>
                  {placingSpot ? '✕ Cancel' : 'Create Spot'}
                </button>
              </div>
              {search.trim() && !placingSpot && (() => {
                const list = filterRoomChoices(searchChoices, search, 8);
                return (
                  <div style={{ marginTop: 6, background: '#fff', borderRadius: 10, boxShadow: '0 6px 20px rgba(0,0,0,0.25)', overflow: 'hidden' }}>
                    {list.length === 0 && <div style={{ padding: '9px 12px', fontSize: 12, color: '#999' }}>No room or spot matches “{search}”.</div>}
                    {list.map(c => (
                      <div key={c.spot ? `spot:${c.spot.id}` : c.code} style={{ display: 'flex', alignItems: 'stretch', borderBottom: '1px solid #f2f2f2' }}>
                        <button onMouseDown={e => e.preventDefault()} onClick={() => pickSearchChoice(c)}
                          style={{ flex: 1, minWidth: 0, display: 'flex', gap: 10, alignItems: 'baseline', textAlign: 'left', padding: '7px 12px', border: 'none', background: '#fff', cursor: 'pointer' }}>
                          <span style={{ fontSize: 12.5, fontWeight: 800, color: c.spot ? UM.brown : '#333', flexShrink: 0 }}>{c.code}</span>
                          <span style={{ fontSize: 12, color: '#8a7a66', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.spot ? (c.name && c.name !== c.code ? `${c.name} · pinned spot` : 'pinned spot') : (c.name || '')}</span>
                        </button>
                        {c.spot && (
                          <button onMouseDown={e => e.preventDefault()} onClick={() => deleteCustomSpot(c.spot)} title="Delete this spot"
                            style={{ flexShrink: 0, width: 30, border: 'none', borderLeft: '1px solid #f2f2f2', background: '#fff', color: '#c0392b', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>✕</button>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}
              {placingSpot && (
                <div style={{ marginTop: 6, background: UM.gold, color: UM.brown, fontSize: 11, fontWeight: 700, padding: '7px 10px', borderRadius: 8, lineHeight: 1.35 }}>
                  Click anywhere on the map to drop the pin — it snaps to the nearest
                  nav point on floor {selectedFloor}. Esc to cancel.
                </div>
              )}
              {searchMsg && (
                <div style={{ marginTop: 6, background: 'rgba(0,0,0,0.78)', color: '#fff', fontSize: 11, padding: '6px 10px', borderRadius: 8 }}>{searchMsg}</div>
              )}
            </div>
          )}

          {/* Name the spot just dropped. Deliberately a modal: the pin is placed
              and nothing is written to the tour until this is answered. */}
          {spotDraft && (
            <div onClick={cancelSpot} style={{ position: 'absolute', inset: 0, zIndex: 30, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div onClick={e => e.stopPropagation()} style={{ background: UM.cream, borderRadius: 14, padding: 18, width: 340, maxWidth: '90%', boxShadow: '0 18px 60px rgba(0,0,0,0.45)', fontFamily: SANS }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: UM.ink, marginBottom: 4 }}>Name this spot</div>
                <div style={{ fontSize: 11.5, color: '#6d5b46', marginBottom: 12, lineHeight: 1.4 }}>
                  Floor {spotDraft.floor}
                  {spotDraft.building ? ` of ${buildingShortCode(spotDraft.building) || spotDraft.building}` : ', outside every building'}
                  {' — '}the floor you're viewing and the outline under the pin. Snapped to the nearest nav point
                  {spotDraft.node.room_number ? ` (by room ${spotDraft.node.room_number})` : ''}
                  {Number.isFinite(spotDraft.node.floor) && spotDraft.node.floor !== spotDraft.floor ? `, which is on floor ${spotDraft.node.floor}` : ''}.
                  That's what the tour walks to.
                </div>
                <input
                  autoFocus
                  value={spotLabel}
                  onChange={e => setSpotLabel(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') confirmSpot(); if (e.key === 'Escape') cancelSpot(); }}
                  maxLength={60}
                  placeholder="e.g. Atrium Stage"
                  style={{ width: '100%', boxSizing: 'border-box', fontSize: 14, padding: '9px 11px', border: `1px solid ${UM.line}`, borderRadius: 8, outline: 'none', marginBottom: 8 }} />
                <div style={{ fontSize: 11.5, color: '#6d5b46', marginBottom: 12 }}>
                  Filed as{' '}
                  <b style={{ color: UM.ink }}>
                    {spotLabelFor(spotDraft.building, spotLabel) || '…'}
                  </b>
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button onClick={cancelSpot} style={{ fontSize: 12, fontWeight: 700, padding: '7px 14px', borderRadius: 7, border: `1px solid ${UM.line}`, background: '#fff', color: UM.ink, cursor: 'pointer' }}>Cancel</button>
                  <button onClick={confirmSpot} disabled={!spotLabel.trim()} style={{ fontSize: 12, fontWeight: 800, padding: '7px 16px', borderRadius: 7, border: 'none', background: UM.navy, color: '#fff', cursor: spotLabel.trim() ? 'pointer' : 'default', opacity: spotLabel.trim() ? 1 : 0.5 }}>Create spot</button>
                </div>
              </div>
            </div>
          )}

          {/* Drag mode indicator */}
          {dragPolyMode && (
            <div style={{
              position: 'absolute', top: 14, right: 14, zIndex: 10, pointerEvents: 'none',
              background: 'rgba(0,77,80,0.93)', color: '#fff', borderRadius: 10,
              padding: '10px 14px', minWidth: 196,
              boxShadow: '0 2px 14px rgba(0,0,0,0.5)', fontFamily: 'Arial,sans-serif',
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', opacity: 0.6, marginBottom: 8 }}>DRAG VERTICES</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 12, opacity: dragHasSelection ? 0.35 : 1, fontWeight: dragHasSelection ? 400 : 700 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: dragHasSelection ? 'rgba(255,255,255,0.4)' : '#00bcd4' }} />
                Click a polygon
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', fontSize: 12, opacity: dragHasSelection ? 1 : 0.35, fontWeight: dragHasSelection ? 700 : 400 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: dragHasSelection ? '#00bcd4' : 'rgba(255,255,255,0.4)' }} />
                Drag a vertex
              </div>
            </div>
          )}

          {/* Floor selector + room-number toggle (hidden while an editor modal is open or in drag mode) */}
          {!activeStop && !editingBankKey && !dragPolyMode && (
            <div style={{ position: 'absolute', top: 14, right: 14, display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end', zIndex: 5 }}>
              <div style={{ display: 'flex', gap: 4, background: 'rgba(0,0,0,0.7)', padding: '4px 6px', borderRadius: 8 }}>
                {[1, 2, 3, 4, 5, 6].map(f => (
                  <button key={f} onClick={() => setSelectedFloor(f)} style={{
                    width: 30, height: 30, borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 800,
                    background: selectedFloor === f ? '#00c853' : 'rgba(255,255,255,0.15)',
                    color: selectedFloor === f ? '#fff' : 'rgba(255,255,255,0.75)',
                  }}>{f}</button>
                ))}
              </div>
              <button onClick={() => setShowRoomNumbers(v => !v)}
                title="Toggle room-number labels on the map"
                style={{
                  border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 800, padding: '6px 10px', borderRadius: 8,
                  background: showRoomNumbers ? UM.gold : 'rgba(0,0,0,0.7)',
                  color: showRoomNumbers ? UM.brown : 'rgba(255,255,255,0.85)',
                }}>
                {showRoomNumbers ? 'Room #s: ON' : 'Room #s: OFF'}
              </button>
            </div>
          )}

          {/* Rich stop editor — centered modal that dims the rest of the page */}
          {activeStop && (
            <StopEditor
              key={`${activeTourId}:${activeStopIdx}`}
              stop={activeStop}
              idx={activeStopIdx}
              total={activeTour.stops.length}
              nextStop={activeTour.stops[activeStopIdx + 1] || null}
              onUpdate={patch => updateStop(activeStopIdx, patch)}
              onClose={() => setActiveStopIdx(null)}
              onSave={() => saveStopForAllTours(activeStop)}
              saveLabel="💾 Save"
              saving={saving}
              saveState={saveState}
              roomChoices={assignChoices}
              unresolved={!stopResolves(activeStop)}
              onAssignRoom={(c) => updateStop(activeStopIdx, roomAssignment(activeStop, c))}
            />
          )}

          {/* Bank-entry editor — edits a reusable stop; saving applies it to
              every tour that uses this room, keeping the bank canonical. */}
          {editingBankKey && (() => {
            const entry = (tourData?.stopBank || []).find(s => bankKey(s) === editingBankKey);
            if (!entry) return null;
            return (
              <StopEditor
                key={`bank:${editingBankKey}`}
                stop={entry}
                idx={0}
                total={1}
                nextStop={null}
                onUpdate={patch => updateBankStop(editingBankKey, patch)}
                onClose={() => setEditingBankKey(null)}
                onSave={() => { saveStopForAllTours(entry); setEditingBankKey(null); }}
                saveLabel="💾 Save"
                freeRoamMode={freeRoamMode(entry)}
                onCycleFreeRoam={() => cycleBankFreeRoam(editingBankKey)}
                saving={saving}
                saveState={saveState}
                roomChoices={assignChoices}
                unresolved={!stopResolves(entry)}
                onAssignRoom={(c) => {
                  // The bank is keyed by room, so a reassigned entry has a new
                  // key — follow it, or the editor would be pointing at nothing.
                  const patch = roomAssignment(entry, c);
                  updateBankStop(editingBankKey, patch);
                  setEditingBankKey(bankKey({ ...entry, ...patch }));
                }}
              />
            );
          })()}

          {(mode === 'tours' || mode === 'routes') && !activeTour && !loading && (
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', pointerEvents: 'none', background: 'rgba(0,0,0,0.55)', color: '#fff', padding: '14px 22px', borderRadius: 10, fontSize: 14, fontWeight: 600 }}>
              Select or create a tour
            </div>
          )}
          {mode === 'freeroam' && !loading && !(tourData?.stopBank || []).some(s => s.freeRoam) && (
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', pointerEvents: 'none', background: 'rgba(0,0,0,0.55)', color: '#fff', padding: '14px 22px', borderRadius: 10, fontSize: 14, fontWeight: 600 }}>
              Click a room, or toggle SHOW on a location in the list, to add it to Free Roam
            </div>
          )}
        </div>
      </div>

      {/* ── Tour title / subtitle / description ── */}
      {tourInfoOpen && activeTour && (
        <TourInfoModal
          tour={activeTour}
          onUpdate={updateActiveTour}
          onDelete={() => { setTourInfoOpen(false); setDeleteTourId(activeTour.id); }}
          onClose={() => setTourInfoOpen(false)}
        />
      )}

      {introOpen && tourData && (
        <IntroClosingModal
          tourData={tourData}
          stampCount={(activeTour || tourData.tours?.[0])?.stops?.length || 0}
          sampleTour={activeTour || tourData.tours?.find(t => (t.stops || []).length) || null}
          onWelcome={patch => setTourData(td => td ? { ...td, welcome: { ...(td.welcome || {}), ...patch } } : td)}
          onClosing={patch => setTourData(td => td ? { ...td, closing: { ...(td.closing || {}), ...patch } } : td)}
          onPreview={(activeTour || tourData.tours?.find(t => (t.stops || []).length)) ? () => { setIntroOpen(false); setTestTour((activeTour || tourData.tours.find(t => (t.stops || []).length)).id); } : null}
          onClose={() => setIntroOpen(false)}
        />
      )}

      {/* ── Add stop from the bank ── */}
      {bankOpen && (
        <StopBankPopup
          bank={tourData?.stopBank || []}
          roomChoices={roomChoices}
          onPickRoom={(c) => { pickSearchChoice(c); setBankOpen(false); }}
          onPick={(s) => { insertBankStop(s); setBankOpen(false); }}
          onEdit={(s) => { setBankOpen(false); setEditingBankKey(bankKey(s)); }}
          onDelete={(s) => deleteBankStop(bankKey(s))}
          onClose={() => setBankOpen(false)}
        />
      )}

      {/* ── Delete-tour confirmation ── */}
      {deleteTourId && (
        <DeleteTourConfirm
          tour={(tourData?.tours || []).find(t => t.id === deleteTourId)}
          onCancel={() => setDeleteTourId(null)}
          onConfirm={() => { deleteTour(deleteTourId); setDeleteTourId(null); }}
        />
      )}

      {/* ── 1:1 tour preview (reuses the live tour viewer with the draft data) ── */}
      {testTour && tourData && (
        <TestTourModal tourData={tourData} tourId={testTour} onClose={() => setTestTour(null)} />
      )}

      {/* ── Edit history sidebar ── */}
      {historyOpen && (
        <div style={{ position: 'fixed', top: 52, right: 0, bottom: 0, width: 300, background: '#fff', borderLeft: '1px solid #e0e0e0', boxShadow: '-6px 0 22px rgba(0,0,0,0.15)', zIndex: 45, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f0f0f0', flexShrink: 0 }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: '#0d2137' }}>Edit History</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={loadHistory} title="Refresh" style={{ width: 26, height: 26, borderRadius: 6, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: 13 }}>↻</button>
              <button onClick={() => setHistoryOpen(false)} title="Close" style={{ width: 26, height: 26, borderRadius: 6, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 800 }}>✕</button>
            </div>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
            {versionsLoading ? (
              <div style={{ padding: 20, fontSize: 12, color: '#aaa', textAlign: 'center' }}>Loading…</div>
            ) : versions.length === 0 ? (
              <div style={{ padding: 20, fontSize: 12, color: '#bbb', textAlign: 'center' }}>No previous saves yet.<br />They'll appear here after you Save.</div>
            ) : (() => {
              // One row per DAY, folded; a day unfolds into its exact saves. Fifty
              // saves made over a few afternoons read as a few afternoons.
              const dayOf = (ts) => new Date(ts).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
              const days = [];
              versions.forEach((v, i) => {
                const d = dayOf(v.ts);
                if (!days.length || days[days.length - 1].day !== d) days.push({ day: d, items: [] });
                days[days.length - 1].items.push({ v, i });
              });
              const toggle = (day) => setOpenDays(o => { const n = new Set(o); if (n.has(day)) n.delete(day); else n.add(day); return n; });
              return days.map(({ day, items }) => {
                const open = openDays.has(day) || items.some(({ v }) => previewTs === v.ts);
                const users = [...new Set(items.map(({ v }) => v.user).filter(Boolean))];
                const nChanges = items.reduce((n, { v }) => n + (Array.isArray(v.changes) ? v.changes.length : 0), 0);
                return (
                  <div key={day} style={{ marginBottom: 6 }}>
                    <button onClick={() => toggle(day)} style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, border: '1px solid #e6e6e6', background: open ? '#f3f6fa' : '#fff', cursor: 'pointer' }}>
                      <span style={{ width: 12, color: '#888', fontSize: 11 }}>{open ? '▾' : '▸'}</span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: 12, fontWeight: 800, color: '#222' }}>{day}{items[0].i === 0 ? <span style={{ color: '#2e7d32', fontWeight: 700 }}> · latest</span> : null}</span>
                        <span style={{ display: 'block', fontSize: 10, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {items.length} save{items.length === 1 ? '' : 's'}{nChanges ? ` · ${nChanges} change${nChanges === 1 ? '' : 's'}` : ''}{users.length ? ` · ${users.join(', ')}` : ''}
                        </span>
                      </span>
                    </button>
                    {open && <div style={{ padding: '6px 0 0 8px' }}>{items.map(({ v, i }) => {
              const isPreview = previewTs === v.ts;
              const changes = Array.isArray(v.changes) ? v.changes : [];
              return (
                <div key={v.ts} style={{ border: `1px solid ${isPreview ? '#ce93d8' : '#eee'}`, borderRadius: 8, padding: '8px 10px', marginBottom: 8, background: isPreview ? '#f7edfb' : '#fafafa' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#222' }}>{new Date(v.ts).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}</div>
                      <div style={{ fontSize: 10, color: i === 0 ? '#2e7d32' : '#999', fontWeight: i === 0 ? 700 : 400 }}>
                        {i === 0 ? '● Latest save' : `Save #${versions.length - i}`}
                        {v.user && <span style={{ color: '#0277bd', fontWeight: 700 }}> · by {v.user}</span>}
                      </div>
                    </div>
                    <button onClick={() => previewVersion(v.ts)} style={{ fontSize: 11, fontWeight: 800, padding: '5px 11px', borderRadius: 6, border: '1px solid #6a1b9a', background: isPreview ? '#6a1b9a' : '#f3e9fb', color: isPreview ? '#fff' : '#6a1b9a', cursor: 'pointer', flexShrink: 0 }}>{isPreview ? 'Previewing' : 'Preview'}</button>
                  </div>
                  {changes.length > 0 && (
                    <ul style={{ margin: '7px 0 0', paddingLeft: 15, fontSize: 10.5, color: '#5b6470', lineHeight: 1.5 }}>
                      {changes.map((c, ci) => <li key={ci}>{c}</li>)}
                    </ul>
                  )}
                </div>
              );
                    })}</div>}
                  </div>
                );
              });
            })()}
          </div>
          {previewTs != null && (
            <div style={{ padding: '10px 12px', borderTop: '1px solid #f0f0f0', flexShrink: 0 }}>
              <button onClick={exitPreview} style={{ width: '100%', fontSize: 12, fontWeight: 800, padding: '9px', borderRadius: 7, border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}>Exit preview → back to latest</button>
            </div>
          )}
          {isAdmin && <div style={{ padding: '10px 12px', borderTop: '1px solid #f0f0f0', flexShrink: 0 }}>
            {restoreNote && <div style={{ fontSize: 11, color: '#2e7d32', fontWeight: 700, marginBottom: 8, lineHeight: 1.4 }}>✓ {restoreNote}</div>}
            <button onClick={() => setRestoreOpen(true)} title="Bring back a stop's content from an Export zip or a tours.json" style={{ width: '100%', fontSize: 12, fontWeight: 800, padding: '9px', borderRadius: 7, border: `1px solid ${UM.sky}`, background: '#fff', color: UM.sky, cursor: 'pointer' }}>⤴ Restore a stop from a backup…</button>
            <div style={{ fontSize: 10, color: '#999', marginTop: 6, lineHeight: 1.4 }}>Overwrote a stop? Open the zip from ⬇ Export and pick the stop to put back.</div>
          </div>}
        </div>
      )}

      {/* ── Restore from a backup ── */}
      {restoreOpen && isAdmin && (
        <RestoreModal
          current={tourData}
          onApply={(picks) => { const r = applyRestore(picks); if (r) setRestoreOpen(false); }}
          onClose={() => setRestoreOpen(false)}
        />
      )}

      {/* ── Export everything: progress + result ── */}
      {exportState && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(10,18,30,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 14, width: 'min(460px, 100%)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)', overflow: 'hidden' }}>
            <div style={{ padding: '14px 18px', background: '#0d2137', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 14, fontWeight: 800 }}>⬇ Export everything</span>
              <span style={{ fontSize: 11, color: '#8aa' }}>{exportState.phase === 'running' ? 'Working…' : exportState.phase === 'done' ? 'Done' : 'Stopped'}</span>
            </div>
            <div style={{ padding: '14px 18px', fontSize: 12.5, color: '#333', lineHeight: 1.6 }}>
              <p style={{ margin: '0 0 10px', color: '#5b6470' }}>
                One zip with the live tour data, every saved version still in Edit History, and every photo they point at. Read from the live store, so unsaved edits on this screen are not in it.
              </p>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {exportState.lines.map((l, i) => (
                  <li key={i} style={{ color: i === exportState.lines.length - 1 && exportState.phase === 'running' ? '#0277bd' : '#333' }}>{l}</li>
                ))}
              </ul>
              {exportState.error && (
                <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 8, background: '#fdecea', color: '#b71c1c', fontWeight: 700 }}>⚠ {exportState.error}</div>
              )}
              {exportState.phase === 'done' && (
                <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 8, background: '#fff8e1', color: '#6d4c00' }}>
                  The file holds the scavenger-hunt door-quote answers, which visitors never see. Keep it somewhere private.
                </div>
              )}
            </div>
            <div style={{ padding: '0 18px 16px', display: 'flex', gap: 8 }}>
              {exportState.phase === 'running' ? (
                <button onClick={cancelExport} style={{ flex: 1, padding: '10px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
              ) : (
                <>
                  {exportState.phase === 'error' && <button onClick={runExport} style={{ flex: 1, padding: '10px', borderRadius: 8, border: 'none', background: UM.gold, color: UM.brown, fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>Try again</button>}
                  <button onClick={() => setExportState(null)} style={{ flex: 1, padding: '10px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>Close</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Override-old-version confirm ── */}
      {overrideOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(10,18,30,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 14, width: 'min(420px, 100%)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)', overflow: 'hidden' }}>
            <div style={{ background: '#7b1fa2', color: '#fff', padding: '12px 16px', fontWeight: 800, fontSize: 14 }}>⚠ Override the latest save?</div>
            <div style={{ padding: '16px 18px 18px' }}>
              <div style={{ fontSize: 13, color: '#333', lineHeight: 1.5, marginBottom: 6 }}>
                You're saving from an <b>old version</b>{previewTs != null ? <> ({new Date(previewTs).toLocaleString()})</> : null}. This will <b>override the latest save</b>.
              </div>
              <div style={{ fontSize: 12, color: '#777', marginBottom: 12 }}>Type <b>CONTINUE</b> to confirm.</div>
              <input autoFocus value={overrideInput}
                onChange={e => setOverrideInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') confirmOverride(); }}
                placeholder="CONTINUE"
                style={{ width: '100%', boxSizing: 'border-box', fontSize: 14, padding: '10px 12px', border: '2px solid #e0e0e0', borderRadius: 8, outline: 'none', marginBottom: 14, textTransform: 'uppercase' }} />
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { setOverrideOpen(false); setOverrideInput(''); }} style={{ flex: 1, padding: '10px', borderRadius: 8, border: '1px solid #ccc', background: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
                <button onClick={confirmOverride} disabled={overrideInput.trim().toUpperCase() !== 'CONTINUE'} style={{ flex: 1, padding: '10px', borderRadius: 8, border: 'none', background: overrideInput.trim().toUpperCase() === 'CONTINUE' ? '#7b1fa2' : '#d8b3e0', color: '#fff', fontWeight: 800, fontSize: 13, cursor: overrideInput.trim().toUpperCase() === 'CONTINUE' ? 'pointer' : 'default' }}>Save &amp; override</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Guide popup ── */}
      {guideOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
             onClick={e => { if (e.target === e.currentTarget) setGuideOpen(false); }}>
          <div style={{ background: '#1a2b3c', borderRadius: 12, width: 820, maxWidth: '94vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.6)', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', background: '#0d2137', flexShrink: 0 }}>
              <span style={{ fontSize: 15, fontWeight: 800, color: '#fff' }}>Eng Tour Creator — Guide</span>
              <button onClick={() => setGuideOpen(false)} style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: 22, lineHeight: 1, cursor: 'pointer', padding: 0 }}>×</button>
            </div>
            <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.1)', flexShrink: 0 }}>
              {['▶ Watch', 'The Basics', 'Tour Stops', 'Free Roam', 'Saving & History'].map((tab, i) => (
                <button key={i} onClick={() => setGuideTab(i)} style={{ flex: 1, padding: '10px 0', fontSize: 12, fontWeight: guideTab === i ? 800 : 500, background: 'transparent', border: 'none', borderBottom: `2px solid ${guideTab === i ? UM.gold : 'transparent'}`, color: guideTab === i ? UM.gold : 'rgba(255,255,255,0.55)', cursor: 'pointer', transition: 'color 0.15s' }}>
                  {tab}
                </button>
              ))}
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', color: '#d0d8e0', fontSize: 13, lineHeight: 1.65 }}>
              {guideTab === 0 && (
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: '#fff', marginBottom: 10 }}>Walkthrough video</div>
                  <div style={{ borderRadius: 10, overflow: 'hidden', background: '#000' }}>
                    <VideoEmbed url="https://youtu.be/cNv8DIabuNQ" />
                  </div>
                  <p style={{ margin: '14px 0 0', color: 'rgba(255,255,255,0.6)' }}>A full walkthrough of the Tour Creator. The other tabs cover each part in writing.</p>
                </div>
              )}
              {guideTab === 1 && (
                <div>
                  <p style={{ margin: '0 0 14px' }}>This is where you edit everything visitors see on the live Engineering Tour. Don't worry about breaking anything — nothing changes for visitors until you press <strong style={{ color: UM.gold }}>Save</strong>, and every save can be looked up later in History.</p>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    <li style={{ marginBottom: 9 }}>The sidebar has three tabs: <strong>TOURS</strong> (the guided tours with numbered stops), <strong>ROUTES</strong> (the walking path between each pair of stops — automatic, or drawn by hand when the tour should go a particular way), and <strong>FREE ROAM</strong> (the starred places visitors can explore on their own).</li>
                    <li style={{ marginBottom: 9 }}>The map shows the building floor by floor — use the floor buttons in the top-right corner to move between levels.</li>
                    <li style={{ marginBottom: 9 }}>To add something, just <strong>click a room on the map</strong>, or type its room number (like <code style={{ background: 'rgba(255,255,255,0.1)', padding: '1px 5px', borderRadius: 4 }}>E2-442</code>) <strong>or its name</strong> ("ENGAP", "Dean's Office") in the search box. A room's preset name becomes the stop's title for you.</li>
                    <li>When you're done, press <strong style={{ color: UM.gold }}>Save</strong> in the top bar. That's it — the live tour updates right away.</li>
                  </ul>
                </div>
              )}
              {guideTab === 2 && (
                <div>
                  <p style={{ margin: '0 0 14px' }}>A tour is a numbered list of stops that visitors walk in order, earning a stamp at each one.</p>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    <li style={{ marginBottom: 9 }}>Pick a tour in the panel <strong>above the map</strong>; <strong>✎ Edit</strong> there changes its title, subtitle and description. Then click rooms on the map (or search) to add stops — the sidebar is the path.</li>
                    <li style={{ marginBottom: 9 }}>A stop's <strong>room</strong> (E2-442) is where the tour navigates to, and its <strong>title</strong> is the name visitors read. To move a stop to a different room, open it and use the <strong>Room</strong> dropdown in the grey admin box — it lists real rooms with their names, so pick "442 · ENGAP" rather than typing. A stop marked <strong style={{ color: '#ffab91' }}>⚠ not on map</strong> is one whose room doesn't exist on the floor plan, and the tour cannot route to it until you pick one.</li>
                    <li style={{ marginBottom: 9 }}><strong>+ From bank</strong> lists every stop ever written <em>and</em> every room the campus has a name for, each marked "has stop content" or "empty" — so you can see at a glance what still needs writing.</li>
                    <li style={{ marginBottom: 9 }}>Somewhere that isn't a room (an atrium, a study area)? <strong>Create Spot</strong>, then click the map: the spot takes the floor you're viewing, the building it lands in, and the nearest walking point — that's what the tour navigates to.</li>
                    <li style={{ marginBottom: 9 }}><strong>Visitors start the tour at stop 1</strong> — its pin on their map reads "Start Here!" (the stop list tags it <strong>START HERE</strong>) and there is no walk to it, so make stop 1 the place you want people to gather. From then on they see only the path they are walking right now, plus the numbered pins for the stops ahead. Stairs and elevators are one tap: climbing from floor 100 to 300 shows a single "100 → 300" box, not one per floor.</li>
                    <li style={{ marginBottom: 9 }}>Is the tour walking people the wrong way between two stops? Open <strong>ROUTES</strong>, pick the leg, and press <strong>Draw by hand</strong>: a preview path to the corridor point nearest your cursor follows the mouse wherever it is, each click sets a point, a stair or elevator lights up when it is under the cursor and asks which floor when clicked, and <strong>Finish</strong> lights up once you reach the next stop's door. Normal and accessible routes are drawn separately (blue and green).</li>
                    <li style={{ marginBottom: 9 }}>Click a stop in the list to open it. The editor shows <strong>exactly what visitors will see</strong> — click any text to change it.</li>
                    <li style={{ marginBottom: 9 }}>To add a photo, video, or link button: hover between paragraphs and press the blue <strong>[+]</strong> that appears. Photos can simply be dragged in from your computer.</li>
                    <li style={{ marginBottom: 9 }}>Reorder stops with the <strong>↑ ↓</strong> arrows; remove one with <strong>✕</strong>. The little <strong>[+]</strong> between stops inserts a new one right there.</li>
                    <li>The <strong>Door quote</strong> (in the grey admin box) is the secret word posted at the stop that visitors type to earn their stamp. It's forgiving — lowercase and small typos still count.</li>
                  </ul>
                </div>
              )}
              {guideTab === 3 && (
                <div>
                  <p style={{ margin: '0 0 14px' }}>Free Roam places appear as <strong style={{ color: UM.gold }}>gold stars</strong> on the visitor map — tapping a star opens the place's info card.</p>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    <li style={{ marginBottom: 9 }}>Switch to the <strong>FREE ROAM</strong> tab in the sidebar, then click a room on the map (or search) to add a place.</li>
                    <li style={{ marginBottom: 9 }}>A duplicate or unwanted place? Press its <strong>Delete</strong> in the FREE ROAM list, then Save. A place that is still a stop in a tour can't be deleted there — remove it from the tour first, or set it to Don't show.</li>
                    <li style={{ marginBottom: 9 }}>Give it a name and a short subtitle, and add photos or a video — editing works the same as tour stops.</li>
                    <li style={{ marginBottom: 9 }}>The <strong>Category</strong> groups places into the filter buttons visitors see (like "Student Services").</li>
                    <li>Each place has one button you click to cycle: <strong>Don't show</strong> → <strong>Show always</strong> (its star is on the visitor's map from the start) → <strong>Show zoomed</strong> (its star appears once they zoom in — Free Roam opens zoomed out, so use this for smaller spots).</li>
                  </ul>
                </div>
              )}
              {guideTab === 4 && (
                <div>
                  <p style={{ margin: '0 0 14px' }}>Every time anyone presses <strong style={{ color: UM.gold }}>Save</strong>, a snapshot is kept automatically — with their name, the time, and a list of what changed.</p>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    <li style={{ marginBottom: 9 }}>Click <strong>History</strong> in the top bar to see past saves, newest first — one row per day; open a day to see each save with its time, who made it and what changed.</li>
                    <li style={{ marginBottom: 9 }}>Press <strong>Preview</strong> on any save to look at it. You're just looking — the live tour isn't affected.</li>
                    <li style={{ marginBottom: 9 }}>Want to bring an old version back? Preview it, then press <strong>Save</strong> — you'll be asked to type CONTINUE to confirm.</li>
                    <li style={{ marginBottom: 9 }}>If two people are editing at the same time, the most recent save wins — check History if something looks unexpected.</li>
                    <li style={{ marginBottom: 9 }}>The last 50 saves are kept, and each one for about six months.</li>
                    <li style={{ marginBottom: 9 }}>If an amber <strong>Older room shapes</strong> banner appears over the map, a building is being drawn from shapes saved in this editor long ago rather than the current campus map; <strong>↺ Use the current campus map</strong> fixes it in one press. A stop on a room that only existed in the old shapes then shows "not on map" until you pick its room again.</li>
                    <li style={{ marginBottom: 9 }}><span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.06em', padding: '1px 6px', borderRadius: 999, background: UM.gold, color: UM.brown, marginRight: 6 }}>UMAPS ADMIN ONLY</span>The two buttons below appear only when signed in with the UMaps Admin account.</li>
                    <li style={{ marginBottom: 9 }}><strong>⬇ Export</strong> in the top bar downloads one zip with the live data, every saved version and every photo. It includes the door-quote answers, so keep it private.</li>
                    <li>Overwrote one stop? At the bottom of History, <strong>⤴ Restore a stop from a backup</strong> opens that zip right here in your browser and lets you put back just that stop's content — its place in the tour and its room stay as they are. If the stop's room has changed since the backup (a name then, a number now), use <strong>PUT INTO</strong> on that row to aim it at the right stop. Then press Save.</li>
                  </ul>
                </div>
              )}
            </div>
            <div style={{ flexShrink: 0, padding: '10px 24px', borderTop: '1px solid rgba(255,255,255,0.1)', background: '#0d2137', textAlign: 'center' }}>
              <div>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>Support Contact: </span>
                <a href="mailto:franci44@myumanitoba.ca" style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', textDecoration: 'none' }}>franci44@myumanitoba.ca</a>
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 3 }}>
                For urgent matters, my cell is <a href="tel:4316886365" style={{ color: 'rgba(255,255,255,0.65)', textDecoration: 'none' }}>431-688-6365</a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Rich stop editor ──────────────────────────────────────────────────────────
// A centered modal (dimming the rest of the page) that renders the EXACT card a
// visitor sees on the live tour, fully editable. Content is an ordered stack of
// media blocks (text / image / video / link); each block's reorder/remove
// controls sit in its corners, and link/video URL editors animate in on hover.
// Admin-only fields (room type, door quote, label) live in a separate side
// container so the tour-info card stays a faithful preview. Hover a divider line
// to reveal a [+] that inserts a media block there.
function StopEditor({ stop, idx, total, nextStop, onUpdate, onClose, onSave, saveLabel = "💾 Save", freeRoamMode: roamMode, onCycleFreeRoam, saving, saveState, roomChoices = [], onAssignRoom, unresolved = false }) {
  // Body edited as blocks in local state (stable cursors); serialized back to
  // `text` on every change, migrating legacy images[]/videoUrl into the stack.
  const [blocks, setBlocks] = useState(() => parseStopBlocks(stop));

  const code = stopCode(stop);

  const commitBlocks = useCallback((next) => {
    setBlocks(next);
    // Inline tokens become the single source of truth — clear the legacy fields.
    onUpdate({ text: serializeBlocks(next), images: [], videoUrl: "" });
  }, [onUpdate]);

  const updateBlock = (i, patch) => commitBlocks(blocks.map((b, j) => j === i ? { ...b, ...patch } : b));
  const removeBlock = (i) => commitBlocks(blocks.filter((_, j) => j !== i));
  const insertAt = (i, type) => {
    const next = [...blocks];
    next.splice(i, 0, makeBlock(type));
    commitBlocks(next);
  };
  const moveBlock = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= blocks.length) return;
    const next = [...blocks];
    [next[i], next[j]] = [next[j], next[i]];
    commitBlocks(next);
  };

  const nextName = nextStop ? (nextStop.title || nextStop.label || stopCode(nextStop)) : null;
  const nextCode = nextStop ? stopCode(nextStop) : null;

  const adminLabel = { fontSize: 9, fontWeight: 800, letterSpacing: "0.08em", color: "#a08f78", margin: "0 0 5px" };
  const adminInput = { width: "100%", boxSizing: "border-box", fontSize: 13, padding: "8px 10px", border: `1px solid ${UM.line}`, borderRadius: 8, outline: "none", background: "#fff" };

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 60, background: "rgba(10,18,30,0.62)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: "min(880px, 100%)", maxHeight: "92vh", display: "flex", flexDirection: "column",
        background: UM.sand, borderRadius: 18, overflow: "hidden", boxShadow: "0 24px 70px rgba(0,0,0,0.55)",
      }}>
        {/* Header with Save top-right */}
        <div style={{ flexShrink: 0, background: UM.navy, color: "#fff", padding: "11px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `3px solid ${UM.gold}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <button onClick={onClose} title="Close editor" style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", width: 28, height: 28, borderRadius: 7, cursor: "pointer", fontSize: 14, fontWeight: 800, flexShrink: 0 }}>✕</button>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.08em", color: "rgba(255,255,255,0.6)" }}>EDIT STOP {idx + 1} OF {total}</div>
              <div style={{ fontFamily: SERIF, fontSize: 15, fontWeight: 800 }}>
                {code}
                {unresolved && <span title="This room is not on the map, so the tour cannot route to it. Pick a room below." style={{ marginLeft: 8, fontSize: 10, fontFamily: SANS, fontWeight: 800, color: "#ffab91" }}>⚠ not on the map</span>}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            {saveState === "saved" && <span style={{ fontSize: 10, color: "#7BD389", fontWeight: 700 }}>✓ Saved</span>}
            {saveState === "error" && <span style={{ fontSize: 10, color: "#ffab91", fontWeight: 700 }}>Failed</span>}
            <button onClick={onSave} disabled={saving} style={{
              fontSize: 13, fontWeight: 900, padding: "8px 18px", borderRadius: 9, border: "none",
              background: UM.gold, color: UM.brown, cursor: saving ? "default" : "pointer", opacity: saving ? 0.6 : 1,
            }}>{saving ? "Saving…" : saveLabel}</button>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: 18 }}>
          {/* ── Admin-only container (at the top, separate from the tour info) ── */}
          <div style={{ background: "#fff", borderRadius: 14, border: `1px solid ${UM.line}`, padding: "13px 14px", boxShadow: "0 6px 22px rgba(0,0,0,0.10)", marginBottom: 14 }}>
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.08em", color: "#b08968", marginBottom: 12 }}>🔒 ADMIN ONLY · HIDDEN FROM VISITORS</div>

            <div style={adminLabel}>RELEVANT DEPARTMENTS (free-roam filter)</div>
            <div style={{ marginBottom: 14 }}>
              <DepartmentPicker value={stop.departments} onChange={d => onUpdate({ departments: d })} />
            </div>

            {onCycleFreeRoam && (
              <div style={{ marginBottom: 14 }}>
                <div style={adminLabel}>FREE ROAM</div>
                <FreeRoamModeButton mode={roamMode} onClick={onCycleFreeRoam} />
                <div style={{ fontSize: 11, color: "#8a7a66", marginTop: 6, lineHeight: 1.45 }}>
                  {FREE_ROAM_MODES.find(m => m.mode === roamMode)?.hint} Click to cycle: Don't show → Show always → Show zoomed.
                </div>
              </div>
            )}

            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 200px", minWidth: 0 }}>
                <div style={adminLabel}>DOOR QUOTE (scavenger-hunt answer)</div>
                <input value={stop.quote || ""} onChange={e => onUpdate({ quote: e.target.value.toUpperCase() })} placeholder="e.g. ROCKETSARECOOL"
                  style={{ ...adminInput, textTransform: "uppercase" }} />
              </div>
              <div style={{ flex: "1 1 240px", minWidth: 0 }}>
                <div style={adminLabel}>ROOM — WHERE THE TOUR NAVIGATES TO</div>
                <RoomAssignPicker stop={stop} code={code} choices={roomChoices} unresolved={unresolved} onAssign={onAssignRoom} />
              </div>
            </div>
          </div>

          {/* ── Tour info (the editable visitor card) ── */}
          <div>
            <div style={{ fontSize: 10, color: "#a08f78", marginBottom: 8, textAlign: "center", fontStyle: "italic" }}>
              This is exactly what visitors see — tap any part to edit.
            </div>
            <div style={{ background: UM.cream, borderRadius: 16, padding: "16px 18px 18px", boxShadow: "0 6px 22px rgba(0,0,0,0.16)" }}>
              <div style={{ fontSize: 11, color: UM.sky, fontWeight: 800, letterSpacing: "0.06em" }}>STOP {idx + 1} OF {total} · {code}</div>

              {/* Title — faint pencil hints it's editable */}
              <EditableLine value={stop.title || ""} onChange={v => onUpdate({ title: v })} placeholder="Stop title"
                style={{ fontFamily: SERIF, fontSize: 21, fontWeight: 800, color: UM.brown }} wrapStyle={{ margin: "4px 0 2px" }} />

              {/* Subtitle */}
              <EditableLine value={stop.subtitle || ""} onChange={v => onUpdate({ subtitle: v })} placeholder="Short subtitle (optional)"
                style={{ fontSize: 13, color: "#8a7a66" }} wrapStyle={{ marginBottom: 6 }} />

              {/* Body block stack with insert dividers */}
              <InsertDivider onInsert={type => insertAt(0, type)} />
              {blocks.map((b, i) => (
                <div key={b._id}>
                  <StopBlock block={b} index={i} count={blocks.length}
                    onChange={patch => updateBlock(i, patch)}
                    onMove={delta => moveBlock(i, delta)}
                    onRemove={() => removeBlock(i)} />
                  <InsertDivider onInsert={type => insertAt(i + 1, type)} />
                </div>
              ))}

              {/* What's Next — names the next stop for the admin */}
              <div style={{ marginTop: 12, padding: "12px 14px", borderRadius: 12, background: "#fff", border: `1.5px solid ${UM.line}`, borderLeft: `4px solid ${UM.sky}` }}>
                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", color: UM.sky, marginBottom: 4, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                  <span>👉 WHAT'S NEXT</span>
                  {nextStop
                    ? <span style={{ fontWeight: 700, color: "#8a7a66", textTransform: "none", letterSpacing: 0 }}>→ Next stop: <b style={{ color: UM.brown }}>{nextName}</b> ({nextCode})</span>
                    : <span style={{ fontWeight: 700, color: "#b6a892", textTransform: "none", letterSpacing: 0 }}>last stop — not shown</span>}
                </div>
                {nextStop ? (
                  <AutoTextarea value={stop.guidance || ""} onChange={v => onUpdate({ guidance: v })}
                    placeholder={`Point visitors toward ${nextName} (${nextCode}). Leave blank for an auto-generated default.`}
                    style={{ width: "100%", boxSizing: "border-box", fontSize: 13, color: UM.ink, lineHeight: 1.55, border: `1px solid ${UM.line}`, borderRadius: 8, outline: "none", padding: "7px 9px", background: "#fff", fontFamily: SANS }} />
                ) : (
                  <div style={{ fontSize: 12, color: "#999", fontStyle: "italic" }}>No "what's next" bubble after the final stop.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// One body block (text / image / video / link). Reorder + remove controls sit in
// the corners (↑ top-right, ↓ bottom-right, ✕ top-left) and fade in on hover so
// the block itself reads like the live infobox.
function StopBlock({ block, index, count, onChange, onMove, onRemove }) {
  const [hover, setHover] = useState(false);
  const corner = (extra) => ({
    position: "absolute", zIndex: 4, width: 26, height: 26, borderRadius: 7,
    background: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.28)", cursor: "pointer",
    fontSize: 12, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center",
    opacity: hover ? 1 : 0, transition: "opacity 120ms ease", ...extra,
  });
  return (
    <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)} style={{ position: "relative", margin: "6px 0" }}>
      {block.type === "text" && (
        <AutoTextarea value={block.value} onChange={v => onChange({ value: v })} placeholder="Write a paragraph…"
          style={{ width: "100%", boxSizing: "border-box", fontSize: 14, color: UM.ink, lineHeight: 1.65, border: "1px solid transparent", borderRadius: 8, outline: "none", background: "transparent", padding: "4px 6px", fontFamily: SANS }} />
      )}
      {block.type === "image" && <ImageDrop src={block.src} height={block.height} onUploaded={p => onChange({ src: p })} onHeight={h => onChange({ height: h })} />}
      {block.type === "video" && <VideoBlock url={block.url} autoplay={block.autoplay} hover={hover} onChange={patch => onChange(patch)} />}
      {block.type === "link" && <LinkBlock label={block.label} url={block.url} hover={hover} onChange={patch => onChange(patch)} />}

      {/* Corner controls */}
      <button onClick={onRemove} title="Remove block" style={corner({ top: -8, left: -8, border: "1px solid #e0b4ad", color: "#c0392b" })}>✕</button>
      <button onClick={() => onMove(-1)} disabled={index === 0} title="Move up"
        style={corner({ top: -8, right: -8, border: `1px solid ${UM.line}`, color: "#5b4d3b", opacity: hover ? (index === 0 ? 0.3 : 1) : 0, cursor: index === 0 ? "default" : "pointer" })}>↑</button>
      <button onClick={() => onMove(1)} disabled={index === count - 1} title="Move down"
        style={corner({ bottom: -8, right: -8, border: `1px solid ${UM.line}`, color: "#5b4d3b", opacity: hover ? (index === count - 1 ? 0.3 : 1) : 0, cursor: index === count - 1 ? "default" : "pointer" })}>↓</button>
    </div>
  );
}

// Floating URL editor overlaid on the CENTRE of a media block, so editing the
// link never shifts the column / disrupts the layout. Shown when the block is
// being edited or when the URL is still empty.
function UrlOverlay({ icon, title, value, placeholder, onChange, onClose, dim, text, onText }) {
  const hasText = onText !== undefined;
  const labelStyle = { fontSize: 9, fontWeight: 800, letterSpacing: "0.08em", color: "#a08f78", margin: "0 0 5px" };
  const inputStyle = { width: "100%", boxSizing: "border-box", fontSize: 12, padding: "7px 9px", border: `1px solid ${UM.line}`, borderRadius: 7, outline: "none", background: "#fff" };
  return (
    <>
      {/* click-away */}
      <div onClick={onClose} style={{ position: "absolute", inset: 0, zIndex: 6, background: dim ? "rgba(20,30,45,0.5)" : "transparent", borderRadius: 12 }} />
      <div onClick={e => e.stopPropagation()} style={{
        position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 7,
        width: "min(86%, 300px)", background: "#fff", borderRadius: 10, padding: "10px 12px",
        boxShadow: "0 8px 26px rgba(0,0,0,0.4)", border: `1px solid ${UM.line}`,
      }}>
        {hasText && (
          <>
            <div style={labelStyle}>✎ BUTTON TEXT</div>
            <input autoFocus value={text} onChange={e => onText(e.target.value)} placeholder="Button label"
              onKeyDown={e => { if (e.key === "Enter") onClose(); }}
              style={{ ...inputStyle, marginBottom: 9 }} />
          </>
        )}
        <div style={labelStyle}>{icon} {title}</div>
        <div style={{ display: "flex", gap: 6 }}>
          <input autoFocus={!hasText} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
            onKeyDown={e => { if (e.key === "Enter") onClose(); }}
            style={{ ...inputStyle, flex: 1, minWidth: 0 }} />
          <button onClick={onClose} title="Done" style={{ border: "none", background: UM.navy, color: "#fff", borderRadius: 7, padding: "0 12px", cursor: "pointer", fontWeight: 800, fontSize: 13 }}>✓</button>
        </div>
      </div>
    </>
  );
}

// "Edit link" pill overlaid at the top of a media block (appears on hover).
function EditLinkButton({ label, onClick }) {
  return (
    <button onClick={onClick} title="Edit link"
      style={{
        position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)", zIndex: 5,
        background: "rgba(27,51,94,0.92)", color: "#fff", border: "none", borderRadius: 999,
        padding: "5px 12px", fontSize: 11, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap",
        boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
      }}>{label}</button>
  );
}

// Link box — the gold button (with inline-editable label). Hover reveals an
// "Edit link" pill; the URL editor opens centred ON TOP of the box (no shift).
function LinkBlock({ label, url, hover, onChange }) {
  const [editing, setEditing] = useState(false);
  const open = editing || !url;
  return (
    <div style={{ position: "relative" }}>
      <div style={{ background: UM.gold, borderRadius: 11, padding: "10px 14px", display: "flex", justifyContent: "center", outline: hover ? `2px solid ${UM.brown}` : "2px solid transparent", transition: "outline-color 120ms" }}>
        <input value={label} onChange={e => onChange({ label: e.target.value })} placeholder="Button label"
          style={{ border: "none", outline: "none", background: "transparent", textAlign: "center", color: UM.brown, fontWeight: 800, fontSize: 14, width: "100%" }} />
      </div>
      {hover && !open && <EditLinkButton label="🔗 Edit link" onClick={() => setEditing(true)} />}
      {open && (
        <UrlOverlay icon="🔗" title="LINK URL" value={url} placeholder="https://…"
          text={label} onText={v => onChange({ label: v })}
          onChange={v => onChange({ url: v })} onClose={() => setEditing(false)} dim={false} />
      )}
    </div>
  );
}

// Video — the embed/placeholder. Hover reveals an "Edit link" pill; the URL
// editor opens centred ON TOP of the video (no column shift). An "Auto-play"
// toggle sits below: when on, the video starts (muted) the moment the stop
// popup opens on the live tour. The editor preview never actually autoplays.
function VideoBlock({ url, autoplay, hover, onChange }) {
  const [editing, setEditing] = useState(false);
  const open = editing || !url;
  return (
    <div style={{ position: "relative" }}>
      {url
        ? <div style={{ borderRadius: 12, overflow: "hidden", background: "#000" }}><VideoEmbed url={url} /></div>
        : <div style={{ height: 120, display: "flex", alignItems: "center", justifyContent: "center", border: `2px dashed ${UM.line}`, borderRadius: 12, color: "#b6a892", fontSize: 12, fontWeight: 700 }}>🎬 Add a video</div>}
      {url && (
        <button onClick={() => onChange({ autoplay: !autoplay })} title="Auto-play this video (muted) when the stop opens"
          style={{
            marginTop: 6, display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 11px", borderRadius: 999,
            border: `1.5px solid ${autoplay ? UM.gold : UM.line}`, background: autoplay ? UM.gold : "#fff",
            color: autoplay ? UM.brown : "#6b5d4b", fontSize: 12, fontWeight: 800, cursor: "pointer",
          }}>
          {autoplay ? "▶ Auto-play: ON" : "▶ Auto-play: OFF"}
        </button>
      )}
      {hover && !open && <EditLinkButton label="🎬 Edit link" onClick={() => setEditing(true)} />}
      {open && (
        <UrlOverlay icon="🎬" title="VIDEO URL" value={url} placeholder="YouTube / mp4 URL"
          onChange={v => onChange({ url: v })} onClose={() => setEditing(false)} dim={!!url} />
      )}
    </div>
  );
}

// A divider line between body blocks. Hovering animates a centered [+] in; click
// it to choose which media type to insert at that position.
function InsertDivider({ onInsert }) {
  const [hover, setHover] = useState(false);
  const [menu, setMenu] = useState(false);
  const open = hover || menu;
  // Once the menu is open it stays open until an option is chosen or the user
  // clicks away (the fixed backdrop) — so the gap between the [+] and the menu
  // can never "close" it by losing hover.
  return (
    <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ position: "relative", height: open ? 30 : 16, transition: "height 120ms ease" }}>
      <div style={{ position: "absolute", top: "50%", left: 0, right: 0, borderTop: `1px dashed ${open ? UM.sky : UM.line}`, transition: "border-color 120ms" }} />
      <button onClick={() => setMenu(m => !m)} title="Insert media here"
        style={{
          position: "absolute", top: "50%", left: "50%", zIndex: 16,
          transform: `translate(-50%,-50%) scale(${open ? 1 : 0.55})`,
          opacity: open ? 1 : 0.28, transition: "transform 130ms ease, opacity 130ms ease",
          width: 24, height: 24, borderRadius: 7, border: "none", cursor: "pointer",
          background: UM.sky, color: "#fff", fontSize: 16, fontWeight: 800, lineHeight: 1,
          display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}>+</button>
      {menu && (
        <>
          {/* Click-away layer keeps the menu reachable across the visual gap */}
          <div onClick={() => { setMenu(false); setHover(false); }} style={{ position: "fixed", inset: 0, zIndex: 14 }} />
          <div style={{
            position: "absolute", top: "calc(50% + 14px)", left: "50%", transform: "translateX(-50%)",
            zIndex: 15, background: "#fff", borderRadius: 10, padding: 6, display: "flex", gap: 4,
            boxShadow: "0 6px 20px rgba(0,0,0,0.25)", border: `1px solid ${UM.line}`,
          }}>
            {BLOCK_TYPES.map(t => (
              <button key={t.type} onClick={() => { onInsert(t.type); setMenu(false); setHover(false); }}
                style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, padding: "6px 10px", border: "none", background: "transparent", cursor: "pointer", borderRadius: 7, color: UM.brown }}
                onMouseEnter={e => e.currentTarget.style.background = UM.sand}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <span style={{ fontSize: 16 }}>{t.emoji}</span>
                <span style={{ fontSize: 10, fontWeight: 800 }}>Add {t.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Click-or-drag image dropzone. Uploads to the dev endpoint and stores the path.
// A bottom grip lets you crop the height (drag down = taller); width is locked
// to 100% and the image is object-fit: cover, so only the visible height changes.
function ImageDrop({ src, height, onUploaded, onHeight }) {
  const inputRef = useRef(null);
  const imgRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [err, setErr] = useState(null); // null or an error message string
  const [resizing, setResizing] = useState(false);

  const handleFile = useCallback(async (file) => {
    if (!file || !file.type?.startsWith("image/")) { setErr("That's not an image file."); return; }
    setBusy(true); setErr(null);
    try {
      const path = await uploadImageFile(file);
      onUploaded(path);
    } catch (e) {
      setErr(e?.message || "Upload failed — try again.");
    } finally {
      setBusy(false);
    }
  }, [onUploaded]);

  const startResize = useCallback((e) => {
    e.preventDefault(); e.stopPropagation();
    const startY = e.clientY;
    const startH = height || imgRef.current?.offsetHeight || 200;
    setResizing(true);
    const move = (ev) => onHeight(Math.round(Math.max(60, Math.min(700, startH + (ev.clientY - startY)))));
    const up = () => { setResizing(false); window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  }, [height, onHeight]);

  return (
    <div
      onClick={() => { if (!resizing) inputRef.current?.click(); }}
      onDragOver={e => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={e => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files?.[0]); }}
      style={{
        position: "relative", cursor: resizing ? "ns-resize" : "pointer", borderRadius: 12, overflow: "hidden",
        border: `2px dashed ${drag ? UM.sky : (src ? "transparent" : UM.line)}`,
        background: src ? "#000" : (drag ? "#eaf6fc" : "#fbfaf7"),
        minHeight: src ? 0 : 110, display: "flex", alignItems: "center", justifyContent: "center",
      }}>
      {src
        ? <img ref={imgRef} src={src} alt="" style={{ width: "100%", height: height ? `${height}px` : "auto", maxHeight: height ? "none" : 240, objectFit: "cover", display: "block", opacity: busy ? 0.5 : 1 }} onError={e => { e.target.style.opacity = 0.3; }} />
        : <div style={{ textAlign: "center", color: busy ? UM.sky : "#a08f78", padding: 14 }}>
            <div style={{ fontSize: 24, marginBottom: 4 }}>{busy ? "⏳" : "🖼"}</div>
            <div style={{ fontSize: 12, fontWeight: 700 }}>{busy ? "Uploading…" : "Click or drag an image"}</div>
            {err && <div style={{ fontSize: 11, color: "#c0392b", marginTop: 4, maxWidth: 260 }}>{err}</div>}
          </div>}
      {src && (
        <div style={{ position: "absolute", top: 8, right: 8, background: "rgba(0,0,0,0.6)", color: "#fff", fontSize: 11, fontWeight: 700, padding: "4px 8px", borderRadius: 7, zIndex: 3 }}>
          {busy ? "Uploading…" : "Click to replace"}
        </div>
      )}
      {src && height ? (
        <div style={{ position: "absolute", bottom: 22, left: 8, background: "rgba(0,0,0,0.6)", color: "#fff", fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 6, zIndex: 3 }}>↕ {height}px · width locked</div>
      ) : null}
      {src && (
        <div onMouseDown={startResize} onClick={e => e.stopPropagation()} title="Drag to crop height (width locked)"
          style={{
            position: "absolute", left: 0, right: 0, bottom: 0, height: 20, cursor: "ns-resize", zIndex: 4,
            background: "linear-gradient(to top, rgba(0,0,0,0.55), rgba(0,0,0,0))",
            display: "flex", alignItems: "flex-end", justifyContent: "center", paddingBottom: 5,
          }}>
          <div style={{ width: 48, height: 5, borderRadius: 3, background: "rgba(255,255,255,0.92)" }} />
        </div>
      )}
      <input ref={inputRef} type="file" accept="image/*" style={{ display: "none" }}
        onChange={e => { handleFile(e.target.files?.[0]); e.target.value = ""; }} />
    </div>
  );
}

// Between-stops inserter for the sidebar. Hovering animates a [+] in (like the
// body editor); clicking it opens a small room picker that inserts a new stop
// at this position.
// ── Room assignment ───────────────────────────────────────────────────────────
// The label IS the room code, so changing the room is how an editor changes
// the label — from a list of rooms that exist, never by typing. Each row shows
// the room's preset name beside its code, because "ENGAP is 442, not 433" is
// something an editor knows by the name and not the number.
function RoomAssignPicker({ stop, code, choices, unresolved, onAssign }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const current = stop?.nodeId
    ? choices.find(c => c.spot && c.spot.nodeId === stop.nodeId)
    : choices.find(c => !c.spot && normalizeBuilding(c.building) === normalizeBuilding(stop?.building) && String(c.room) === String(stop?.room));
  const list = q.trim() ? filterRoomChoices(choices, q, 10) : choices.slice(0, 10);
  const pick = (c) => { onAssign?.(c); setOpen(false); setQ(""); };
  return (
    <div style={{ position: "relative" }}>
      <button type="button" onClick={() => setOpen(o => !o)} disabled={!onAssign} title={onAssign ? "Change the room this stop navigates to" : undefined}
        style={{ width: "100%", textAlign: "left", display: "flex", alignItems: "center", gap: 10, fontSize: 13, fontWeight: 700, color: unresolved ? "#b71c1c" : "#6b5d4b", padding: "8px 12px", background: unresolved ? "#fdecea" : "#f6f3ee", borderRadius: 8, border: `1px solid ${unresolved ? "#f3b4ad" : UM.line}`, cursor: onAssign ? "pointer" : "default" }}>
        <span style={{ fontFamily: SERIF, fontSize: 14, color: unresolved ? "#b71c1c" : UM.brown }}>{code}</span>
        <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 600, color: "#8a7a66" }}>
          {unresolved ? (stop?.nodeId ? "pinned spot — nav point missing" : "not a room on the map — pick one") : (current?.name || (stop?.nodeId ? "pinned spot" : ""))}
        </span>
        {onAssign && <span style={{ fontSize: 11, color: UM.sky, fontWeight: 800 }}>{open ? "▲" : "▼ change"}</span>}
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 8 }} />
          <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 9, background: "#fff", borderRadius: 10, padding: 8, boxShadow: "0 8px 26px rgba(0,0,0,0.25)", border: `1px solid ${UM.line}` }}>
            <input autoFocus value={q} onChange={e => setQ(e.target.value)}
              onKeyDown={e => { if (e.key === "Escape") setOpen(false); if (e.key === "Enter" && list.length === 1) pick(list[0]); }}
              placeholder="Room number, name, or a pinned spot"
              style={{ width: "100%", boxSizing: "border-box", fontSize: 12.5, padding: "7px 9px", border: `1px solid ${UM.line}`, borderRadius: 7, outline: "none" }} />
            <div style={{ marginTop: 6, maxHeight: 220, overflowY: "auto", border: "1px solid #eee", borderRadius: 7 }}>
              {list.length === 0 && <div style={{ padding: "10px 9px", fontSize: 12, color: "#999" }}>No room matches.</div>}
              {list.map(c => (
                <button key={c.code} type="button" onMouseDown={e => e.preventDefault()} onClick={() => pick(c)}
                  style={{ display: "flex", gap: 10, alignItems: "baseline", width: "100%", textAlign: "left", padding: "7px 9px", border: "none", borderBottom: "1px solid #f2f2f2", background: c === current ? "#fff8e6" : "#fff", cursor: "pointer" }}>
                  <span style={{ fontSize: 12.5, fontWeight: 800, color: "#333", flexShrink: 0 }}>{c.code}</span>
                  <span style={{ fontSize: 12, color: "#8a7a66", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name || ""}</span>
                </button>
              ))}
            </div>
            {!q.trim() && choices.length > 10 && <div style={{ fontSize: 10, color: "#999", marginTop: 5 }}>Showing 10 of {choices.length} — type to narrow it down.</div>}
          </div>
        </>
      )}
    </div>
  );
}

function StopInsertDivider({ index, onSubmit, roomChoices = [] }) {
  const [hover, setHover] = useState(false);
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState("");
  const [err, setErr] = useState(null);
  const show = hover || open;
  const submit = (codeStr) => {
    const r = onSubmit(index, codeStr != null ? codeStr : val);
    if (r.ok) { setVal(""); setErr(null); setOpen(false); setHover(false); }
    else setErr(r.msg);
  };
  // Controlled suggestions (replaces the native datalist) — by code ignoring
  // case, spaces and dashes ("e2213" finds E2-213) or by the room's name.
  const suggestions = filterRoomChoices(roomChoices, val, 8);
  return (
    <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ position: "relative", height: show ? 22 : 10, transition: "height 120ms ease" }}>
      <div style={{ position: "absolute", left: 14, right: 14, top: "50%", borderTop: `1px dashed ${show ? "#6a1b9a" : "#ececec"}`, transition: "border-color 120ms" }} />
      <button onClick={() => setOpen(o => !o)} title="Insert a stop here"
        style={{
          position: "absolute", top: "50%", left: "50%", zIndex: 4,
          transform: `translate(-50%,-50%) scale(${show ? 1 : 0.5})`,
          opacity: show ? 1 : 0.22, transition: "transform 130ms ease, opacity 130ms ease",
          width: 20, height: 20, borderRadius: 6, border: "none", cursor: "pointer",
          background: "#6a1b9a", color: "#fff", fontSize: 14, fontWeight: 800, lineHeight: 1,
          display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 7px rgba(0,0,0,0.25)",
        }}>+</button>
      {open && (
        <>
          <div onClick={() => { setOpen(false); setHover(false); }} style={{ position: "fixed", inset: 0, zIndex: 8 }} />
          {/* Fully controlled dropdown — no native <datalist>, whose popup
              mis-positions/flickers inside scrollable/positioned containers. */}
          <div style={{
            position: "absolute", top: "calc(50% + 12px)", left: "50%", marginLeft: -122,
            zIndex: 9, background: "#fff", borderRadius: 8, padding: 8, width: 244, boxSizing: "border-box",
            boxShadow: "0 6px 20px rgba(0,0,0,0.25)", border: "1px solid #e0e0e0",
          }}>
            <div style={{ fontSize: 9, fontWeight: 800, color: "#888", marginBottom: 5, letterSpacing: "0.06em" }}>INSERT STOP HERE</div>
            <div style={{ display: "flex", gap: 5 }}>
              <input autoFocus value={val}
                onChange={e => { setVal(e.target.value); setErr(null); }}
                onKeyDown={e => { if (e.key === "Enter") submit(); }}
                placeholder='Room number or name — e.g. "E2-442"'
                style={{ flex: 1, minWidth: 0, boxSizing: "border-box", fontSize: 12, padding: "6px 8px", border: "1px solid #ccc", borderRadius: 6, outline: "none" }} />
              <button onClick={() => submit()} style={{ fontSize: 11, fontWeight: 800, padding: "0 12px", borderRadius: 6, border: "none", background: "#6a1b9a", color: "#fff", cursor: "pointer" }}>Add</button>
            </div>
            {suggestions.length > 0 && (
              <div style={{ marginTop: 6, maxHeight: 172, overflowY: "auto", border: "1px solid #eee", borderRadius: 6 }}>
                {suggestions.map(o => (
                  <button key={o.code} onMouseDown={e => e.preventDefault()} onClick={() => submit(o.code)}
                    style={{ display: "flex", gap: 8, alignItems: "baseline", width: "100%", textAlign: "left", padding: "6px 9px", border: "none", borderBottom: "1px solid #f2f2f2", background: "#fff", cursor: "pointer" }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#333", flexShrink: 0 }}>{o.code}</span>
                    <span style={{ fontSize: 11, color: "#8a7a66", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.name || ""}</span>
                  </button>
                ))}
              </div>
            )}
            {err && <div style={{ fontSize: 10, color: "#e53935", marginTop: 4 }}>{err}</div>}
          </div>
        </>
      )}
    </div>
  );
}


// ── Stop bank picker ──────────────────────────────────────────────────────────
// Every place a tour could stop at: the stops already written (with their
// content) AND every room the campus has a name for, listed as empty until
// somebody writes it up. Each row says which it is, so "is there already a
// stop for the Dean's Office" is answered by looking rather than by searching
// three tours. Picking a written stop appends a copy (minus "What's Next");
// picking an empty room adds a fresh stop titled with the room's name.
const stopHasContent = (s) => !!(String(s?.title || "").trim() || String(s?.subtitle || "").trim() || String(s?.text || "").trim()
  || (Array.isArray(s?.images) && s.images.some(Boolean)) || s?.videoUrl || String(s?.quote || "").trim());
function StopBankPopup({ bank, roomChoices = [], onPick, onPickRoom, onEdit, onDelete, onClose }) {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | written | empty
  const query = q.trim().toLowerCase();
  const inBank = new Set((bank || []).map(s => `${normalizeBuilding(s.building)}::${String(s.room || "").toLowerCase()}`));
  const rows = [
    ...(bank || []).map(s => ({ kind: "bank", key: bankKey(s), entry: s, name: s.title || stopCode(s), code: stopCode(s), written: stopHasContent(s) })),
    ...roomChoices
      .filter(c => c.name && !inBank.has(`${normalizeBuilding(c.building)}::${String(c.room).toLowerCase()}`))
      .map(c => ({ kind: "room", key: `room:${c.code}`, choice: c, name: c.name, code: c.code, written: false })),
  ];
  const items = rows.filter(r => {
    if (filter === "written" && !r.written) return false;
    if (filter === "empty" && r.written) return false;
    if (!query) return true;
    return `${r.name} ${r.entry?.subtitle || ""} ${r.code}`.toLowerCase().includes(query);
  });
  const written = rows.filter(r => r.written).length;
  const iconBtn = (extra) => ({ flexShrink: 0, width: 34, borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", ...extra });
  const chip = (id, text) => (
    <button key={id} onClick={() => setFilter(id)} style={{ fontSize: 10.5, fontWeight: 800, padding: "4px 10px", borderRadius: 999, border: `1.5px solid ${filter === id ? UM.gold : UM.line}`, background: filter === id ? UM.gold : "#fff", color: filter === id ? UM.brown : "#6b5d4b", cursor: "pointer" }}>{text}</button>
  );
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 62, background: "rgba(10,18,30,0.62)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: "min(600px, 100%)", height: "82vh", display: "flex", flexDirection: "column", background: UM.sand, borderRadius: 16, overflow: "hidden", boxShadow: "0 24px 70px rgba(0,0,0,0.55)" }}>
        <div style={{ flexShrink: 0, background: UM.navy, color: "#fff", padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `3px solid ${UM.gold}` }}>
          <div style={{ fontFamily: SERIF, fontSize: 15, fontWeight: 800 }}>Stop bank · {written} written, {rows.length - written} empty</div>
          <button onClick={onClose} title="Close" style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", width: 28, height: 28, borderRadius: 7, cursor: "pointer", fontSize: 14, fontWeight: 800 }}>✕</button>
        </div>
        <div style={{ flexShrink: 0, padding: "10px 14px 6px" }}>
          <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name or room — e.g. ENGAP, E2-442"
            style={{ width: "100%", boxSizing: "border-box", fontSize: 13, padding: "8px 10px", border: `1px solid ${UM.line}`, borderRadius: 8, outline: "none", background: "#fff" }} />
          <div style={{ display: "flex", gap: 6, marginTop: 8, alignItems: "center" }}>
            {chip("all", `All (${rows.length})`)}{chip("written", `Has stop content (${written})`)}{chip("empty", `Empty (${rows.length - written})`)}
          </div>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "0 12px 14px" }}>
          {items.length === 0 && (
            <div style={{ padding: "24px 12px", textAlign: "center", color: "#8a7a66", fontSize: 13 }}>
              {rows.length ? "Nothing matches your search." : "No stops yet — click a room on the map to make the first one."}
            </div>
          )}
          {items.map((r) => {
            const s = r.entry;
            const badge = r.written
              ? { text: "HAS STOP CONTENT", bg: "#e8f5e9", fg: "#2e7d32" }
              : { text: "EMPTY", bg: "#f3f3f3", fg: "#888" };
            return (
              <div key={r.key} style={{ display: "flex", alignItems: "stretch", gap: 6, marginTop: 8 }}>
                <button onClick={() => (s ? onPick(s) : onPickRoom?.(r.choice))} title={s ? "Add this stop to the current tour" : "Add this room as a new stop (titled with its name)"}
                  style={{ flex: 1, minWidth: 0, textAlign: "left", background: "#fff", border: `1px solid ${UM.line}`, borderRadius: 10, padding: "10px 12px", cursor: "pointer" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0, fontFamily: SERIF, fontSize: 14, fontWeight: 800, color: r.written ? UM.brown : "#6b5d4b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</div>
                    <span style={{ flexShrink: 0, fontSize: 9, fontWeight: 800, letterSpacing: "0.06em", padding: "2px 7px", borderRadius: 999, background: badge.bg, color: badge.fg }}>{badge.text}</span>
                  </div>
                  <div style={{ fontSize: 11, color: "#8a7a66", marginTop: 2 }}>{r.code}{s?.subtitle ? ` · ${s.subtitle}` : ""}</div>
                  {(s?.departments || []).length > 0 && (
                    <div style={{ fontSize: 10, color: UM.sky, marginTop: 3, fontWeight: 700 }}>{s.departments.join(", ")}</div>
                  )}
                </button>
                {s && <button onClick={() => onEdit(s)} title="Edit this stop" style={iconBtn({ border: `1px solid ${UM.line}`, background: "#fff", color: "#5b4d3b" })}>✎</button>}
                {s && <button onClick={() => { if (window.confirm(`Remove "${r.name}" from the bank?`)) onDelete(s); }} title="Remove from bank" style={iconBtn({ width: "auto", padding: "0 10px", fontSize: 11, border: "1px solid #e0b4ad", background: "#fff", color: "#c0392b" })}>Delete</button>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Restore from a backup ────────────────────────────────────────────────────
// An Export zip (or a bare tours.json) is opened IN THE BROWSER — nothing is
// uploaded — and its stops are listed against what the editor has now, so the
// one that was overwritten can be put back on its own, rather than the whole
// document being rolled back over everything saved since.
function RestoreModal({ current, onApply, onClose }) {
  const [sources, setSources] = useState(null);   // [{ id, label, ts, data }]
  const [sourceId, setSourceId] = useState(null);
  const [picks, setPicks] = useState(() => new Set());
  const [targets, setTargets] = useState({});  // backup key → current key to put it into ("" = add to bank)
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [fileName, setFileName] = useState("");
  const source = (sources || []).find(s => s.id === sourceId) || null;
  const rows = useMemo(() => (source ? restorableStops(source.data) : []), [source]);
  const nowRows = useMemo(() => restorableStops(current || {}), [current]);
  const nowByKey = useMemo(() => new Map(nowRows.map(r => [r.key, r])), [nowRows]);
  const targetFor = (r) => (r.key in targets ? targets[r.key] : (nowByKey.has(r.key) ? r.key : ""));
  const query = q.trim().toLowerCase();
  const shown = rows.filter(r => !query || `${r.code} ${r.title}`.toLowerCase().includes(query));

  const openFile = async (file) => {
    if (!file) return;
    setBusy(true); setErr(null); setPicks(new Set()); setFileName(file.name);
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      let list;
      if (/\.zip$/i.test(file.name) || (bytes[0] === 0x50 && bytes[1] === 0x4b)) {
        const entries = await readZip(bytes);
        const dec = new TextDecoder();
        list = backupSources(entries.filter(e => /\.json$/i.test(e.path)).map(e => ({ path: e.path, text: dec.decode(e.data) })));
      } else {
        list = backupSources(JSON.parse(new TextDecoder().decode(bytes)));
      }
      if (!list.length) throw new Error("No tour data found in that file. Use the zip from ⬇ Export, or a tours.json.");
      setSources(list); setSourceId(list[0].id);
    } catch (e) {
      setSources(null); setSourceId(null);
      setErr(e?.message || "Could not read that file.");
    } finally { setBusy(false); }
  };
  const toggle = (key) => setPicks(p => { const n = new Set(p); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const picked = rows.filter(r => picks.has(r.key));
  const describe = (r) => {
    const tk = targetFor(r);
    const now = tk ? nowByKey.get(tk) : null;
    if (!now) return { text: nowByKey.has(r.key) ? "will be added to the bank" : "not in your data now — choose a stop to put it into, or add it to the bank", color: "#0277bd" };
    const d = diffContent(now.stop, r.stop);
    if (!d.length) return { text: `same as ${now.code} now`, color: "#999" };
    return { text: `${now.code} differs now: ${d.join(", ")}`, color: "#b26a00" };
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 66, background: "rgba(10,18,30,0.62)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: "min(640px, 100%)", maxHeight: "88vh", display: "flex", flexDirection: "column", background: UM.sand, borderRadius: 16, overflow: "hidden", boxShadow: "0 24px 70px rgba(0,0,0,0.55)" }}>
        <div style={{ flexShrink: 0, background: UM.navy, color: "#fff", padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `3px solid ${UM.gold}` }}>
          <div style={{ fontFamily: SERIF, fontSize: 15, fontWeight: 800 }}>⤴ Restore a stop from a backup</div>
          <button onClick={onClose} title="Close" style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", width: 28, height: 28, borderRadius: 7, cursor: "pointer", fontSize: 14, fontWeight: 800 }}>✕</button>
        </div>
        <div style={{ flexShrink: 0, padding: "12px 16px 8px", fontSize: 12, color: "#5b4d3b", lineHeight: 1.5 }}>
          Pick the zip from <b>⬇ Export</b> (or a <code>tours.json</code>). It is read here in your browser. Tick the stops to put back: their text, photos, video, quote and departments return; where they sit in the tour, their room and their What's Next stay as they are now.
          <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 10 }}>
            <label style={{ fontSize: 12, fontWeight: 800, padding: "8px 14px", borderRadius: 8, border: "none", background: UM.navy, color: "#fff", cursor: "pointer" }}>
              {busy ? "Reading…" : "Choose file…"}
              <input type="file" accept=".zip,.json,application/zip,application/json" onChange={e => openFile(e.target.files?.[0])} style={{ display: "none" }} />
            </label>
            <span style={{ fontSize: 11, color: "#8a7a66", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{fileName}</span>
          </div>
          {err && <div style={{ marginTop: 8, padding: "7px 10px", borderRadius: 8, background: "#fdecea", color: "#b71c1c", fontWeight: 700 }}>⚠ {err}</div>}
        </div>
        {sources && (
          <>
            <div style={{ flexShrink: 0, padding: "0 16px 8px", display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <select value={sourceId || ""} onChange={e => { setSourceId(e.target.value); setPicks(new Set()); }}
                style={{ flex: "1 1 260px", fontSize: 12, padding: "7px 9px", border: `1px solid ${UM.line}`, borderRadius: 8, background: "#fff" }}>
                {sources.map(sv => <option key={sv.id} value={sv.id}>{sv.label}</option>)}
              </select>
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter by room or title"
                style={{ flex: "1 1 160px", fontSize: 12, padding: "7px 9px", border: `1px solid ${UM.line}`, borderRadius: 8, background: "#fff", outline: "none" }} />
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "0 12px 8px" }}>
              {shown.length === 0 && <div style={{ padding: "18px 12px", textAlign: "center", color: "#8a7a66", fontSize: 12 }}>No stops match.</div>}
              {shown.map(r => {
                const on = picks.has(r.key);
                const d = describe(r);
                return (
                  <label key={r.key} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "9px 10px", marginTop: 6, borderRadius: 10, background: on ? "#fff8e6" : "#fff", border: `1px solid ${on ? UM.gold : UM.line}`, cursor: "pointer" }}>
                    <input type="checkbox" checked={on} onChange={() => toggle(r.key)} style={{ marginTop: 3 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                        <span style={{ fontSize: 12.5, fontWeight: 800, color: "#333", flexShrink: 0 }}>{r.code}</span>
                        <span style={{ fontFamily: SERIF, fontSize: 13.5, fontWeight: 800, color: r.hasContent ? UM.brown : "#999", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.title || <i>untitled</i>}</span>
                      </div>
                      <div style={{ fontSize: 10.5, color: d.color, marginTop: 2 }}>{d.text}{r.where?.length ? ` · in backup: ${r.where.join(", ")}` : ""}</div>
                      {on && (
                        <div onClick={e => e.preventDefault()} style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
                          <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", color: "#a08f78" }}>PUT INTO</span>
                          {/* The room a stop lives in can have changed since the backup —
                              a name in the room field then, a number now — so the editor
                              says which current stop receives the content. */}
                          <select value={targetFor(r)} onChange={e => setTargets(t => ({ ...t, [r.key]: e.target.value }))}
                            style={{ flex: 1, fontSize: 11.5, padding: "4px 6px", border: `1px solid ${UM.line}`, borderRadius: 6, background: "#fff" }}>
                            <option value="">Add to the bank as {r.code}</option>
                            {nowRows.map(n => <option key={n.key} value={n.key}>{n.code}{n.title ? ` · ${n.title}` : ""}{n.key === r.key ? " (same room)" : ""}</option>)}
                          </select>
                        </div>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
            <div style={{ flexShrink: 0, padding: "10px 16px 14px", display: "flex", gap: 8, alignItems: "center", borderTop: `1px solid ${UM.line}` }}>
              <span style={{ flex: 1, fontSize: 11, color: "#8a7a66" }}>{picked.length ? `${picked.length} stop${picked.length === 1 ? "" : "s"} selected` : "Nothing selected yet"}</span>
              <button onClick={onClose} style={{ fontSize: 12, fontWeight: 800, padding: "9px 14px", borderRadius: 9, border: `1px solid ${UM.line}`, background: "#fff", color: "#6b5d4b", cursor: "pointer" }}>Cancel</button>
              <button onClick={() => onApply(picked.map(r => ({ key: r.key, stop: r.stop, targetKey: targetFor(r) })))} disabled={!picked.length}
                style={{ fontSize: 13, fontWeight: 900, padding: "9px 18px", borderRadius: 9, border: "none", background: picked.length ? UM.gold : "#e8e2d6", color: picked.length ? UM.brown : "#9a8f80", cursor: picked.length ? "pointer" : "default" }}>
                Restore {picked.length || ""} stop{picked.length === 1 ? "" : "s"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Route Adjuster panel ─────────────────────────────────────────────────────
// The legs of the active tour, one per stop, each saying whether the tour
// walks an automatic path or one drawn by hand — for the mode on screen. Pick
// a leg to see it on the map; "Draw by hand" turns the map into the pen.
function RoutesPanel({ tour, legInfo, graphReady, routeMode, onMode, routeLeg, onSelectLeg, routeDraw, onBeginDraw, onReset, onUndo, onCancel, onFinish, canFinish, onChooseFloor, nodeFloor }) {
  const color = ROUTE_MODES[routeMode].color;
  const section = { fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: '#888' };
  const nameOf = (stop, i) => (stop ? `${i + 1} · ${stopCode(stop)}` : 'Start');
  if (!tour) return <div style={{ padding: '22px 16px', fontSize: 12, color: '#999', textAlign: 'center', lineHeight: 1.5 }}>Pick a tour above the map to adjust its routes.</div>;
  if (!graphReady || !legInfo) return <div style={{ padding: 20, fontSize: 12, color: '#aaa', textAlign: 'center' }}>Loading the walking network…</div>;

  // Drawing: the sidebar becomes the instructions and the controls.
  if (routeDraw) {
    const info = legOf(legInfo, routeDraw.idx);
    const dest = info?.leg.toStop;
    const here = routeDraw.ids[routeDraw.ids.length - 1];
    const f = nodeFloor(here);
    return (
      <div style={{ padding: '12px 14px' }}>
        <div style={{ ...section, color }}>DRAWING · {ROUTE_MODES[routeDraw.mode].label.toUpperCase()} ROUTE</div>
        <div style={{ fontSize: 13, fontWeight: 800, color: '#222', margin: '6px 0 2px' }}>{nameOf(info?.leg.fromStop, routeDraw.idx - 1)} → {nameOf(dest, routeDraw.idx)}</div>
        <div style={{ fontSize: 11, color: '#666', lineHeight: 1.5, marginBottom: 10 }}>
          The path from your last point to the corridor point nearest the cursor follows the mouse. <b>Click</b> to set it. Click a <b>stair</b> or <b>elevator</b> dot to change floors. Finish at a door of <b>{dest ? stopCode(dest) : 'the stop'}</b> (gold dots).
          {routeDraw.mode === 'a11y' && <> Accessible routes cannot use stairs.</>}
        </div>
        {routeDraw.shaft && (
          <div style={{ border: `1.5px solid ${color}`, borderRadius: 10, padding: '10px 12px', marginBottom: 10, background: '#fff' }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#222', marginBottom: 6 }}>
              {routeDraw.shaft.type === 'stair' ? 'Take the stairs' : 'Take the elevator'} to floor…
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {routeDraw.shaft.options.map(o => (
                <button key={o.floor} onClick={() => onChooseFloor(o)} style={{ fontSize: 13, fontWeight: 800, width: 38, height: 34, borderRadius: 8, border: `1.5px solid ${color}`, background: '#fff', color, cursor: 'pointer' }}>{o.floor}</button>
              ))}
              <button onClick={() => onChooseFloor(null)} style={{ fontSize: 11, fontWeight: 700, padding: '0 10px', height: 34, borderRadius: 8, border: '1px solid #ccc', background: '#fff', color: '#555', cursor: 'pointer' }}>Stay on {f}</button>
            </div>
          </div>
        )}
        <div style={{ fontSize: 11, color: '#888', marginBottom: 10 }}>
          {routeDraw.ids.length - 1} node{routeDraw.ids.length === 2 ? '' : 's'} so far · on floor {f}
          {canFinish && <b style={{ color: '#2e7d32' }}> · at the destination</b>}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={onUndo} disabled={routeDraw.marks.length < 2} style={{ flex: 1, fontSize: 11, fontWeight: 800, padding: '8px 0', borderRadius: 7, border: '1px solid #ccc', background: '#fff', color: '#333', cursor: routeDraw.marks.length < 2 ? 'default' : 'pointer', opacity: routeDraw.marks.length < 2 ? 0.5 : 1 }}>↶ Undo</button>
          <button onClick={onCancel} style={{ flex: 1, fontSize: 11, fontWeight: 800, padding: '8px 0', borderRadius: 7, border: '1px solid #e0b4ad', background: '#fff', color: '#c0392b', cursor: 'pointer' }}>Cancel</button>
          <button onClick={onFinish} disabled={!canFinish} title={canFinish ? 'Save this drawing for the leg' : 'Reach one of the destination doors first'} style={{ flex: 1.4, fontSize: 11, fontWeight: 900, padding: '8px 0', borderRadius: 7, border: 'none', background: canFinish ? UM.gold : '#e8e2d6', color: canFinish ? UM.brown : '#9a8f80', cursor: canFinish ? 'pointer' : 'default' }}>✓ Finish</button>
        </div>
        <div style={{ fontSize: 10, color: '#aaa', marginTop: 10 }}>Esc cancels. The drawing is kept when you press Save in the top bar.</div>
      </div>
    );
  }

  return (
    <>
      <div style={{ padding: '10px 14px 6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={section}>ROUTES ({legInfo.length})</span>
        <div style={{ display: 'flex', gap: 4 }}>
          {MODE_IDS.map(m => (
            <button key={m} onClick={() => onMode(m)} style={{ fontSize: 10, fontWeight: 800, padding: '4px 9px', borderRadius: 999, border: `1.5px solid ${ROUTE_MODES[m].color}`, background: routeMode === m ? ROUTE_MODES[m].color : '#fff', color: routeMode === m ? '#fff' : ROUTE_MODES[m].color, cursor: 'pointer' }}>{ROUTE_MODES[m].label}</button>
          ))}
        </div>
      </div>
      <div style={{ fontSize: 11, color: '#999', padding: '0 14px 8px', lineHeight: 1.45 }}>
        The tour starts at stop 1 (“Start Here!”). Each leg between stops is walked automatically unless drawn by hand. Pick one to see it; the map shows the corridors on the floor you're viewing.
      </div>
      {legInfo.length === 0 && <div style={{ padding: '14px', fontSize: 12, color: '#bbb', textAlign: 'center', lineHeight: 1.5 }}>{(tour.stops || []).length ? 'Visitors start at the first stop. Add a second stop and the walk between them appears here.' : 'This tour has no stops yet.'}</div>}
      {legInfo.map((info) => {
        const on = routeLeg === info.idx;
        const manual = info.kind === 'manual';
        return (
          <div key={info.idx} onClick={() => onSelectLeg(info.idx)}
            style={{ padding: '9px 14px', cursor: 'pointer', background: on ? '#fff8e6' : 'none', borderLeft: on ? `3px solid ${color}` : '3px solid transparent', borderBottom: '1px solid #f3f3f3' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {nameOf(info.leg.fromStop, info.idx - 1)} <span style={{ color: '#aaa' }}>→</span> {nameOf(info.leg.toStop, info.idx)}
                </div>
                <div style={{ fontSize: 10, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {info.leg.toStop?.title || ''}{info.floors.length ? ` · floor${info.floors.length > 1 ? 's' : ''} ${info.floors.join(' → ')}` : ''}
                </div>
              </div>
              <span style={{ flexShrink: 0, fontSize: 9, fontWeight: 800, letterSpacing: '0.06em', padding: '3px 8px', borderRadius: 999, background: manual ? color : '#eee', color: manual ? '#fff' : '#666' }}>
                {manual ? 'HAND-DRAWN' : 'AUTOMATIC'}
              </span>
            </div>
            {manual && info.startMoved && <div style={{ fontSize: 10, color: '#b26a00', marginTop: 4 }}>⚠ An earlier leg now arrives at a different door — the tour walks to this drawing's start first. Redraw to tidy up.</div>}
            {manual && info.entry?.by && <div style={{ fontSize: 10, color: '#999', marginTop: 3 }}>drawn by {info.entry.by}</div>}
            {!manual && info.invalid && <div style={{ fontSize: 10, color: '#c62828', marginTop: 4 }}>⚠ A hand-drawn route exists but can't be used ({info.invalid}) — automatic for now. Redraw it.</div>}
            {!manual && info.failed && !info.invalid && <div style={{ fontSize: 10, color: '#c62828', marginTop: 4 }}>⚠ No automatic route found{info.destIds.length ? '' : ' — the stop has no door on the map'}.</div>}
            {on && (
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <button onClick={e => { e.stopPropagation(); onBeginDraw(info.idx); }} disabled={!info.destIds.length}
                  title={info.destIds.length ? 'Draw this leg by hand, click by click' : 'The stop has no door to finish at'}
                  style={{ flex: 1, fontSize: 11, fontWeight: 800, padding: '7px 0', borderRadius: 7, border: 'none', background: info.destIds.length ? color : '#ddd', color: '#fff', cursor: info.destIds.length ? 'pointer' : 'default' }}>
                  ✎ {manual || info.invalid ? 'Redraw by hand' : 'Draw by hand'}
                </button>
                {(manual || info.invalid) && (
                  <button onClick={e => { e.stopPropagation(); onReset(info.idx, routeMode); }} title="Forget the drawing and route this leg automatically"
                    style={{ flex: 1, fontSize: 11, fontWeight: 800, padding: '7px 0', borderRadius: 7, border: '1px solid #ccc', background: '#fff', color: '#333', cursor: 'pointer' }}>Back to automatic</button>
                )}
              </div>
            )}
          </div>
        );
      })}
      <div style={{ padding: '10px 14px', fontSize: 10, color: '#999', lineHeight: 1.5 }}>
        <span style={{ display: 'inline-block', width: 18, height: 3, background: ROUTE_MODES.std.color, verticalAlign: 'middle', marginRight: 6 }} />Normal route&nbsp;&nbsp;
        <span style={{ display: 'inline-block', width: 18, height: 3, background: ROUTE_MODES.a11y.color, verticalAlign: 'middle', marginRight: 6 }} />Accessible route<br />
        <span style={{ display: 'inline-block', width: 18, height: 2, background: '#5b6470', verticalAlign: 'middle', marginRight: 6 }} />Corridors on this floor&nbsp;&nbsp;
        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 4, background: '#a0724a', verticalAlign: 'middle', marginRight: 4 }} />Stairs&nbsp;
        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 4, background: '#607d8b', verticalAlign: 'middle', marginRight: 4 }} />Elevator
      </div>
    </>
  );
}

// ── Tour info editor ─────────────────────────────────────────────────────────
// Title, subtitle and description — what the tour picker shows a visitor.
// Edits apply live (the panel above the map shows them as you type) and are
// persisted by the top-bar Save, like every other edit.
function TourInfoModal({ tour, onUpdate, onDelete, onClose }) {
  const field = { width: "100%", boxSizing: "border-box", fontSize: 14, padding: "9px 11px", border: `1px solid ${UM.line}`, borderRadius: 8, outline: "none", background: "#fff", fontFamily: SANS };
  const label = { fontSize: 9, fontWeight: 800, letterSpacing: "0.08em", color: "#a08f78", margin: "12px 0 5px" };
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(10,18,30,0.62)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: "min(520px, 100%)", background: UM.cream, borderRadius: 16, overflow: "hidden", boxShadow: "0 24px 70px rgba(0,0,0,0.55)" }}>
        <div style={{ background: UM.navy, color: "#fff", padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `3px solid ${UM.gold}` }}>
          <div>
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.08em", color: "rgba(255,255,255,0.6)" }}>TOUR {tour.id}</div>
            <div style={{ fontFamily: SERIF, fontSize: 15, fontWeight: 800 }}>{tour.title || "Untitled tour"}</div>
          </div>
          <button onClick={onClose} title="Done" style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", width: 28, height: 28, borderRadius: 7, cursor: "pointer", fontSize: 14, fontWeight: 800 }}>✕</button>
        </div>
        <div style={{ padding: "6px 18px 18px" }}>
          <div style={label}>TITLE</div>
          <input autoFocus value={tour.title || ""} onChange={e => onUpdate({ title: e.target.value })} placeholder="e.g. Mechanical Engineering Tour" style={{ ...field, fontFamily: SERIF, fontWeight: 800, color: UM.brown }} />
          <div style={label}>SUBTITLE</div>
          <input value={tour.subtitle || ""} onChange={e => onUpdate({ subtitle: e.target.value })} placeholder="One line under the title (optional)" style={field} />
          <div style={label}>DESCRIPTION</div>
          <textarea value={tour.description || ""} onChange={e => onUpdate({ description: e.target.value })} rows={4} placeholder="What a visitor reads when picking this tour" style={{ ...field, resize: "vertical", lineHeight: 1.5 }} />
          <div style={{ display: "flex", gap: 10, marginTop: 18, alignItems: "center" }}>
            <button onClick={onDelete} title="Delete this entire tour" style={{ fontSize: 12, fontWeight: 800, padding: "9px 14px", borderRadius: 9, border: "1px solid #e0b4ad", background: "#fff", color: "#c0392b", cursor: "pointer" }}>Delete tour</button>
            <span style={{ flex: 1, fontSize: 11, color: "#8a7a66" }}>Changes show above the map at once; press Save in the top bar to publish them.</span>
            <button onClick={onClose} style={{ fontSize: 13, fontWeight: 900, padding: "9px 18px", borderRadius: 9, border: "none", background: UM.gold, color: UM.brown, cursor: "pointer" }}>Done</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Free Roam visibility button ───────────────────────────────────────────────
// One button, three states (src/utils/engtourBank.js): Don't show → Show always →
// Show zoomed. Replaces the SHOW + "⭐ Primary landmark" pair, which read as unclear.
const ROAM_STYLE = {
  off: { border: '#ccc', background: '#fff', color: '#888' },
  always: { border: '#e0a800', background: '#FFB81C', color: '#4A1E13' },
  zoomed: { border: '#e0a800', background: '#fff6dc', color: '#7a5600' },
};
function FreeRoamModeButton({ mode, onClick, small = false }) {
  const m = FREE_ROAM_MODES.find(x => x.mode === mode) || FREE_ROAM_MODES[0];
  const st = ROAM_STYLE[m.mode];
  return (
    <button onClick={onClick} title={`${m.label}: ${m.hint} Click to change.`} aria-label={`Free Roam: ${m.label}. Click to change.`}
      style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5, minWidth: small ? 98 : 130,
        padding: small ? '4px 9px' : '6px 14px', borderRadius: 999, cursor: 'pointer', whiteSpace: 'nowrap',
        fontSize: small ? 10.5 : 12, fontWeight: 800, border: `1.5px solid ${st.border}`, background: st.background, color: st.color }}>
      {m.label}
    </button>
  );
}

// ── Intro & closing ───────────────────────────────────────────────────────────
// The screens every visitor sees around the tours — the welcome, "How to read
// room numbers", and the completion form — laid out as the visitor sees them,
// with the words edited in place. What it writes (`welcome.heading/.blurb/
// .roomGuide`, `closing`) and every default is src/utils/engtourIntro.js, which
// the viewer and api/send-tour-email.js read too. The form's fields are
// Required / Optional / Off; the server enforces the SAVED setting.
const FIELD_MODE_LABELS = [["required", "Required"], ["optional", "Optional"], ["off", "Off"]];

function IntroClosingModal({ tourData, stampCount, sampleTour, onWelcome, onClosing, onPreview, onClose }) {
  const intro = introContent(tourData);
  const fields = intro.closing.fields;
  // Stored as the editor arranged them; `type` / `max` are derived, not saved.
  const writeFields = (list) => onClosing({ fields: list.map(({ id, rawLabel, placeholder, mode, custom }) => ({ id, label: rawLabel, placeholder, mode, ...(custom ? { custom: true } : {}) })) });
  const patchField = (id, patch) => writeFields(fields.map(f => f.id === id ? { ...f, ...patch } : f));
  const moveField = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= fields.length) return;
    const list = [...fields];
    [list[i], list[j]] = [list[j], list[i]];
    writeFields(list);
  };
  const customCount = fields.filter(f => f.custom).length;
  const addQuestion = () => writeFields([...fields, { id: newFieldId(), rawLabel: "New question", placeholder: "", mode: "optional", custom: true }]);

  const card = { flex: "1 1 320px", minWidth: 300, maxWidth: 400, background: UM.cream, borderRadius: 18, overflow: "hidden", boxShadow: "0 10px 32px rgba(0,0,0,0.35)", display: "flex", flexDirection: "column" };
  const step = { fontSize: 9, fontWeight: 800, letterSpacing: "0.1em", color: "rgba(255,255,255,0.75)", margin: "0 0 6px 4px" };
  const box = { width: "100%", boxSizing: "border-box", fontFamily: SANS, fontSize: 14, color: UM.ink, lineHeight: 1.55, padding: "8px 10px", border: `1.5px dashed ${UM.line}`, borderRadius: 9, background: "#fff", outline: "none", resize: "vertical" };
  const hint = { fontSize: 10.5, color: "#8a7a66", lineHeight: 1.45, marginTop: 5 };
  const serifInput = { width: "100%", boxSizing: "border-box", fontFamily: SERIF, fontWeight: 800, color: UM.brown, border: `1.5px dashed ${UM.line}`, borderRadius: 9, background: "#fff", padding: "6px 9px", outline: "none" };
  const smallBtn = (disabled) => ({ fontSize: 11, padding: "1px 6px", border: "1px solid #ddd", background: "#fff", borderRadius: 4, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.3 : 1 });

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(10,18,30,0.72)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: "min(1680px, 100%)", maxHeight: "100%", display: "flex", flexDirection: "column", background: "#1b2a40", borderRadius: 16, overflow: "hidden", boxShadow: "0 24px 70px rgba(0,0,0,0.55)" }}>
        <div style={{ background: UM.navy, color: "#fff", padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, borderBottom: `3px solid ${UM.gold}`, flexShrink: 0 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: SERIF, fontSize: 16, fontWeight: 800 }}>Introduction &amp; closing</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>What every visitor sees before and after a tour. Click any dashed box to change it.</div>
          </div>
          {onPreview && (
            <button onClick={onPreview} title="Walk it as a visitor, with these changes" style={{ fontSize: 12, fontWeight: 800, padding: "7px 13px", borderRadius: 7, border: "none", background: "#00c853", color: "#fff", cursor: "pointer" }}>▶ Test tour</button>
          )}
          <button onClick={onClose} style={{ fontSize: 13, fontWeight: 900, padding: "7px 16px", borderRadius: 7, border: "none", background: UM.gold, color: UM.brown, cursor: "pointer" }}>Done</button>
        </div>

        <div style={{ overflowY: "auto", padding: "16px 18px 18px", display: "flex", gap: 18, flexWrap: "wrap", alignItems: "flex-start", justifyContent: "center" }}>
          {/* ── 1 · Welcome ── */}
          <div style={{ flex: "1 1 320px", minWidth: 300, maxWidth: 400 }}>
            <div style={step}>1 · WELCOME — THE FIRST SCREEN</div>
            <div style={card}>
              <div style={{ position: "relative", height: 150, background: `linear-gradient(135deg, ${UM.navy} 0%, ${UM.blue} 55%, ${UM.sky} 100%)` }}>
                {intro.banner && <img src={intro.banner} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} onError={e => { e.target.style.display = "none"; }} />}
                <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(27,51,94,0.1) 0%, rgba(27,51,94,0.78) 100%)" }} />
                <input value={tourData.welcome?.heading ?? intro.heading} onChange={e => onWelcome({ heading: e.target.value })} placeholder={intro.heading}
                  style={{ position: "absolute", left: 14, right: 14, bottom: 12, boxSizing: "border-box", fontFamily: SERIF, fontSize: 21, fontWeight: 800, color: "#fff", background: "rgba(0,0,0,0.18)", border: "1.5px dashed rgba(255,255,255,0.6)", borderRadius: 8, padding: "4px 8px", outline: "none" }} />
                <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 5, background: UM.gold }} />
              </div>
              <div style={{ padding: "16px 18px 18px" }}>
                <textarea value={tourData.welcome?.blurb ?? intro.blurb} onChange={e => onWelcome({ blurb: e.target.value })} rows={5} style={box} />
                <div style={{ marginTop: 12, padding: "11px 0", borderRadius: 11, background: UM.navy, color: "#fff", fontSize: 14, fontWeight: 800, textAlign: "center", opacity: 0.85 }}>Get started →</div>
                <div style={hint}>Next, visitors pick a tour — that list is the tours themselves (✎ Edit above the map).</div>
              </div>
            </div>
          </div>

          {/* ── 2 · How to read room numbers ── */}
          <div style={{ flex: "1 1 320px", minWidth: 300, maxWidth: 400 }}>
            <div style={step}>2 · AFTER PICKING A TOUR</div>
            <div style={card}>
              <div style={{ padding: "16px 18px 18px" }}>
                <input value={tourData.welcome?.roomGuide?.heading ?? intro.roomGuide.heading} onChange={e => onWelcome({ roomGuide: { ...(tourData.welcome?.roomGuide || {}), heading: e.target.value } })}
                  placeholder={intro.roomGuide.heading} style={{ ...serifInput, fontSize: 17, marginBottom: 10 }} />
                <textarea value={tourData.welcome?.roomGuide?.body ?? intro.roomGuide.body} onChange={e => onWelcome({ roomGuide: { ...(tourData.welcome?.roomGuide || {}), body: e.target.value } })}
                  rows={9} style={{ ...box, fontSize: 12.5, background: UM.sand, borderLeft: `4px solid ${UM.gold}` }} />
                <div style={hint}>One point per line. Start a line with <b>-</b> to tuck it under the point above. Put <b>**double stars**</b> around words to make them bold.</div>
                <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.08em", color: "#a08f78", margin: "12px 0 6px" }}>VISITORS SEE</div>
                <div style={{ background: UM.sand, borderRadius: 14, padding: "13px 14px 12px", borderLeft: `4px solid ${UM.gold}` }}>
                  <RoomGuideList body={intro.roomGuide.body} />
                </div>
              </div>
            </div>
          </div>

          {/* ── 3 · Completion, with who gets its email beside it (kept together when the row wraps) ── */}
          <div style={{ flex: "2 1 640px", maxWidth: 840, display: "flex", gap: 18, flexWrap: "wrap", alignItems: "flex-start", justifyContent: "center" }}>
          <div style={{ flex: "1 1 340px", minWidth: 290, maxWidth: 440 }}>
            <div style={step}>LAST · AFTER THE FINAL STAMP</div>
            <div style={card}>
              <div style={{ padding: "16px 18px 18px" }}>
                <input value={tourData.closing?.heading ?? intro.closing.heading} onChange={e => onClosing({ heading: e.target.value })}
                  placeholder={intro.closing.heading} style={{ ...serifInput, fontSize: 20, marginBottom: 8 }} />
                <textarea value={tourData.closing?.body ?? intro.closing.body} onChange={e => onClosing({ body: e.target.value })} rows={4} style={box} />
                <div style={hint}><b>{"{stamps}"}</b> becomes the tour's number of stamps — visitors read "{fillStamps("{stamps}", stampCount)}" today.</div>

                <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.08em", color: "#a08f78", margin: "14px 0 4px" }}>THE FORM — WHAT GETS SENT IN THE EMAIL</div>
                <div style={{ ...hint, marginTop: 0, marginBottom: 8 }}>Required boxes must be filled before Submit works; Off boxes aren't shown or sent.</div>
                {fields.map((f, i) => (
                  <div key={f.id} style={{ background: "#fff", border: `1px solid ${UM.line}`, borderRadius: 10, padding: "8px 9px", marginBottom: 7, opacity: f.mode === "off" ? 0.6 : 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <input value={f.rawLabel} onChange={e => patchField(f.id, { rawLabel: e.target.value })} maxLength={80} placeholder={f.custom ? "Your question" : f.label}
                        style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 800, color: UM.brown, border: `1px dashed ${UM.line}`, borderRadius: 6, padding: "4px 7px", outline: "none" }} />
                      <button onClick={() => moveField(i, -1)} disabled={i === 0} title="Move up" style={smallBtn(i === 0)}>↑</button>
                      <button onClick={() => moveField(i, 1)} disabled={i === fields.length - 1} title="Move down" style={smallBtn(i === fields.length - 1)}>↓</button>
                      {f.custom && <button onClick={() => writeFields(fields.filter(x => x.id !== f.id))} title="Remove this question" style={{ ...smallBtn(false), color: "#c0392b" }}>✕</button>}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
                      <input value={f.placeholder || ""} onChange={e => patchField(f.id, { placeholder: e.target.value })} placeholder="Example text in the box (optional)" maxLength={80}
                        style={{ flex: 1, minWidth: 0, fontSize: 11.5, color: "#6b5d4b", border: `1px dashed ${UM.line}`, borderRadius: 6, padding: "4px 7px", outline: "none" }} />
                      <div style={{ display: "flex", border: `1px solid ${UM.line}`, borderRadius: 7, overflow: "hidden", flexShrink: 0 }}>
                        {FIELD_MODE_LABELS.map(([m, l]) => (
                          <button key={m} onClick={() => patchField(f.id, { mode: m })} aria-pressed={f.mode === m}
                            style={{ fontSize: 10.5, fontWeight: 800, padding: "4px 8px", border: "none", cursor: "pointer",
                              background: f.mode === m ? (m === "required" ? UM.navy : m === "optional" ? UM.gold : "#8a7a66") : "#fff",
                              color: f.mode === m ? (m === "optional" ? UM.brown : "#fff") : "#8a7a66" }}>{l}</button>
                        ))}
                      </div>
                    </div>
                    {f.id === "email" && f.mode === "off" && <div style={{ ...hint, color: "#b45309" }}>Without an email you won't be able to reply to them.</div>}
                    {f.custom && !f.label && f.mode !== "off" && <div style={{ ...hint, color: "#b45309" }}>A question with no wording isn't asked.</div>}
                  </div>
                ))}
                <button onClick={addQuestion} disabled={customCount >= MAX_CUSTOM_FIELDS}
                  style={{ width: "100%", fontSize: 12, fontWeight: 800, padding: "8px 0", borderRadius: 9, border: `1.5px dashed ${UM.sky}`, background: "#fff", color: UM.sky, cursor: customCount >= MAX_CUSTOM_FIELDS ? "default" : "pointer", opacity: customCount >= MAX_CUSTOM_FIELDS ? 0.5 : 1 }}>
                  + Add a question{customCount >= MAX_CUSTOM_FIELDS ? ` (${MAX_CUSTOM_FIELDS} max)` : ""}
                </button>

                <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.08em", color: "#a08f78", margin: "14px 0 5px" }}>AFTER THEY SUBMIT</div>
                <textarea value={tourData.closing?.thanks ?? intro.closing.thanks} onChange={e => onClosing({ thanks: e.target.value })} rows={3} style={box} />
              </div>
            </div>
          </div>

          {/* ── Who the completion email goes to ── */}
          <div style={{ flex: "1 1 280px", minWidth: 270, maxWidth: 380 }}>
            <div style={step}>SURVEY RECIPIENTS — WHO GETS THE EMAIL</div>
            <div style={card}>
              <SurveyRecipients recipients={tourData.closing?.recipients} onChange={recipients => onClosing({ recipients })}
                closing={tourData.closing} sampleTour={sampleTour} hint={hint} />
            </div>
          </div>
          </div>
        </div>
        <div style={{ flexShrink: 0, padding: "9px 18px", fontSize: 11, color: "rgba(255,255,255,0.7)", borderTop: "1px solid rgba(255,255,255,0.1)" }}>
          Changes show in ▶ Test tour at once; press <b style={{ color: UM.gold }}>💾 Save</b> in the top bar to publish them. A box left empty shows the original wording.
        </div>
      </div>
    </div>
  );
}

// ── Survey recipients ─────────────────────────────────────────────────────────
// Who a visitor's completion email goes to: regular (To), CC and BCC lists of
// addresses, saved as `closing.recipients` (rules in src/utils/engtourIntro.js).
// An address only counts once it is a chip — the box beside "Add" warns while
// it holds something not yet added, so nobody leaves thinking a typed address
// is on the list. "Send test email" mails a sample to the lists as they stand,
// unsaved; visitors' emails follow the SAVED lists.
function SurveyRecipients({ recipients, onChange, closing, sampleTour, hint }) {
  const lists = normalizeRecipients(recipients);
  const total = recipientCount(lists);
  const full = total >= MAX_RECIPIENTS;
  const [drafts, setDrafts] = useState({ to: "", cc: "", bcc: "" });
  const [errors, setErrors] = useState({});
  const [editing, setEditing] = useState(null); // { kind, addr, value }
  const [test, setTest] = useState({ state: "idle", msg: "" });

  const where = (addr) => RECIPIENT_KINDS.find(({ kind }) => lists[kind].includes(addr));
  const write = (next) => { onChange(next); setTest({ state: "idle", msg: "" }); };
  // A message for an address that can't go on a list, or null when it can.
  const problem = (addr, except) => {
    if (!addr) return "Type an email address first.";
    if (!isEmailAddress(addr)) return "That doesn't look like an email address.";
    const other = where(addr);
    if (other && addr !== except) return `Already added as a ${other.kind === "to" ? "regular" : other.kind.toUpperCase()} recipient.`;
    return null;
  };
  const add = (kind) => {
    const addr = cleanAddress(drafts[kind]);
    const err = full ? `${MAX_RECIPIENTS} recipients is the most.` : problem(addr);
    setErrors(e => ({ ...e, [kind]: err }));
    if (err) return;
    write({ ...lists, [kind]: [...lists[kind], addr] });
    setDrafts(d => ({ ...d, [kind]: "" }));
  };
  const remove = (kind, addr) => write({ ...lists, [kind]: lists[kind].filter(a => a !== addr) });
  const saveEdit = () => {
    const { kind, addr } = editing;
    const next = cleanAddress(editing.value);
    const err = problem(next, addr);
    if (err) { setEditing(ed => ({ ...ed, err })); return; }
    write({ ...lists, [kind]: lists[kind].map(a => a === addr ? next : a) });
    setEditing(null);
  };
  const pending = RECIPIENT_KINDS.filter(({ kind }) => cleanAddress(drafts[kind]));

  const sendTest = async () => {
    setTest({ state: "sending", msg: "" });
    try {
      const r = await sendTestTourEmail({
        recipients: lists, closing,
        tour: sampleTour ? { id: sampleTour.id, title: sampleTour.title, stops: (sampleTour.stops || []).map(s => s.title || s.label || "") } : null,
      });
      const n = recipientCount(r);
      setTest({ state: "sent", msg: `Test email sent to ${n} address${n === 1 ? "" : "es"}${r.defaultInbox ? ", with the UMaps default inbox as the regular recipient" : ""}. Check spam if it doesn't arrive in a minute.` });
    } catch (e) {
      setTest({ state: "error", msg: e.message });
    }
  };

  const chipBtn = { fontSize: 10.5, fontWeight: 800, padding: "2px 7px", borderRadius: 5, border: `1px solid ${UM.line}`, background: "#fff", cursor: "pointer", flexShrink: 0 };
  const input = { flex: 1, minWidth: 0, fontSize: 12.5, color: UM.ink, border: `1.5px dashed ${UM.line}`, borderRadius: 7, padding: "6px 8px", outline: "none", background: "#fff" };

  return (
    <div style={{ padding: "16px 18px 18px" }}>
      <div style={{ fontSize: 12.5, color: UM.ink, lineHeight: 1.5 }}>
        When a visitor presses Submit, their answers and stamps are emailed to the people below.
      </div>
      {total === 0
        ? <div style={{ ...hint, color: "#b45309" }}>No one is added yet, so the email goes to the UMaps default inbox.</div>
        : !lists.to.length && <div style={{ ...hint, color: "#b45309" }}>No regular recipient: the UMaps default inbox will be the "To", with your CC / BCC copied in.</div>}

      {RECIPIENT_KINDS.map(({ kind, label, add: addLabel, hint: kindHint }) => (
        <div key={kind} style={{ marginTop: 14 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.08em", color: "#a08f78" }}>{label.toUpperCase()}</div>
            <div style={{ fontSize: 10, color: "#a08f78" }}>· {lists[kind].length} added</div>
          </div>
          <div style={{ ...hint, marginTop: 1, marginBottom: 6 }}>{kindHint}</div>
          {lists[kind].map(addr => (
            editing && editing.kind === kind && editing.addr === addr ? (
              <div key={addr} style={{ background: "#fff", border: `1.5px solid ${UM.sky}`, borderRadius: 9, padding: "6px 7px", marginBottom: 5 }}>
                <div style={{ display: "flex", gap: 5 }}>
                  <input autoFocus value={editing.value} aria-label={`Edit ${addr}`} type="email"
                    onChange={e => setEditing(ed => ({ ...ed, value: e.target.value, err: null }))}
                    onKeyDown={e => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") setEditing(null); }} style={input} />
                  <button onClick={saveEdit} style={{ ...chipBtn, background: UM.navy, color: "#fff", border: "none" }}>Save</button>
                  <button onClick={() => setEditing(null)} style={{ ...chipBtn, color: "#6b5d4b" }}>Cancel</button>
                </div>
                {editing.err && <div style={{ ...hint, color: "#c0392b" }}>{editing.err}</div>}
              </div>
            ) : (
              <div key={addr} style={{ display: "flex", alignItems: "center", gap: 6, background: "#eef6ee", border: "1px solid #a5d6a7", borderRadius: 9, padding: "5px 7px", marginBottom: 5 }}>
                <span style={{ fontSize: 9.5, fontWeight: 900, color: "#2e7d32", flexShrink: 0 }}>{kind === "to" ? "TO" : kind.toUpperCase()}</span>
                <span title={addr} style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 700, color: UM.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{addr}</span>
                <button onClick={() => setEditing({ kind, addr, value: addr })} title={`Edit ${addr}`} style={{ ...chipBtn, color: UM.sky }}>Edit</button>
                <button onClick={() => remove(kind, addr)} title={`Remove ${addr}`} style={{ ...chipBtn, color: "#c0392b" }}>Delete</button>
              </div>
            )
          ))}
          <div style={{ display: "flex", gap: 5 }}>
            <input value={drafts[kind]} type="email" placeholder={addLabel} aria-label={addLabel} disabled={full}
              onChange={e => { const v = e.target.value; setDrafts(d => ({ ...d, [kind]: v })); setErrors(er => ({ ...er, [kind]: null })); }}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); add(kind); } }}
              style={{ ...input, opacity: full ? 0.5 : 1 }} />
            <button onClick={() => add(kind)} disabled={full}
              style={{ fontSize: 12, fontWeight: 800, padding: "0 12px", borderRadius: 7, border: "none", background: UM.sky, color: "#fff", cursor: full ? "default" : "pointer", opacity: full ? 0.5 : 1, flexShrink: 0 }}>+ Add</button>
          </div>
          {errors[kind]
            ? <div style={{ ...hint, color: "#c0392b" }}>{errors[kind]}</div>
            : cleanAddress(drafts[kind]) && <div style={{ ...hint, color: "#b45309" }}>Not added yet. Press + Add (or Enter) to put it on the list.</div>}
        </div>
      ))}
      <div style={{ ...hint, marginTop: 10 }}>{total} of {MAX_RECIPIENTS} recipients. Visitors' emails follow the list once you press 💾 Save.</div>

      <button onClick={sendTest} disabled={!total || test.state === "sending"}
        style={{ width: "100%", marginTop: 12, fontSize: 13, fontWeight: 900, padding: "10px 0", borderRadius: 9, border: "none", background: UM.navy, color: "#fff", cursor: !total || test.state === "sending" ? "default" : "pointer", opacity: !total ? 0.45 : 1 }}>
        {test.state === "sending" ? "Sending…" : "Send Test Email"}
      </button>
      <div style={{ ...hint, color: test.state === "error" ? "#c0392b" : test.state === "sent" ? "#2e7d32" : hint.color }}>
        {test.msg || (total
          ? "Sends a sample completion email, marked as a test, to everyone above (saved or not)."
          : "Add a recipient to send a test.")}
        {pending.length > 0 && test.state !== "sending" && <><br /><b style={{ color: "#b45309" }}>The address typed under {pending.map(k => k.label.replace(" recipients", "")).join(" and ")} isn't added, so the test won't go to it.</b></>}
      </div>
    </div>
  );
}

// ── Delete-tour confirmation ──────────────────────────────────────────────────
function DeleteTourConfirm({ tour, onCancel, onConfirm }) {
  if (!tour) return null;
  const name = tour.title || `Tour ${tour.id}`;
  const n = (tour.stops || []).length;
  return (
    <div onClick={onCancel} style={{ position: "fixed", inset: 0, zIndex: 64, background: "rgba(10,18,30,0.62)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: "min(420px, 100%)", background: UM.cream, borderRadius: 16, overflow: "hidden", boxShadow: "0 24px 70px rgba(0,0,0,0.55)" }}>
        <div style={{ background: "#c0392b", color: "#fff", padding: "12px 16px", fontFamily: SERIF, fontSize: 15, fontWeight: 800 }}>Delete this tour?</div>
        <div style={{ padding: "16px 18px" }}>
          <div style={{ fontSize: 14, color: UM.ink, lineHeight: 1.55 }}>
            Delete <b>{name}</b> (tour {tour.id}){n ? <> and its <b>{n}</b> stop{n === 1 ? "" : "s"}</> : ""}? This can't be undone once you save.
          </div>
          <div style={{ fontSize: 12, color: "#8a7a66", marginTop: 8 }}>The stops stay in the bank, so you can re-add them to another tour.</div>
          <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
            <button onClick={onCancel} style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: `1px solid ${UM.line}`, background: "#fff", color: "#6b5d4b", fontSize: 14, fontWeight: 800, cursor: "pointer" }}>Cancel</button>
            <button onClick={onConfirm} style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: "none", background: "#c0392b", color: "#fff", fontSize: 14, fontWeight: 900, cursor: "pointer" }}>Delete tour</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── 1:1 tour preview ──────────────────────────────────────────────────────────
// Full-screen overlay that mounts the real EngTourPage viewer fed the draft
// (unsaved) tour data, starting where a visitor starts — the welcome screen,
// then the tour picker — so an editor sees exactly what a visitor would,
// without saving or leaving the page. It used to jump straight into the chosen
// tour, which is why editors reported the intro "missing" from Test tour.
function TestTourModal({ tourData, tourId, onClose }) {
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 70, background: "#000" }}>
      <EngTourPage previewData={tourData} previewTourId={tourId} previewFromStart />
      {/* Above the viewer's own popups (z 80–90) so it's always clickable. */}
      <button onClick={onClose} title="Close preview"
        style={{ position: "fixed", top: 12, left: 12, zIndex: 200, background: "rgba(0,0,0,0.85)", color: "#fff", border: "1px solid rgba(255,255,255,0.5)", borderRadius: 999, padding: "8px 16px", fontSize: 13, fontWeight: 800, cursor: "pointer" }}>
        ✕ Exit preview
      </button>
    </div>
  );
}
